import type {FilterDescriptor} from '@progress/kendo-data-query'
import type {
    AgeSystemCharacterInterface,
    BuildStatsInterface,
    CharacterSheetInterface,
    ElementSelectionInterface,
    SwadeCharacterInterface,
    SwadeCharacterPrototypeInterface, SwadePerkPointAllocation
} from "@/types/characters";
import type {
    AppliedConfiguration,
    ContainerEffect,
    SettingElementInterface,
    SwadeAbilityInterface,
    SwadeAncestryInterface,
    SwadeCharacterFrameworkInterface,
    SwadeDrawbackInterface,
    SwadeEdgeContainerInterface,
    SwadeEdgeInterface,
    SwadeHindranceInterface,
    SwadeItemInterface,
    SwadeItemContainerInterface,
    SwadeModConfigurationInterface,
    SwadeModificationInterface,
    SwadeSkillInterface,
    SwadeSkillRankInterface,
    RandomTableInterface,
    RandomTableResultInterface,
    CarriedPayloadApplication
} from "@/types/items";
import type {
    SwadeSettingsInterface,
    SwadeSystemDefaultsInterface,
    SwadeWealthDieCode,
    SystemDefaultsInterface,
    SystemInterface
} from "@/types/systems";
import type {CharacterLocalSettingInterface, SettingRuleInterface, WorldInterface} from "@/types/worlds.ts";
import type {
    SwadePowerContainerInterface,
    SwadePowerInterface,
    SwadePowerLevelInterface,
    SwadePowerModifierLevelInterface,
    SwadeSuperPowerDetailInterface,
} from "@/types/powers.ts";
import router from '@/router';
import DieRank from "@/types/die-rank";
import {v4 as uuid} from 'uuid'
import {defineStore} from "pinia";
import {useAppDataStore} from '@/stores/app-data'
import {api} from '@/api/client';
import {deepCopyObject, handleError} from "@/appdata/globals";
import {recordsForSourceModification, resolvedModificationUuid, diffConfigurationSets, sameConfigurationSet, matchesConfiguration} from "@/appdata/applied-configuration";
import { buildTableRollRecords, recordsForModification } from '@/appdata/tableRolls';
import { isSyntheticWorldUuid, localSettingToWorld } from "@/appdata/character-local-setting";
import {compareContainersBy, compareContainersByName} from "@/composables/useEquipmentStatus";
import {containerDisplayNameFor} from "@/appdata/chosen-labels";
import {rulesFor} from "@/appdata/item-type-rules";
import { normalizeArmorStackingLimit } from "@/appdata/armor-stacking";
import { NO_LANGUAGE_SKILL } from "@/appdata/language-skill";
import { powerDetailBase, resolveContainerDetails, resolvePowerDetails } from "@/appdata/container-details";
import { coerceContainerValue, containerDisplayName, containerFieldPolicy, ownFieldsFor, resolveDrawbackType, resolveSkillTrapping } from "@/appdata/container-fields";
import { foldTextList } from '@/appdata/text-list';
import { authoredTargetMode, buildContainerTargetChoiceSet, candidateContainerTargets, CONTAINER_PATHS, containerTargetEditable, containerTargetRecord, effectTargets, elementEntryUuid, isElementEmpty, isOfferedOption, isRedirectableElement, isTargetedContainer, preselectionFor, resolveContainerTargets, supportedContainerPath } from '@/appdata/container-targets';
import { carriedAppliedConfigs, carriedBuildOptionConfigs, carriedEntries, carriedRows, desiredCarriedApplications } from '@/appdata/container-carry';
import { directGrants, fixedGrants, containerTargetGrants, grantMultiplier, isLegacyPowerContainer, isPowerOf, levelCount, levelScaledConfiguration, POWER_CONTAINER_SCHEMA, type PowerLevelOption } from '@/appdata/power-levels';
import { ownPowerRecords, pickedPowerOptions, recordsOfSet, rungEffectModifications, rungChoiceSetPrompts, catalogChoiceSetPrompts } from '@/appdata/power-options';
import { DERIVED_TOUGHNESS_PATH, STOCK_TOUGHNESS_FORMULA, evaluateDerivedFormula, isDerivedSourceAttribute, parseDerivedFormula } from '@/appdata/derived-formula';
import type { DerivedFormulaParts } from '@/appdata/derived-formula';
import { effectiveOffset, isEffectivePath, sumEffectiveOffsets, writeEffectiveOffsets } from '@/appdata/effective-attributes';
import { effectiveEffect, enterableEffectFor, enteredValueFor, isEnterable } from '@/appdata/container-entries';
import { isSkillRankEffect, shiftSkillRank, skillRankAmount } from '@/appdata/skill-rank-effects';
import { resolveDetail } from '@/appdata/resolve-detail';
import { hindranceDegrees } from '@/appdata/hindrance-degrees';
import { withPageSize } from '@/appdata/page-size';
import { parseScalar } from '@/types/range-resolver';
import {useUxData} from "@/stores/ux-data.ts";
import {ChoiceCancelledError} from "@/types/errors";
import {config} from "@vue/test-utils";

type CalculateBaseTarget = { purpose: string, attribute: string };
type ModSource = 'ancestry' | 'permanent' | 'temporary';

// syncCarriedPayloads' re-entrancy guard, keyed per character so two sheets can sync independently
// and nothing is retained after the call. See the doc comment on syncCarriedPayloads.
const carriedSyncInFlight = new WeakSet<object>();
const carriedResyncRequested = new WeakSet<object>();

const CALCULATE_BASE_TARGETS: Record<string, CalculateBaseTarget[]> = {
    vigor:  [{ purpose: 'strain', attribute: 'vigor' }],
    spirit: [{ purpose: 'strain', attribute: 'spirit' }],
    // Encumbrance and minimum Strength are NOT slots here: they are signed die-step offsets on
    // protoData.attributes.strength, rebuilt from the effective.* rows (src/appdata/effective-attributes.ts).
};

// Resolvers for empty-element modifications. Each resolver owns: detection (matches),
// eligibility/cancellation (throws ChoiceCancelledError when inapplicable), UI invocation
// (e.g. opening a picker), and AppliedConfiguration shaping. resolveChoiceSets groups
// matching mods by resolver and invokes each resolver's resolveBatch.
type DynamicSourceResolver = {
    matches: (mod: SwadeModificationInterface) => boolean;
    resolveBatch: (
        mods: SwadeModificationInterface[],
        character: CharacterSheetInterface,
        source: { uuid: string, name: string },
        existingChoices?: AppliedConfiguration[],
    ) => Promise<AppliedConfiguration[]>;
};

// Synthesize a choiceSet whose options are derived from `character.skills`. Lets the
// regular ChoiceApplicator render the picker UI unchanged. The synthetic's uuid mirrors
// the originalMod's uuid so the recorded choiceSetUuid stays stable across re-applies
// (otherwise the install-time reuse logic would miss the match and re-prompt every time).
const buildSyntheticSkillChoiceSet = (
    originalMod: SwadeModificationInterface,
    skills: SwadeSkillRankInterface[],
): SwadeModificationInterface => {
    const originalConfig = originalMod.configuration;
    return {
        uuid: originalMod.uuid,
        version: originalMod.version,
        configuration: {
            path: 'character.choiceSet',
            label: (originalConfig.label && originalConfig.label.length > 0)
                ? originalConfig.label
                : 'Choose a skill',
            element: skills.map((s) => ({
                configuration: {
                    path: 'character.skills',
                    element: s.skill.uuid,
                    label: s.skill.name,
                    value: originalConfig.value,
                    valueType: originalConfig.valueType,
                    operator: originalConfig.operator,
                    mod_type: originalConfig.mod_type,
                    summary: originalConfig.summary,
                },
            })) as any,
            value: null,
            valueType: null,
            summary: null,
            operator: null,
            mod_type: 'character',
        },
    };
};

const DYNAMIC_SOURCE_RESOLVERS: DynamicSourceResolver[] = [
    // character.edges with empty element → open EdgeFinder so the user picks any edge
    // from the character's world. Resolves to a character.edges 'add' configuration; the
    // existing applyModification dispatch (→ applyEdgeModification → addEdgeToCharacter)
    // then performs the full edge-add cascade. Rollback dispatches to removeEdge so the
    // granted edge is cascade-removed when the source mod is rolled back.
    {
        matches: (mod) =>
            mod.configuration.path === 'character.edges'
            && isElementEmpty(mod.configuration.element),

        resolveBatch: async (mods, character, source, existingChoices) => {
            const appData = useAppDataStore();
            const world = character.world ?? null;
            const applied: AppliedConfiguration[] = [];

            // Sequential picks: one EdgePicker session per matching mod so the user
            // can target each grant separately when multiple coexist on one source.
            for (const mod of mods) {
                const existing = (existingChoices ?? []).find(
                    (e) => e.choiceSetUuid === mod.uuid,
                ) ?? null;
                const result = await appData.requestEdgePick(source, mod, world, existing);
                applied.push(result);
            }
            return applied;
        },
    },

    // character.skills with empty element → open ChoiceApplicator with a synthetic choice
    // set built from `character.skills` so the player picks one of their own skills as the
    // target. The resolved configuration has `element` populated; existing applyModification
    // / rollbackModification dispatch for character.skills handles it from there.
    {
        matches: (mod) =>
            mod.configuration.path === 'character.skills'
            && isElementEmpty(mod.configuration.element),

        resolveBatch: async (mods, character, source, existingChoices) => {
            const skills = character.skills ?? [];
            if (skills.length === 0) {
                // No skills to target → caller's existing ChoiceCancelledError catch aborts
                // the larger flow (e.g., cyberware install) just as if the user cancelled.
                throw new ChoiceCancelledError();
            }
            const synthetics = mods.map((m) => buildSyntheticSkillChoiceSet(m, skills));
            const appData = useAppDataStore();
            return appData.requestChoices(source, synthetics, existingChoices);
        },
    },

    // container.* with an unset element ('' or null) OR an empty list ([]) → let the player pick one
    // or more of their own containers on that path. Delegates the whole synthesis to the shared
    // buildContainerTargetChoiceSet (also used by the container-target menu), so the regular
    // ChoiceApplicator renders the picker unchanged and the two callers cannot drift apart. The
    // synthetic's uuid mirrors the original mod's uuid so the recorded choiceSetUuid stays stable
    // across re-applies.
    //
    // THIS ENTRY STILL DIFFERS FROM THE TWO ABOVE, deliberately: they match on isElementEmpty, the
    // union that also treats a bare `undefined` as empty. This entry matches on isPromptElement
    // (null/'') OR isEmptyList ([]), but never on `undefined` alone — on a container path an ABSENT
    // element means "modify the container that granted me" (mode 'self'), which needs no prompt at
    // all. Matching `undefined` here would open a picker for every self-targeting container
    // modification — the most common authored shape — and withChoiceResolution would still apply
    // the self effect alongside it. isPromptElement and isEmptyList are the same predicates
    // resolveContainerTargets uses to report mode 'prompt' for null/''/[], so the two cannot drift.
    //
    // CONTRACT: buildContainerTargetChoiceSet keys every option by the CONTAINER's own uuid, not its
    // catalog selection uuid, and resolveContainerTargets' byContainer branch matches a container's
    // own uuid first. A pick therefore names exactly the container the player chose, never every
    // container sharing its catalog selection.
    {
        // supportedContainerPath is the own-property-safe lookup, and the ONLY path guard here. It
        // rejects two shapes, and BOTH failures land in the same place:
        //   'constructor'     — `path` is author-supplied and a bare CONTAINER_PATHS['constructor']
        //                       returns an inherited member rather than undefined.
        //   'container.powers'— container-SHAPED but not a key, so there is no array to target.
        // Either one reaching resolveBatch would find nothing to target and wait (spec 2026-10-13
        // §4a). Before that it threw ChoiceCancelledError, and withChoiceResolution CATCHES that and
        // returns without calling finishFn, so a single unsupported authored modification silently
        // abandoned the whole add, taking every valid container modification beside it down with it.
        // Either way it is not a pick, and this guard keeps it out of isResolvableModification, so it
        // neither makes its item unique nor counts as a live choice. Both shapes are pinned in the
        // asksWhenResolved table of item-uniqueness-flag.test.ts.
        //
        // A `startsWith('container.')` clause used to sit in front of this one. It was redundant —
        // every CONTAINER_PATHS key already begins "container." — and it was not merely inert: it
        // short-circuited the 'constructor' case before the hasOwnProperty lookup was ever reached,
        // so the test named for that hazard was in fact resting on the prefix. Removed, which is
        // what lets that test exercise the guard it was written for.
        //
        // Since spec 2026-09-10: a SPECIFIC list (chips, a bare uuid, a legacy string list) is a pick
        // too — one container among the ELIGIBLE ones (those holding an authored catalog element).
        // Exactly one eligible resolves without a dialog into the same record the applicator would
        // write; none eligible records nothing and never aborts the add (the modification waits for a
        // target; the pencil shows once one exists); two or more open a single pick. Since the owner's
        // rulings of 2026-10-13 pick-one and pick-many wait the same way when nothing is free to target:
        // no container on the path at all, or every candidate taken by another copy of the source.
        matches: (mod) =>
            supportedContainerPath(mod.configuration?.path) !== undefined
            && isRedirectableElement(mod.configuration?.element)
            // All Specified is authored, never picked (spec 2026-09-13 §3.3): no synthetic set, no record.
            && authoredTargetMode(mod.configuration) !== 'allSpecified',

        resolveBatch: async (mods, character, source, existingChoices) => {
            const applied: AppliedConfiguration[] = [];
            const synthetics: SwadeModificationInterface[] = [];
            for (const mod of mods) {
                const isSpecific = authoredTargetMode(mod.configuration) === 'specific';
                // Only what this source may pick: never a container another copy of it already picked
                // with this modification (spec 2026-10-13 §4a).
                const free = candidateContainerTargets(character, mod.configuration, { sourceUuid: source.uuid, modUuid: mod.uuid });
                // Nothing free to target — no container on the path at all, nothing eligible yet for a
                // specific list, or every candidate taken by another copy: the pick waits, recording
                // nothing, and its pencil sets it once a container arrives (spec 2026-10-13 §4a).
                // It never aborts the add.
                if (free.length === 0) continue;
                if (isSpecific && free.length === 1) {
                    const existing = recordsForSourceModification(existingChoices, source.uuid, mod.uuid);
                    applied.push(...(existing.length > 0 ? existing : [containerTargetRecord(source.uuid, mod, free[0])]));
                    continue;
                }
                const synthetic = buildContainerTargetChoiceSet(character, mod, source.uuid);
                // Never null here: `free` is not empty, and this resolver matches supported paths only.
                if (!synthetic) continue;
                synthetics.push(synthetic);
            }
            if (synthetics.length > 0) {
                applied.push(...await useAppDataStore().requestChoices(source, synthetics, existingChoices));
            }
            return applied;
        },
    },
];

// A modification whose answer is recorded in additionalData.choices — either an explicit
// character.choiceSet, or one a dynamic source resolver turns into a pick. Exported because
// retiring a resolved choice has to ask the same question the resolver layer asks.
export function isResolvableModification(mod: SwadeModificationInterface): boolean {
    if (!mod?.configuration) return false;
    if (mod.configuration.path === 'character.choiceSet') return true;

    return DYNAMIC_SOURCE_RESOLVERS.some((resolver) => resolver.matches(mod));
}

// The one "asks" rule (spec 2026-10-13 §4): what resolveChoiceSets prompts for, and what makes an
// item unique. An offered set (spec 2026-09-21 §5.1) is picked on a power card, never asked on add.
export const asksWhenResolved = (mod: SwadeModificationInterface): boolean =>
    !isOfferedOption(mod?.configuration) && isResolvableModification(mod);

// A specific list that names only the item it sits on targets that item, the way self does: an
// item's own starting charges are authored this way (seedContainerCharges keeps bySelection for it).
// The resolver still records it; it just never makes the item unique (spec 2026-10-13 §2).
const namesOnlyItself = (item: SwadeItemInterface | null | undefined, mod: SwadeModificationInterface): boolean => {
    if (authoredTargetMode(mod?.configuration) !== 'specific' || !item?.uuid) return false;
    const element: unknown = mod.configuration.element;
    return (Array.isArray(element) ? element : [element]).every((entry) => elementEntryUuid(entry) === item.uuid);
};

/**
 * A container that takes no more units (spec 2026-10-13 §4): another source has picked it, or its
 * item asks a question on add. Cyberware is exempt from the second half — it stacks on identical
 * answers, which _addCyberwareToCharacter decides. Computed from the item and the live records, never
 * stored: once the pick that named a container is rolled back, the container stacks again.
 */
export function isUniqueContainer(character: any, container: SwadeItemContainerInterface | null | undefined): boolean {
    if (!container) return false;
    if (isTargetedContainer(character, container)) return true;
    const item = container.selection;
    return item?.type !== 'cyberware'
        && (item?.modifications ?? []).some((mod) => asksWhenResolved(mod) && !namesOnlyItself(item, mod));
}

/** The containers of one catalog item that may take another unit, in sheet order. */
export function stackableContainers(character: any, catalogUuid: string | null | undefined): SwadeItemContainerInterface[] {
    if (!catalogUuid) return [];
    const equipment: SwadeItemContainerInterface[] = Array.isArray(character?.equipment) ? character.equipment : [];
    return equipment.filter((container) => container?.selection?.uuid === catalogUuid && !isUniqueContainer(character, container));
}

// The resolver that would prompt for this configuration had it been authored on the source itself:
// a container.* modification with no target, an edge or skill grant with a blank element.
const resolverFor = (configuration: SwadeModConfigurationInterface | undefined): DynamicSourceResolver | undefined =>
    configuration ? DYNAMIC_SOURCE_RESOLVERS.find((resolver) => resolver.matches({ configuration } as SwadeModificationInterface)) : undefined;

// `configuration` answers `option`: the same in every field matchesConfiguration reads but the target.
const isAnswerTo = (option: SwadeModConfigurationInterface, configuration: SwadeModConfigurationInterface): boolean =>
    matchesConfiguration({ ...option, element: configuration.element }, configuration);

/**
 * The records a set's prompt shows as already chosen. A record whose option was then given a target
 * (resolveOptionTarget) no longer equals that option, so it is handed over as the option it answers.
 */
function chosenOptionRecords(sets: SwadeModificationInterface[], records: AppliedConfiguration[] | undefined): AppliedConfiguration[] {
    return (records ?? []).map((record) => {
        const set = sets.find((s) => s.uuid === resolvedModificationUuid(record));
        const options = (Array.isArray(set?.configuration?.element) ? set.configuration.element : []) as Partial<SwadeModificationInterface>[];
        if (options.some((o) => matchesConfiguration(o.configuration, record.configuration))) return record;
        const option = options.find((o) => resolverFor(o.configuration) && isAnswerTo(o.configuration!, record.configuration));
        return option?.configuration ? { ...record, configuration: option.configuration } : record;
    });
}

/**
 * A chosen option that is itself a pick is asked for its target by the resolver that would ask for
 * it on the source, under the set's uuid so the answer stays the set's record. The record keeps the
 * option — its label, value and carry — with the target in `element`, one record per target. An
 * option with nothing to ask, or one whose resolver finds no target yet (a specific list with
 * nothing eligible), keeps its record as chosen and applies nothing. Cancel throws
 * ChoiceCancelledError, which cancels the whole answer.
 */
async function resolveOptionTarget(
    character: CharacterSheetInterface,
    source: { uuid: string, name: string },
    record: AppliedConfiguration,
    existingChoices: AppliedConfiguration[] | undefined,
): Promise<AppliedConfiguration[]> {
    const option = record.configuration;
    const resolver = resolverFor(option);
    if (!resolver) return [record];

    const setUuid = resolvedModificationUuid(record) ?? '';
    const previous = (existingChoices ?? []).filter((e) => resolvedModificationUuid(e) === setUuid && isAnswerTo(option, e.configuration));
    const answers = await resolver.resolveBatch([{ uuid: setUuid, configuration: option } as SwadeModificationInterface], character, source, previous);
    if (answers.length === 0) return [record];

    return answers.map((answer) => ({
        ...record,
        configuration: { ...deepCopyObject(option), element: answer.configuration.element, label: option.label ?? answer.configuration.label },
    }));
}

/**
 * The modifications a direct-apply loop should still apply from a source's authored list. Two
 * things are removed: character.choiceSet modifications (their answers live in records and are
 * applied by the record loop beside every direct loop), and any container.* modification that
 * ALREADY HAS RECORDS for this source — a player's pick or redirect replaces the authored target,
 * and the record loop applies it. This is a filter, not a substitution: substituting the records
 * here would apply them twice, because the record loop is not removed.
 *
 * Every direct-apply loop consumes this. A loop that iterates selection.modifications directly is
 * a bug — see the disposition table in the layer-4 plan.
 */
export function directModificationsWithoutOverrides(
    character: any,
    sourceUuid: string | null | undefined,
    modifications: SwadeModificationInterface[] | undefined,
): SwadeModificationInterface[] {
    const records = character?.additionalData?.choices ?? [];
    return (modifications ?? []).filter((mod) => {
        if (mod?.configuration?.path === 'character.choiceSet') return false;
        if (!supportedContainerPath(mod?.configuration?.path)) return true;
        return recordsForSourceModification(records, sourceUuid, mod?.uuid).length === 0;
    });
}

// `detail` reaches rebuildContainerOverrides' two accumulators as a computed property key, and
// `detail` is author-supplied data (ultimately decoded JSON off a modification's `configuration`).
// '__proto__' is a live accessor on Object.prototype — assigning through it rewires the
// accumulator's own prototype instead of setting a key; 'constructor' and 'prototype' shadow
// inherited members the same way resolveContainerField's own() helper already guards against on
// the read side (container-fields.ts). containerFieldPolicy and resolveDetail both gate on
// Object.prototype.hasOwnProperty against a closed whitelist that lists none of these three, so
// neither accumulator write is reachable with one today — but a write site should not depend on a
// different file's registry staying empty of these names to stay safe, so this is checked here too.
const UNSAFE_DETAIL_KEYS = new Set(['__proto__', 'constructor', 'prototype']);
const isUnsafeDetailKey = (detail: string): boolean => UNSAFE_DETAIL_KEYS.has(detail);

/**
 * Whether a configuration can contribute anything. Every strategy already discards these, but the
 * rebuild has to know BEFORE it decides a field is targeted at all: resolving a field that no
 * effect actually changes is not identity — '0/6/12' resolves to 0, '1d6' to 'd6'.
 */
const contributesSomething = (configuration: SwadeModConfigurationInterface): boolean => {
    // Widened to `string | null`: the declared operator union has no '' member, but this
    // comparison exists precisely because a blank form template can hand back '' at runtime
    // regardless of what the type declares (see the ChoiceSetConfigurator.vue / stringStrategy
    // comment in resolve-detail.ts this mirrors) — TS2367 would otherwise flag it as unreachable.
    const operator = (configuration?.operator ?? null) as string | null;
    if (operator === null || operator === '' || operator === 'block') return false;

    const value = configuration?.value ?? null;
    if (value === null) return false;
    if (typeof value === 'string' && value.trim() === '') return false;

    // A non-primitive value (e.g. a choice-set's nested-partial shape) has no sensible rendering
    // anywhere a "contributes" answer is trusted — not on a policy field (JS's default stringifies
    // an object as "[object Object]") and not through resolveDetail either. This used to be
    // re-checked in rebuildContainerOverrides' policy branch AFTER `last` was already chosen,
    // which let a later non-primitive value silently blank out an earlier valid one instead of
    // being dropped before grouping ever saw it. Living here once is what keeps the two in sync.
    // `0` is unaffected — it is typeof 'number' and reaches this line like any other number.
    if (typeof value !== 'string' && typeof value !== 'number') return false;

    return true;
};

// Mirrors container-fields.ts's own(): `detail` is author-supplied, so the base lookup must be
// own-key only. A bare `?.[detail]` returns an inherited member — 'toString' on any plain object
// yields Object.prototype.toString — and it is currently harmless only because resolveDetail
// returns undefined for an unregistered detail before that value would ever be used. A write site
// should not depend on a different file's whitelist staying complete to stay safe.
const ownDetail = (details: unknown, detail: string): unknown =>
    details !== null && details !== undefined && Object.prototype.hasOwnProperty.call(details, detail)
        ? (details as Record<string, unknown>)[detail]
        : null;

/**
 * Normalises `element` for key derivation so representationally-equivalent authored forms collapse
 * onto the same key. resolveContainerTargets itself treats a bare string and a one-element array
 * identically (`Array.isArray(element) ? element : [element]`), so the key must too, or a rollback
 * authored in the other form silently misses and the override leaks. Order must not matter either,
 * hence the sort — `['a','b']` and `['b','a']` name the same target set.
 *
 * Nested choiceSet partials (the third member of SwadeModConfigurationInterface's `element` union)
 * never reach a container.* modification — only a `character.choiceSet` configuration carries them,
 * and applyModification routes `container.*` paths before that distinction would matter. Entries are
 * therefore strings or `{ uuid }` chips, and `elementEntryUuid` reads exactly those two shapes:
 * anything else — a bare number, a partial with no uuid — is DROPPED rather than stringified, so a
 * malformed entry can neither match a container by accident nor collide keys as "[object Object]".
 */
const normalizeContainerModElement = (element: SwadeModConfigurationInterface['element']): string[] =>
    [...elementUuids(element)].sort();

/**
 * The uuids an element names, in authored order: a bare string, a string list, or a `{ uuid, label }`
 * chip list — the API's import handler emits the chip list for character.abilities while the editor
 * wrote a single string, so an applier must read all three. Malformed entries are dropped the way
 * elementEntryUuid drops them, never handed to the API as "[object Object]".
 */
const elementUuids = (element: SwadeModConfigurationInterface['element']): string[] => {
    if (element === null || element === undefined) return [];
    const list = Array.isArray(element) ? element : [element];
    return list.map(elementEntryUuid).filter((uuid): uuid is string => uuid !== null);
};

/**
 * A modification's `element` when — and only when — it was authored as a plain NAME (an attribute,
 * a derived stat, a startingFunds key). `element` is a union, and the name tests below all run it
 * through `Array.prototype.includes` or `===` against a string: neither ever matches an array or a
 * partial list, so reading `null` for those shapes reproduces exactly what those tests already
 * returned. Deliberately NOT used where the value is used as an object INDEX — JS stringifies there
 * and this would change which key is read.
 */
const elementName = (element: SwadeModConfigurationInterface['element']): string | null =>
    typeof element === 'string' ? element : null;

/**
 * The build entry behind a skill rank: the entry that records the rank's uuid, else, for an entry that
 * records none, the one for the rank's catalog skill.
 */
const protoSkillFor = (character: CharacterSheetInterface, rank: SwadeSkillRankInterface): any =>
    character?.protoData?.skills?.find((protoSkill: any) => protoSkill.rankUuid
        ? protoSkill.rankUuid === rank.uuid
        : protoSkill.uuid === rank.skill?.uuid);

/**
 * A configuration as the derived-stat and build-point walkers see it: the authored fields plus the
 * read-time `multiplier` decoration. Nothing stores that field — `carriedAppliedConfigs` attaches it
 * as the applied count (`CarriedAppliedConfig`, src/appdata/container-carry.ts) and calculateParry's
 * power branch attaches its own power-point ratio — so it is declared here rather than on the shared
 * interface, which describes what is persisted.
 */
type WalkerModConfiguration = SwadeModConfigurationInterface & { multiplier?: number };

/**
 * `rollbackCharacterBuildOption` flips a `multiply` into `multiply-subtract`, a transient in-memory
 * operator that only `applyCharacterPurchasingPowerOption` consumes. It is deliberately absent from
 * the persisted `SwadeModConfigurationInterface['operator']` union: nothing ever writes it to the API.
 */
interface RollbackModConfiguration extends Omit<SwadeModConfigurationInterface, 'operator'> {
    operator: SwadeModConfigurationInterface['operator'] | 'multiply-subtract',
}

/**
 * A power modifier AS SELECTED onto a container — what `buildPowerModifierFromSelection` returns and
 * the PHP api repo's `PowerContainer::$configuration` round-trips verbatim. `level` (the chosen
 * catalog rung) is now declared on SwadePowerModifierLevelInterface itself; this alias survives only
 * to name the selected role at the one walker that reads it.
 */
type SelectedPowerModifier = SwadePowerModifierLevelInterface;

/**
 * A stable identity for one modification's configuration, used to populate ContainerEffect's
 * sourceModUuid and, symmetrically, to re-derive the same value at rollback time.
 *
 * Not a real modification uuid: applyModification / registerContainerEffect (and
 * rollbackModification) only ever receive a bare SwadeModConfigurationInterface, never the wrapping
 * SwadeModificationInterface that actually carries an authored uuid. The configuration's own
 * identifying fields stand in for it — two calls with an equal configuration always produce the
 * same key, which is exactly what lets rollbackModification's container branch (handed the same
 * configuration back) tell deregisterContainerEffects which one of a source's several modifications
 * to remove, without ever having seen a real uuid.
 *
 * Normalised, not a raw snapshot of the fields: `value: 1` and `value: '1'` name the same target
 * (this codebase has a documented number-or-string hazard on exactly this kind of field), and so do
 * `element: 'sword'` and `element: ['sword']` (see normalizeContainerModElement above). Hashing the
 * raw fields would let a rollback authored in the other representation silently no-op instead of
 * matching. `label` and `summary` stay excluded — a label-only edit must still resolve to the same
 * key as the modification it relabels.
 *
 * Exported because it is one half of a write/re-derive pair, not a convenience. cloneCharacter
 * rewrites a ledger entry's `configuration.element` through its own old→new container map, and the
 * key stored beside it EMBEDS that element — so the clone must re-derive it with this exact
 * function. A second copy over there that agreed today would strand every redirected modification
 * the day either side changed, and strand it silently: nothing reads the key except an equality
 * test that simply finds no match.
 */
export const deriveContainerModKey = (configuration: SwadeModConfigurationInterface): string =>
    JSON.stringify([
        configuration?.path ?? null,
        configuration?.detail ?? null,
        configuration?.operator ?? null,
        String(configuration?.value ?? null),
        configuration?.valueType ?? null,
        normalizeContainerModElement(configuration?.element),
        // Appended ONLY when set: every key persisted before spec 2026-09-13 has six elements, and a
        // narrowed rollback of such an entry must still derive the same string.
        ...(configuration?.applyToAll === true ? [true] : []),
    ]);

export const useCharacterDataStore = defineStore('characterData', {
    state: (): any => ({
        // The currency adjuster's subject in the editor: the container an add just filled, or the
        // funds-only 'swade-currency-adjuster' sentinel (CharacterCreatorView renders it from this).
        context: null as SwadeItemContainerInterface | 'swade-currency-adjuster' | null,
        placeholder: {
            character: {
                name: null,
            } as SwadeCharacterInterface | AgeSystemCharacterInterface,
            settingRule: null as SettingRuleInterface | null,
            // Broadcast slot for AdvancementConfigurator and similar observers that
            // need to react to the currently-edited ability. Written only by outermost
            // editor consumers (AbilityList, AncestryList) via v-model on AbilityEditor.
            // AbilityEditor itself does not touch this slot — it receives ability via
            // defineModel.
            ability: null as SwadeAbilityInterface | null,
            ancestry: null as SwadeAncestryInterface | null,
            advantage: null as SwadeEdgeInterface | null,
            disadvantage: null as SwadeHindranceInterface | null,
            skill: null as SwadeSkillInterface | null,
            item: null as SwadeItemInterface | null,
            power: null as SwadePowerInterface | null,
            modification: null as SwadeModificationInterface | null,
            framework: null as SwadeCharacterFrameworkInterface | null,
            randomTable: null as RandomTableInterface | null,
        },
        selections: {
            ability: null as SwadeAbilityInterface | null,
            skill: null as SwadeSkillInterface | null,
            weapon: null as SwadeItemInterface | null,
            armor: null as SwadeItemInterface | null,
            item: null as SwadeItemInterface | null,
            edge: null as SwadeEdgeInterface | null,
            hindrance: null as string | null,
        } as ElementSelectionInterface | any,
        abilities: [] as SwadeAbilityInterface[],
        ancestries: [] as SwadeAncestryInterface[],
        skills: [] as SwadeSkillInterface[],
        disadvantages: [] as SwadeHindranceInterface[],
        advantages: [] as SwadeEdgeInterface[],
        weapons: [] as SwadeItemInterface[],
        armor: [] as SwadeItemInterface[],
        items: [] as SwadeItemInterface[],
        powers: [] as SwadePowerInterface[],
        frameworks: [] as SwadeCharacterFrameworkInterface[],
        tables: [] as RandomTableInterface[],
        buildOptions: null as SwadeSettingsInterface | null,
        build: {
            attributes: {
                allocated: 0 as number,
                total: 0 as number,
                unspentPoints: true as boolean,
                fullyAllocated: false as boolean,
                overAllocated: false as boolean,
            } as BuildStatsInterface,
            traits: {
                allocated: 0 as number,
                total: 0 as number,
                unspentPoints: true as boolean,
                fullyAllocated: false as boolean,
                overAllocated: false as boolean,
            } as BuildStatsInterface,
            perkPoints: {
                allocated: 0 as number,
                total: 2 as number,
                max: 4 as number,
                unspentPoints: true as boolean,
                fullyAllocated: false as boolean,
                overAllocated: false as boolean,
            } as BuildStatsInterface,
            languages: null as BuildStatsInterface | null,
            other: null as BuildStatsInterface | null,
        },
        // In-flight resolveWorldChoiceSets calls, keyed by world uuid, so a second caller for the
        // same world joins the first instead of opening a duplicate prompt. Not persisted state —
        // entries are deleted as each resolution settles.
        _worldChoiceResolutions: new Map<string, Promise<void>>(),
    }),
    getters: {
        attributeSteps: (state) => {
            return [
                {
                    value: 'd4',
                    title: 'd4',
                },
                {
                    value: 'd6',
                    title: 'd6',
                },
                {
                    value: 'd8',
                    title: 'd8',
                },
                {
                    value: 'd10',
                    title: 'd10',
                },
                {
                    value: 'd12',
                    title: 'd12',
                },
            ]
        },
        equipmentOptions: (state) => {
            return [
                ...state.weapons,
                ...state.armor,
                ...state.items
            ];
        },
    },
    actions: {
        async resolveChoiceSets(
            character: CharacterSheetInterface,
            source: { uuid: string, name: string },
            modifications: SwadeModificationInterface[],
            existingChoices?: AppliedConfiguration[],
        ): Promise<{ applied: AppliedConfiguration[], resolved: SwadeModConfigurationInterface[] }> {
            // Partition modifications into (a) groups handled by a DynamicSourceResolver
            // (empty-element character.edges, future skills/edges/etc.) and (b) real
            // character.choiceSet mods that go through the existing ChoiceApplicator path.
            const dynamicGroups = new Map<DynamicSourceResolver, SwadeModificationInterface[]>();
            const realChoiceSets: SwadeModificationInterface[] = [];

            for (const mod of (modifications ?? [])) {
                // The one "asks" rule, shared with the unique flag (spec 2026-10-13 §4). It skips an
                // offered set (spec 2026-09-21 §5.1), which is picked on a power card, never here:
                // neither the add-time prompt nor the world prompt may collect it.
                if (!asksWhenResolved(mod)) continue;
                if (mod.configuration?.path === 'character.choiceSet') {
                    realChoiceSets.push(mod);
                    continue;
                }
                const resolver = DYNAMIC_SOURCE_RESOLVERS.find((r) => r.matches(mod));
                if (!resolver) continue;
                if (!dynamicGroups.has(resolver)) dynamicGroups.set(resolver, []);
                dynamicGroups.get(resolver)!.push(mod);
            }

            if (dynamicGroups.size === 0 && realChoiceSets.length === 0) {
                return { applied: [], resolved: [] };
            }

            const applied: AppliedConfiguration[] = [];

            // Dynamic resolvers run sequentially; each owns its own UI session.
            for (const [resolver, mods] of dynamicGroups) {
                const groupApplied = await resolver.resolveBatch(mods, character, source, existingChoices);
                applied.push(...groupApplied);
            }

            // Real choice sets, then a target for each chosen option that is itself a pick.
            if (realChoiceSets.length > 0) {
                const appData = useAppDataStore();
                const realApplied = await appData.requestChoices(source, realChoiceSets, chosenOptionRecords(realChoiceSets, existingChoices));
                for (const record of realApplied) {
                    applied.push(...await resolveOptionTarget(character, source, record, existingChoices));
                }
            }

            // The whole answer is in: a pick that landed on a stack takes one unit of it (spec 2026-10-13
            // §6). This is why resolving can change the sheet; a cancel above never gets this far.
            const split: AppliedConfiguration[] = await this.splitTargetedStacks(character, applied);
            const resolved = split.map(a => deepCopyObject(a.configuration) as SwadeModConfigurationInterface);
            return { applied: split, resolved };
        },
        // `character: any` accepts the CharacterSheetInterface intersection-type character
        // without requiring consumers to narrow through every mixed-in system shape.
        async withChoiceResolution<T>(
            character: any,
            source: { uuid: string, name: string },
            modifications: SwadeModificationInterface[],
            finishFn: (resolved: SwadeModConfigurationInterface[]) => Promise<T>,
            multiplier: number = 1,
        ): Promise<T | undefined> {
            let applied: AppliedConfiguration[];
            let resolved: SwadeModConfigurationInterface[];
            try {
                ({ applied, resolved } = await this.resolveChoiceSets(character, source, modifications));
            } catch (err) {
                if (err instanceof ChoiceCancelledError) return undefined;
                throw err;
            }

            if (applied.length > 0) {
                character.additionalData ??= {};
                character.additionalData.choices ??= [];
                character.additionalData.choices.push(...applied);
            }

            const passes = Math.max(1, multiplier);
            for (let i = 0; i < passes; i++) {
                for (const mod of directModificationsWithoutOverrides(character, source.uuid, modifications)) {
                    await this.applyModification(character, mod.configuration, true, 'permanent', source.uuid);
                }
            }

            const result = await finishFn(resolved);
            await this.syncCarriedPayloads(character);
            return result;
        },
        // `character: any` accepts the CharacterSheetInterface intersection-type character
        // without requiring consumers to narrow through every mixed-in system shape.
        async rollbackAppliedChoicesFor(character: any, containerUuid: string): Promise<void> {
            const all = character.additionalData?.choices ?? [];
            const matching = all.filter((c: AppliedConfiguration) => c.uuid === containerUuid);
            for (const choice of matching) {
                await this.rollbackModification(character, choice.configuration, true, 'permanent', containerUuid);
            }
            // The LIVE array, never `all`: rolling back a recorded `character.edges add` removes that
            // edge, and its removeEdge re-enters here and prunes the edge's own answers. Writing `all`
            // back restored them as orphans (2026-10-17). Same rule as retireResolvedChoices.
            const current = character.additionalData?.choices;
            if (current) {
                character.additionalData.choices = current.filter((c: AppliedConfiguration) => c.uuid !== containerUuid);
            }

            // Ledger first, then recompute. Deregistering after the rollback loop above — rather
            // than inside it — keeps the two ledgers in step: choices are pruned by source, and so
            // are effects.
            this.deregisterContainerEffects(character, containerUuid);
            await this.syncCarriedPayloads(character);
        },
        /**
         * Record and apply one table-roll pick. Writes an anchor + one record
         * per result modification to additionalData.choices, then dispatches
         * only the real-path records (anchors are identity-only). Defensive:
         * clears any existing pick at the same slot first.
         */
        async applyTableRoll(
            character: CharacterSheetInterface,
            args: { sourceUuid: string; modUuid: string; tableUuid: string; result: RandomTableResultInterface; rollIndex: number },
        ): Promise<void> {
            await this.rollbackTableRoll(character, { modUuid: args.modUuid, rollIndex: args.rollIndex });

            const records = buildTableRollRecords(args);
            character.additionalData ??= {};
            character.additionalData.choices ??= [];
            character.additionalData.choices.push(...records);

            for (const record of records) {
                if ((record.configuration?.path ?? null) === null) continue; // skip anchor
                await this.applyModification(character, record.configuration, true, 'permanent', record.uuid);
            }
            await this.syncCarriedPayloads(character);
        },

        /**
         * Roll back one pick: rollbackModification each real-path record for
         * (modUuid, rollIndex), then remove all matching records (anchor + mods).
         */
        async rollbackTableRoll(
            character: CharacterSheetInterface,
            args: { modUuid: string; rollIndex: number },
        ): Promise<void> {
            const choices = character.additionalData?.choices;
            if (!Array.isArray(choices)) return;

            const matching = recordsForModification(choices, args.modUuid)
                .filter((r) => r.rollIndex === args.rollIndex);
            if (matching.length === 0) return;

            for (const record of matching) {
                if ((record.configuration?.path ?? null) === null) continue; // skip anchor
                await this.rollbackModification(character, record.configuration, true, 'permanent', record.uuid);
            }

            // The LIVE array, never `choices`: see rollbackAppliedChoicesFor.
            const removeSet = new Set(matching);
            character.additionalData!.choices = (character.additionalData?.choices ?? []).filter((r: AppliedConfiguration) => !removeSet.has(r));
            await this.syncCarriedPayloads(character);
        },
        // Removes choices whose container uuid is no longer present in any of the character's
        // live containers. Roll back each orphan's modification before removing it. This catches
        // ancestry choices left behind after the ancestry was cleared without a full rollback.
        async cleanupOrphanedChoices(character: any): Promise<void> {
            const choices = character.additionalData?.choices;

            // Built unconditionally. The container-effects ledger below is pruned against the same
            // live set, and it is a SEPARATE ledger from `choices` — an effect can be orphaned on a
            // character that has no choices at all, so this must not be gated on that array.
            const live = new Set<string>();
            // The world is a first-class choice source (see resolveWorldChoiceSets); its
            // applied choiceSet is keyed on world.uuid. Without this, cleanup prunes the
            // world's choice as an orphan whenever another source (e.g. framework) is cleared.
            if (character.world?.uuid) live.add(character.world.uuid);
            // A character-local setting is a choice container on the same footing as a world,
            // and character.world is null by construction whenever one is present — so without
            // this every choice it applied reads as an orphan. Keyed on the CURRENT blob uuid,
            // so re-seeding from a different template still prunes the old answers.
            if (character.additionalData?.localSetting?.uuid) {
                live.add(character.additionalData.localSetting.uuid);
            }
            if (character.ancestry?.uuid) live.add(character.ancestry.uuid);
            if (character.framework?.uuid) live.add(character.framework.uuid);
            const collect = (items: any[] | undefined) => {
                if (!Array.isArray(items)) return;
                for (const item of items) if (item?.uuid) live.add(item.uuid);
            };
            collect(character.abilities);
            collect(character.edges);
            collect(character.hindrances);
            collect(character.equipment);
            collect(character.armor);
            collect(character.powers);

            if (Array.isArray(choices) && choices.length > 0) {
                const orphans = choices.filter((c: AppliedConfiguration) => !c.uuid || !live.has(c.uuid));
                for (const orphan of orphans) {
                    await this.rollbackModification(character, orphan.configuration, true, 'permanent', orphan.uuid);
                }
                // The LIVE array, never `choices`: see rollbackAppliedChoicesFor. Drops exactly the
                // orphans rolled back above, as the `live` test did on the snapshot.
                const rolledBack = new Set(orphans);
                character.additionalData.choices = (character.additionalData?.choices ?? []).filter((c: AppliedConfiguration) => !rolledBack.has(c));
            }

            // NOT nested in the choices block above. Deregistration is narrow by design — one
            // rollback removes one effect — which makes this the only net left for an apply/rollback
            // count mismatch, and an effect whose source is gone must be pruned whether or not the
            // character happens to hold any choices. Nested, `choices: []` left the orphan (and its
            // override) in place while one unrelated choice was enough to prune it.
            //
            // The recompute moves with the filter, not with the choices block: this line mutates the
            // ledger, and recomputeContainerOverrides' contract is that callers rebuild after doing
            // so. Guarded on additionalData only so a character that has none is left untouched
            // rather than having the key created for it.
            if (character.additionalData) {
                const effects: ContainerEffect[] = Array.isArray(character.additionalData.containerEffects)
                    ? character.additionalData.containerEffects
                    : [];
                character.additionalData.containerEffects = effects.filter(
                    (e) => e.sourceUuid && live.has(e.sourceUuid),
                );

                this.recomputeContainerOverrides(character);
            }

            // Also drop grantedEquipment entries whose containerUuid is no longer present.
            const grantedEquipment = character.additionalData?.grantedEquipment;
            if (Array.isArray(grantedEquipment) && grantedEquipment.length > 0) {
                const liveContainerUuids = new Set<string>(
                    (Array.isArray(character.equipment) ? character.equipment : [])
                        .map((c: any) => c.uuid)
                        .filter((u: any): u is string => typeof u === 'string' && u.length > 0),
                );
                character.additionalData.grantedEquipment = grantedEquipment.filter(
                    (g: any) => liveContainerUuids.has(g.containerUuid),
                );
            }

            await this.syncCarriedPayloads(character);
        },
        /**
         * Clear and rebuild every container override from `additionalData.containerEffects`.
         *
         * Total, not incremental. Character collections are small, and a global rebuild has ONE
         * invariant to verify instead of an obligation on every caller to work out which targets a
         * ledger change touched. It is also self-healing: a stale override left by any earlier bug
         * is cleared on the next pass.
         *
         * Callers MUST mutate the ledger BEFORE calling this. Existing removal paths roll back while
         * the record is still present and filter it afterwards (rollbackAppliedChoicesFor:392-400,
         * cleanupOrphanedChoices:471), and recomputing from that mid-rollback state would re-apply
         * the very effect being removed.
         */
        recomputeContainerOverrides(character: any): void {
            const effects: ContainerEffect[] = Array.isArray(character?.additionalData?.containerEffects)
                ? character.additionalData.containerEffects
                : [];

            // Index the effects by target container, so each container is rebuilt in one pass.
            const byTarget = new Map<string, ContainerEffect[]>();
            for (const effect of effects) {
                // The ledger is persisted and decoded from JSON, so an entry's path is untrusted
                // input rather than something this session necessarily wrote. Without this, a
                // `character.*` record — or one with no path — writes into container overrides.
                //
                // hasOwnProperty rather than a bare lookup, matching containerFieldPolicy's own
                // guard (container-fields.ts) and ownDetail below: 'constructor' resolves to the
                // Object constructor, '__proto__' to Object.prototype, 'toString' to a function —
                // all truthy, so a bare lookup would pass `if (!arrayName)` and go on to build a
                // key no real container can match. Harmless today only by that accident, not
                // because this lookup is guarded.
                const path = effect?.configuration?.path ?? '';
                const arrayName = Object.prototype.hasOwnProperty.call(CONTAINER_PATHS, path)
                    ? supportedContainerPath(path)
                    : undefined;
                if (!arrayName) continue;

                // effectTargets resolves an All Specified entry against the sheet NOW and reads a
                // frozen entry's stored list; a stored uuid with no container, or a blank one, yields
                // nothing, so no bucket is ever keyed on a uuid-less container.
                for (const target of effectTargets(character, effect)) {
                    const key = `${arrayName}:${target.uuid}`;
                    if (!byTarget.has(key)) byTarget.set(key, []);
                    // The entry as THIS target should see it (spec 2026-09-15): a player-entered
                    // value stands in for a null authored one, per target, so the fold below treats
                    // it like any authored value. The ledger entry itself is never changed — its
                    // configuration.value stays null and its derived key stays stable.
                    byTarget.get(key)!.push(effectiveEffect(effect, target.uuid));
                }
            }

            for (const arrayName of new Set(Object.values(CONTAINER_PATHS))) {
                const containers: any[] = Array.isArray(character?.[arrayName]) ? character[arrayName] : [];

                for (const container of containers) {
                    const key = `${arrayName}:${container?.uuid ?? ''}`;
                    this.rebuildContainerOverrides(
                        container,
                        arrayName === 'powers' ? powerDetailBase(container) : (container?.selection?.details ?? null),
                        byTarget.get(key) ?? [],
                        arrayName === 'powers',
                        ownFieldsFor(arrayName),
                    );
                }
            }
        },

        /**
         * Rebuild one container's two override namespaces from the effects targeting it.
         *
         * Writes to `configuration`, and to the container's own properties named in `ownFields`
         * (ownFieldsFor) — NEVER through `selection`. `resolveContainerDetails` hands back
         * `selection.details` by reference when no override survives, and containers built from one
         * catalog item share a `selection` — writing through it corrupts the catalog for every
         * character holding the item.
         *
         * `base` is the object a detail's untouched value is read from — an item's `selection.details`,
         * a power's `powerDetailBase` — so this writer never derives a base itself.
         */
        rebuildContainerOverrides(container: any, base: unknown, effects: ContainerEffect[], keepEmptyObject: boolean = false, ownFields: Readonly<Record<string, string>> = {}): void {
            if (!container) return;

            const details: Record<string, unknown> = {};
            const containerNamespace: Record<string, unknown> = {};
            // A field in `ownFields` resolves here instead of into containerNamespace, and reaches the
            // container's own property through the reconciliation below.
            const fieldWrites: Record<string, string> = {};
            // The player's own text behind each written property (ruling 2026-09-24): captured the
            // first time the property is written, kept while a write is in force, so a text-list fold
            // starts from it on every rebuild rather than from its own output, and the property gets it
            // back when nothing targets the field any more. Blank text is not recorded.
            const fieldBases: Record<string, string> = {};
            const previousWrites: unknown = container.configuration?.fieldWrites;
            const previousBases: unknown = container.configuration?.fieldBases;
            const ownFieldBase = (detail: string): string | null =>
                ownDetail(previousWrites, detail) !== null
                    ? coerceContainerValue(ownDetail(previousBases, detail))
                    : coerceContainerValue(container[ownFields[detail]]);

            // Group by field first. A field is only resolved when at least one effect targets it AND
            // at least one of those effects contributes something — zero-config resolution is NOT
            // identity ('0/6/12' -> 0, '1d6' -> 'd6'), so resolving an untouched, or an
            // all-discardable, field would silently rewrite it.
            const byField = new Map<string, ContainerEffect[]>();
            for (const effect of effects) {
                const detail = effect?.configuration?.detail ?? null;
                // See isUnsafeDetailKey above: `detail` is author-supplied, and '__proto__' /
                // 'constructor' / 'prototype' must never reach a bracket-write into details or
                // containerNamespace below.
                if (detail === null || isUnsafeDetailKey(detail)) continue;
                // A `block`, a blank/null operator (blank-form-template), or an add/subtract/
                // multiply/alter with a null or blank value changes nothing — see
                // contributesSomething above. Filtering here, before grouping, keeps an
                // all-discardable field out of byField entirely (untargeted, not resolved against
                // nothing) and — as a side effect relied on below — leaves the LAST entry of any
                // field that does survive as the last one that actually contributes, not merely the
                // last one authored.
                if (!contributesSomething(effect?.configuration)) continue;
                if (!byField.has(detail)) byField.set(detail, []);
                byField.get(detail)!.push(effect);
            }

            for (const [detail, fieldEffects] of byField) {
                const policy = containerFieldPolicy(detail);

                // `charges` and any future construction-time field never participate in a recompute.
                if (policy?.timing === 'construction') continue;

                const configurations = fieldEffects.map((effect) => effect.configuration);

                if (policy) {
                    // This writer implements lastWins and textList. Consult the registry rather than
                    // assume every non-construction policy field folds one of these ways, so an
                    // additiveSum/override field added later is skipped here — with this comment as
                    // the marker — instead of being silently mis-folded.
                    if (policy.fold !== 'lastWins' && policy.fold !== 'textList') continue;

                    const own = Object.prototype.hasOwnProperty.call(ownFields, detail);

                    if (policy.fold === 'textList') {
                        // textList (ruling 2026-09-24): the fold notes run too — alter overwrites, add
                        // joins with ", ", subtract removes an exact item. An own field folds from the
                        // player's own text; a synthetic field has nothing underneath.
                        const base = own ? ownFieldBase(detail) : null;
                        const folded = foldTextList(base, configurations);
                        if (own) {
                            fieldWrites[detail] = folded;
                            if (base !== null) fieldBases[detail] = base;
                            continue;
                        }
                        containerNamespace[detail] = folded;
                        continue;
                    }

                    // lastWins: the final CONTRIBUTING configuration's value replaces whatever is
                    // underneath. byField already dropped discardable configurations above —
                    // including, now, any value that is not a string or a number (see
                    // contributesSomething) — so "the final configuration" here already means the
                    // final one that both does something AND is renderable on a policy field.
                    // Nothing left to re-check: trusting that one filter, instead of re-testing
                    // `last.value`'s type here too, is what keeps this in sync with it rather than
                    // two rules that can drift apart.
                    //
                    // Insertion order, NOT a sort. This is deliberate and is NOT the order-dependence
                    // defect that layer 3a kept hitting: there the result had to be independent of
                    // array order, whereas "last write wins" makes order the semantics. Ledger order
                    // is stable — effects are appended in application order, deregistration filters
                    // without reordering, and JSON round-trips preserve array order.
                    //
                    // Known limitation, accepted: if two DIFFERENT sources both override one field,
                    // the winner depends on which source was applied first, which can differ between
                    // a fresh build and a reload. Every use case on the table is one effect per
                    // container per field, so this is not reachable today. Do not "fix" it with an
                    // arbitrary sort — that trades a rare ambiguity for a permanent one.
                    const last = configurations[configurations.length - 1];
                    if (own) {
                        // Narrowed to text: the property is a string column, and a number from
                        // decoded JSON must not reach it as one.
                        const value = coerceContainerValue(last.value);
                        if (value !== null) {
                            fieldWrites[detail] = value;
                            const base = ownFieldBase(detail);
                            if (base !== null) fieldBases[detail] = base;
                        }
                        continue;
                    }
                    containerNamespace[detail] = last.value;
                    continue;
                }

                // Not a policy field, so it belongs to layer 3a. resolveDetail is the sole authority
                // for item detail fields and returns undefined for anything it does not register —
                // undefined means "no strategy", never "no value", so it must not be written.
                const resolved = resolveDetail(detail, ownDetail(base, detail), configurations as never);
                if (resolved !== undefined) details[detail] = resolved;
            }

            // A real property is written only when what the effects resolve to CHANGES.
            // `configuration.fieldWrites` records the value last written, so a rebuild that resolves
            // the same value — and one runs at the end of nearly every sheet action — leaves the
            // player's own edit in place, while one that resolves another value writes it over.
            // One that resolves nothing hands the property the player's own text back
            // (`fieldBases`, ruling 2026-09-24) — blank when there was none, as before that ruling.
            for (const [detail, property] of Object.entries(ownFields)) {
                const value = fieldWrites[detail] ?? null;
                if (value === (ownDetail(previousWrites, detail) ?? null)) continue;
                container[property] = value ?? coerceContainerValue(ownDetail(previousBases, detail));
            }

            const next: Record<string, unknown> = { ...(container.configuration ?? {}) };

            // Merge, never replace. EdgeContainer.configuration is a pre-existing column and the
            // API filter deliberately passes unknown keys through untouched.
            if (Object.keys(details).length > 0) next.details = details;
            else delete next.details;

            if (Object.keys(containerNamespace).length > 0) next.container = containerNamespace;
            else delete next.container;

            if (Object.keys(fieldWrites).length > 0) next.fieldWrites = fieldWrites;
            else delete next.fieldWrites;

            if (Object.keys(fieldBases).length > 0) next.fieldBases = fieldBases;
            else delete next.fieldBases;

            // A power's configuration also holds its modifiers and level copies, and the API column
            // is a non-nullable array: it stays an object even when nothing at all remains.
            container.configuration = Object.keys(next).length > 0 ? next : (keepEmptyObject ? {} : null);
        },
        /**
         * previous -> next as a SET operation per (source container, modification). Rolls back only
         * what left the set and applies only what joined it, then replaces that modification's
         * records atomically. Ordinary choice sets are one-element sets, so this is the one
         * reconciliation path for both editChoiceSets and editContainerTargets.
         *
         * One container-specific rule: a fixed-target container modification with NO records has
         * nothing currently applied — since spec r3 (whole-branch review ruling, 2026-09-14), a
         * Specific Choices modification applies only through a recorded pick — but a legacy fan-out
         * may still hold a ledger entry keyed off the AUTHORED configuration (registerContainerEffect
         * refused to create one after that spec, but earlier data can still carry one). So the
         * "previous" set defaults to `[authoredConfiguration]` rather than `[]` even with no records:
         * for a fresh modification (no such entry) rolling that back is a harmless no-op, and for a
         * legacy fan-out it is exactly what retires the entry, by key, the moment a pick is first
         * confirmed.
         */
        async reconcileRecordSets(
            character: any,
            container: { uuid: string, name: string },
            source: { modifications?: SwadeModificationInterface[] },
            next: AppliedConfiguration[],
        ): Promise<void> {
            const allChoices: AppliedConfiguration[] = character.additionalData?.choices ?? [];
            const previousAll = allChoices.filter((c) => c.uuid === container.uuid);

            // Multiplier and tier exactly as editChoiceSets computed them.
            const equipmentContainer = (character.equipment ?? []).find(
                (c: SwadeItemContainerInterface) => c.uuid === container.uuid
            );
            const isCyberware = equipmentContainer?.selection?.type === 'cyberware';
            const ticksPerChange = equipmentContainer
                ? (this.isItemInUse(equipmentContainer) ? this.multiplierForContainer(equipmentContainer) : 0)
                : 1;
            const modSource: ModSource = isCyberware ? 'temporary' : 'permanent';

            // The `!!u` guard is defensive: every current producer of AppliedConfiguration records
            // (ChoiceApplicator, the dynamic-source resolvers, preselectionFor) sets sourceModUuid or
            // choiceSetUuid, so a record with neither is not reachable through any caller today and
            // no test exercises this filter actually removing anything.
            const modUuids = [...new Set(next.map(resolvedModificationUuid))].filter((u): u is string => !!u);
            const touched = new Set<string>();
            const written: AppliedConfiguration[] = [];

            for (const modUuid of modUuids) {
                const nextRecords = next.filter((r) => resolvedModificationUuid(r) === modUuid);
                const previousRecords = previousAll.filter((r) => resolvedModificationUuid(r) === modUuid);
                const authored = (source.modifications ?? []).find((m) => m.uuid === modUuid);
                const authoredConfiguration = authored?.configuration;
                const isFixedContainerMod = !!authoredConfiguration
                    && !!supportedContainerPath(authoredConfiguration.path)
                    && authoredTargetMode(authoredConfiguration) === 'specific';

                const previousConfigurations = previousRecords.length > 0
                    ? previousRecords.map((r) => r.configuration)
                    : (isFixedContainerMod ? [authoredConfiguration!] : []);
                const { removed, added } = diffConfigurationSets(previousConfigurations, nextRecords.map((r) => r.configuration));

                // Rollback before apply, mirroring the order the old editChoiceSets used
                // (`if (prev) await rollback(...); await apply(...)`). removed/added are disjoint by
                // diffConfigurationSets' construction and the container-effects ledger is a plain
                // push/splice with no capacity limit, so no test can currently observe apply-before-
                // rollback behaving differently — kept in this order for clarity of intent, not
                // because a test depends on it.
                for (let i = 0; i < ticksPerChange; i++) {
                    for (const configuration of removed) {
                        await this.rollbackModification(character, configuration, true, modSource, container.uuid);
                    }
                    for (const configuration of added) {
                        await this.applyModification(character, configuration, true, modSource, container.uuid);
                    }
                }

                touched.add(modUuid);
                written.push(...nextRecords);
            }

            // Filter the LIVE array, never the `allChoices` snapshot: the loop above can write choices
            // itself. An added edge records its own choice set's answer (applyEdgeModification ->
            // addEdgeToCharacter -> withChoiceResolution), on a list it creates when the character
            // had none, and a removed edge prunes its records (removeEdge -> rollbackAppliedChoicesFor).
            // Re-assigning the snapshot lost the first, which hid the edge's ChoicePencil, and
            // resurrected the second (owner report 2026-10-17). Same rule as retireResolvedChoices.
            const current: AppliedConfiguration[] = character.additionalData?.choices ?? [];
            // `?? ''` is a type-safety cast, not a behavioural branch: `touched` is built exclusively
            // from `modUuids` (itself filtered to non-empty strings), so it can never contain '' —
            // `.has(undefined)` and `.has('')` are equally false here regardless of the fallback value.
            const survivors = current.filter((c) =>
                c.uuid !== container.uuid || !touched.has(resolvedModificationUuid(c) ?? '')
            );
            character.additionalData ??= {};
            character.additionalData.choices = [...survivors, ...written];
            await this.syncCarriedPayloads(character);
        },
        // `character: any` accepts the CharacterSheetInterface intersection-type character
        // without requiring consumers to narrow through every mixed-in system shape.
        async editChoiceSets(
            character: any,
            container: { uuid: string, name: string },
            source: { modifications?: SwadeModificationInterface[] },
        ): Promise<void> {
            const allChoices = character.additionalData?.choices ?? [];
            const existing = allChoices.filter((c: AppliedConfiguration) => c.uuid === container.uuid);

            let applied: AppliedConfiguration[];
            try {
                ({ applied } = await this.resolveChoiceSets(character, container, source.modifications ?? [], existing));
            } catch (err) {
                if (err instanceof ChoiceCancelledError) return;
                throw err;
            }

            await this.reconcileRecordSets(character, container, source, applied);
            // A power re-choosing through its header pencil (owner, 2026-09-24) folds its cost and
            // stat line again, as a pick does.
            const rechosenPower = (character.powers ?? []).find((p: SwadePowerContainerInterface) => p.uuid === container.uuid);
            if (rechosenPower) {
                this.recalculatePower(character, rechosenPower);
                this.updateProtoPowers(character);
            }

            const equipmentContainer = (character.equipment ?? []).find(
                (c: SwadeItemContainerInterface) => c.uuid === container.uuid
            );
            const isCyberware = equipmentContainer?.selection?.type === 'cyberware';

            // Recompose container.name for cyberware so the displayed choice label tracks the
            // new selection. Mirrors the add-time composition in _addCyberwareToCharacter.
            if (equipmentContainer && isCyberware) {
                const containerChoices = character.additionalData.choices.filter(
                    (c: AppliedConfiguration) => c.uuid === container.uuid
                );
                const labels = containerChoices
                    .map((c: AppliedConfiguration) =>
                        c.configuration?.label
                            ?? (typeof c.configuration?.element === 'string' ? c.configuration.element : '')
                    )
                    .filter((l: unknown): l is string => typeof l === 'string' && l.length > 0);
                if (labels.length > 0) {
                    equipmentContainer.name = `${equipmentContainer.selection?.name ?? ''} (${labels.join(', ')})`;
                }
            }

            this.recalculateDerivedStats(character);
            this.updateBuildValidation(character, this.buildOptions);
            await this.recalculateTotalPerkPoints(character);
            // updateBuildValidation rebuilds the character-point and language pools with nothing allocated, and the
            // pencil that asked reports to no host, so nothing else fills them back in: the character points read 0
            // until the next edit (owner, 2026-09-29). These two counters, not the whole build-status pass — its
            // attribute sync would carry a cyberware choice's temporary step into calculateBase.strain. Guarded on
            // protoData as recalculateTotalPerkPoints guards updateAllocatedPerkPoints.
            this.updateAllocatedCharacterPoints(character);
            if (character.protoData) this.updateAllocatedLanguages(character);
        },
        /**
         * The container-target menu: re-open (or open for the first time) the pick for every
         * container.* modification a source carries that has something to retarget
         * (`containerTargetEditable`: a stable uuid, a supported path, not self, and for a specific
         * list a choice to make), pre-selected from its records — never from the authored target: with no record nothing is applied yet (spec 2026-09-13 r3) — and
         * reconcile the answer as a set.
         */
        async editContainerTargets(
            character: any,
            container: { uuid: string, name: string },
            source: { modifications?: SwadeModificationInterface[] },
        ): Promise<void> {
            const records: AppliedConfiguration[] = character.additionalData?.choices ?? [];
            const eligible = (source.modifications ?? []).filter((mod) =>
                containerTargetEditable(character, container.uuid, mod, records));
            if (eligible.length === 0) return;

            const synthetics: SwadeModificationInterface[] = [];
            const preselection: AppliedConfiguration[] = [];
            for (const mod of eligible) {
                const synthetic = buildContainerTargetChoiceSet(character, mod, container.uuid);
                if (!synthetic) continue;
                synthetics.push(synthetic);
                preselection.push(...preselectionFor(character, container.uuid, mod, records));
            }
            if (synthetics.length === 0) return;

            let applied: AppliedConfiguration[];
            try {
                applied = await useAppDataStore().requestChoices(container, synthetics, preselection);
            } catch (err) {
                if (err instanceof ChoiceCancelledError) return;
                throw err;
            }

            // The whole answer is in: a pick on a stack takes one unit of it (spec 2026-10-13 §6).
            applied = await this.splitTargetedStacks(character, applied);
            await this.reconcileRecordSets(character, container, source, applied);
        },
        // Presents the ChoiceApplicator for a world's own `character.choiceSet` modifications.
        // The world is a first-class choice source (like ancestry/framework), but unlike them it
        // has no add-time hook — its choices are surfaced when the build view first opens. Gated
        // on unresolved sets so a fully-resolved world never re-prompts. Full world detail
        // (incl. `modifications`) lives on `store.world`, NOT the slim `character.world`.
        async resolveWorldChoiceSets(character: any, world: WorldInterface | null): Promise<void> {
            if (!world?.uuid) return;

            const modifications = world.modifications ?? [];
            const choiceSetMods = modifications.filter(
                (mod: SwadeModificationInterface) =>
                    mod.configuration?.path === 'character.choiceSet' && mod.uuid
                    // An offered set (spec 2026-09-21 §5.1) is picked on a power card, never here.
                    && !isOfferedOption(mod.configuration)
            );
            if (choiceSetMods.length === 0) return;

            const container = { uuid: world.uuid, name: world.name ?? '' };
            const resolved = new Set(
                (character.additionalData?.choices ?? [])
                    .filter((c: AppliedConfiguration) => c.uuid === container.uuid)
                    .map((c: AppliedConfiguration) => c.choiceSetUuid)
            );
            const hasUnresolved = choiceSetMods.some(
                (mod: SwadeModificationInterface) => !resolved.has(mod.uuid!)
            );
            if (!hasUnresolved) return;

            // Two callers reach here for the same world in one commit: applyCharacterLocalSetting
            // prompts at its last step, and its own store.$patch({ world }) re-arms
            // CharacterCreatorView's world watcher, which prompts too. The `hasUnresolved` gate
            // above cannot catch it — both read `choices` before either has written an answer — so
            // the second used to hit the ChoiceApplicator's concurrency guard and surface an error.
            //
            // Joining the in-flight resolution rather than starting a second one keeps this to a
            // single prompt with a single set of records, and does so for any future caller pair
            // rather than just this one.
            const inFlight = this._worldChoiceResolutions.get(world.uuid);
            if (inFlight) return inFlight;

            const resolution = this.editChoiceSets(character, container, { modifications })
                .finally(() => { this._worldChoiceResolutions.delete(world.uuid!); });

            this._worldChoiceResolutions.set(world.uuid, resolution);
            return resolution;
        },
        filtersToQueryString(filters: Array<FilterDescriptor>) {
            return useAppDataStore().filtersToQueryString(filters);
        },
        characterRank(character: CharacterSheetInterface) {
            if (!Array.isArray(character?.abilities)) return "";
            const activeAdvances = character.abilities?.filter((item: SwadeAbilityInterface) => {
                return ['advance'].includes(item.type) && item.active;
            }) ?? [];

            const ux = useUxData();
            if (activeAdvances.length  < 4) {
                return ux.labels.swade?.rank?.novice ?? 'Novice';
            }
            else if (activeAdvances.length  < 8) {
                return ux.labels.swade?.rank?.seasoned ?? 'Seasoned';
            }
            else if (activeAdvances.length  < 12) {
                return ux.labels.swade?.rank?.veteran ?? 'Veteran';
            }
            else if (activeAdvances.length  < 16) {
                return ux.labels.swade?.rank?.heroic ?? 'Heroic';
            }
            else {
                return ux.labels.swade?.rank?.legendary ?? 'Legendary';
            }
        },
        hasAdvances(character: CharacterSheetInterface) {
            if (!Array.isArray(character?.abilities)) return false;

            const advances = character?.abilities?.filter((item: SwadeAbilityInterface) => {
                return item.type === 'advance';
            }) ?? [];

            return (advances.length > 0);
        },
        hasActiveAdvances(character: CharacterSheetInterface) {
            if (!Array.isArray(character?.abilities)) return false;

            const advances = character?.abilities?.filter((item: SwadeAbilityInterface) => {
                return item.type === 'advance' && item.active;
            }) ?? [];

            return (advances.length > 0);
        },
        protoLanguageSkill(character: CharacterSheetInterface, item: SwadeSkillRankInterface) {
            if (!Array.isArray(character.protoData?.skills)) return null;

            return character.protoData?.skills?.find((protoSkill: any) => {
                return protoSkill.rankUuid === item.uuid;
            })
        },
        usesWealthDie(world: WorldInterface) {
            if (!Array.isArray(world?.modifications)) return false;

            const usingWealthDie = world.modifications.filter((modification: SwadeModificationInterface) => {
                return modification?.configuration?.path === 'startingFunds'
                    && modification?.configuration?.element === 'type'
                    && modification?.configuration?.value === 'wealth';
            })

            return (usingWealthDie?.length > 0);
        },
        buildPowerModifierFromSelection(source: any) {
            // Levels come out of the world/power catalog by reference; deep-copy so
            // `v-model="modifier.level.powerPointModifier"` cannot write back into it.
            return {
                uuid: source?.uuid,
                label: source?.label,
                summary: source?.summary,
                level: deepCopyObject(source?.levels?.at(0) ?? {}),
                multiplier: 1,
                multipleChoice: (source?.levels?.length ?? 0) > 1,
                allowMultiple: (source?.allowMultiple ?? false),
                maxSelections: (source?.maxSelections ?? null),
            };
        },
        recalculatePowerPointTotal(power: any) {
            const defaultLevel = power.selection?.details?.levels?.at(0) ?? power.selection?.details ?? power.details;

            let total = Number(power.basePowerPoints ?? defaultLevel?.powerPoints ?? 0);
            if (!Number.isFinite(total)) {
                // Textual costs ("Special", "1/target") take no modifier math; keep the
                // point fields null so displays fall back to the selection's text.
                power.powerPoints = null;
                return;
            }

            if (Array.isArray(power.configuration.modifiers)) {
                // String() only spells out the ToString parseInt already performs on a non-string
                // argument; the parsed result is unchanged.
                power.configuration.modifiers.forEach((item: SelectedPowerModifier) => {
                    console.debug(item);
                    total = total + parseInt(String(item.level?.powerPointModifier ?? "0")) * parseInt(String(item.multiplier ?? "1"));
                    console.debug(total);
                })
            }

            power.powerPoints = total;
        },
        /**
         * A power's cost total, then the fold of every container.powers effect targeting it (spec
         * 2026-09-11 §4). `power.powerPoints` stays the PRE-override total — the fold's base, never
         * written back — so this is idempotent; the resolved cost lives in configuration.details
         * .powerPoints and readers take it first (resolvePowerDetails). With no effect the two
         * namespaces are removed and the container is byte-identical to what it was.
         */
        recalculatePower(character: any, power: SwadePowerContainerInterface) {
            if (!power) return;
            this.recalculatePowerPointTotal(power);
            const uuid = typeof power.uuid === 'string' ? power.uuid : null;
            const ledger: unknown = character?.additionalData?.containerEffects;
            const effects = (Array.isArray(ledger) ? ledger as ContainerEffect[] : [])
                .filter((effect) =>
                    effect?.configuration?.path === 'container.powers'
                    && uuid !== null
                    && effectTargets(character, effect).some((target: any) => target?.uuid === uuid))
                // The same substitution recomputeContainerOverrides makes: one rule for every
                // ledger reader (spec 2026-09-15 §5), even though no power row enters a value yet.
                .map((effect) => effectiveEffect(effect, uuid));
            this.rebuildContainerOverrides(power, powerDetailBase(power), effects, true, ownFieldsFor('powers'));
        },
        /**
         * The modifications a power container grants — `configuration.modifications`, a
         * superpower's scaled copies — applied exactly as the cards always applied them inline: a
         * copy of each configuration, no proto update, permanent, the container as source, each call
         * started in list order without waiting for the previous (spec 2026-09-11 §6). `configurations`
         * narrows the list to what one handler is applying; the default is the whole list, and a
         * container persisted without the key applies nothing.
         */
        applyPowerModifications(character: any, power: SwadePowerContainerInterface, configurations?: SwadeModConfigurationInterface[]): Promise<unknown[]> {
            const list = configurations ?? (power?.configuration?.modifications ?? []).map((m) => m.configuration);
            return Promise.all(list.map((configuration) =>
                this.applyModification(character, { ...configuration }, false, 'permanent', power?.uuid ?? null)));
        },
        /** The reverse of applyPowerModifications, with the same arguments. */
        rollbackPowerModifications(character: any, power: SwadePowerContainerInterface, configurations?: SwadeModConfigurationInterface[]): Promise<unknown[]> {
            const list = configurations ?? (power?.configuration?.modifications ?? []).map((m) => m.configuration);
            return Promise.all(list.map((configuration) =>
                this.rollbackModification(character, { ...configuration }, false, 'permanent', power?.uuid ?? null)));
        },
        /**
         * Creation-time allocation stays frozen while any super power remains on the sheet: the one
         * rule addPowerToCharacter and removePowerContainer both recompute after touching
         * character.powers, reading the container's selection (spec §5.5).
         */
        syncPowerLock(character: any): void {
            if (!character?.protoData) return;
            const locked = (Array.isArray(character.powers) ? character.powers : [])
                .some((p: SwadePowerContainerInterface) => p?.selection?.details?.type === 'superpower');
            // Frozen as the sheet locks, before the recalculation after the add rebuilds the totals from the base.
            if (locked) this.freezeCreationPools(character);
            character.protoData.locked = locked;
        },
        /**
         * The creation-time pools BuildStatus shows once a sheet locks (character.configuration), frozen as
         * they stand now: a deep copy, because a locked sheet's live totals are rebuilt from the base without
         * their build-point modifiers, and a copy sharing its nested pools with the live build drifts with it
         * (then is saved that way). The character points stay live. A sheet that already holds its pools
         * (loaded, or locked earlier) keeps them. The one rule for both locks — the first active advance and
         * the first super power — whose two copies used to diverge.
         */
        freezeCreationPools(character: any): void {
            character.configuration = character.configuration ?? { ...deepCopyObject(this.build), other: undefined };
        },
        /**
         * One add path for a power (spec 2026-09-21 §5.5): the container, its direct grants once per
         * level, the cost fold and the tracker. Offered sets are never prompted (§5.1). Only the FIXED
         * grants go through the resolver: a target grant (blank element, §5.4) applies nothing until
         * the player picks its target on the card, and handing it to the resolver would let the skills
         * dynamic resolver prompt for it.
         */
        async addPowerToCharacter(character: any, power: SwadePowerInterface): Promise<SwadePowerContainerInterface | null> {
            const container = this.newPowerContainer(character, power);
            if (!container) return null;
            if (!Array.isArray(character.powers)) character.powers = [];
            character.powers.push(container);
            // As before (previously Details.vue's onPowerChanged, ahead of the add): the store now
            // owns this recompute on both sides of the add/remove pair.
            this.syncPowerLock(character);

            // withChoiceResolution applies the grants `passes` times, prompts the catalog's own choice
            // sets (never an offered set — that is a modifier the card picks; owner, 2026-09-24: Reach
            // asks its reach when the power is added, as an edge asks its choices; shaped by the level
            // count, one pick per level — owner, 2026-09-25, Heightened Senses) and its container picks
            // (owner, 2026-09-25: a container.skills grant asks which skill, as an edge's would — the
            // card's pickers cover the three character paths alone), applies each answer once with the
            // power as source — the record is rolled back with the power — and ends with
            // syncCarriedPayloads.
            await this.withChoiceResolution(
                character,
                { uuid: container.uuid as string, name: power.name ?? '' },
                [...fixedGrants(container), ...containerTargetGrants(container), ...catalogChoiceSetPrompts(container)],
                async (resolved: SwadeModConfigurationInterface[]) => {
                    for (const config of resolved) {
                        await this.applyModification(character, config, true, 'permanent', container.uuid as string);
                    }
                },
                grantMultiplier(container),
            );

            this.recalculatePower(character, container);
            this.sortPowersList(character);
            this.updateProtoPowers(character);
            return container;
        },

        /**
         * Apply or roll back a schema-2 power's direct grants `count` times, keyed to the container —
         * the tick equipment quantities use, for levels. A grant that has records is applied by the
         * record loop, and a target grant only ever applies through its records, so both are filtered
         * out here.
         */
        async tickPowerGrants(character: any, power: SwadePowerContainerInterface, count: number, action: 'apply' | 'rollback'): Promise<void> {
            if (count <= 0 || typeof power?.uuid !== 'string') return;
            const grants = directModificationsWithoutOverrides(character, power.uuid, fixedGrants(power));
            for (let i = 0; i < count; i++) {
                for (const grant of grants) {
                    if (action === 'apply') await this.applyModification(character, grant.configuration, true, 'permanent', power.uuid);
                    else await this.rollbackModification(character, grant.configuration, true, 'permanent', power.uuid);
                }
            }
        },

        /**
         * One removal path (spec §5.5): a legacy container rolls back its copies, a schema-2 container
         * unwinds its grants once per level; both strip their records and effects, leave the list, and
         * re-derive the ledgers and the derived stats.
         */
        async removePowerContainer(character: any, power: SwadePowerContainerInterface): Promise<void> {
            if (!character || !power) return;
            if (isLegacyPowerContainer(power)) {
                // Only a legacy SUPER power ever had configuration.modifications actually applied: the
                // retired Details handler pushed and applied copies under this exact condition
                // (`element.details?.type === 'superpower'`). A legacy CORE power's copy list, if it
                // holds anything, was never put on the ledger, so rolling it back here would roll back
                // modifications that were never applied in the first place.
                if (power.selection?.details?.type === 'superpower') {
                    await this.rollbackPowerModifications(character, power);
                }
            }
            else {
                await this.tickPowerGrants(character, power, grantMultiplier(power), 'rollback');
            }
            await this.rollbackAppliedChoicesFor(character, power.uuid ?? '');
            // A container read back through the store is a reactive proxy, so identity alone would keep it;
            // the uuid (every container has one) is the key, identity the fallback.
            character.powers = (Array.isArray(character.powers) ? character.powers : []).filter((p: SwadePowerContainerInterface) =>
                p !== power && (typeof power.uuid !== 'string' || p?.uuid !== power.uuid));
            this.syncPowerLock(character);
            await this.syncCarriedPayloads(character);
            this.updateProtoPowers(character);
            this.recalculateDerivedStats(character);
        },

        /**
         * A lone stepped grant record follows the level (spec §5.4, ruling 22): its value becomes the
         * level count. A grant with several records — a split — keeps its values; the card flags an
         * excess. Element grants (no step) are never touched. A skill rank pick (owner, 2026-10-08) is
         * not a split: every record of it takes the pick's value at the level (levelScaledConfiguration).
         */
        async followTheLevel(character: any, power: SwadePowerContainerInterface, levelCount: number): Promise<void> {
            const records = ownPowerRecords(character, power);
            const grants = directGrants(power);
            const next = records.map((record) => {
                const key = resolvedModificationUuid(record);
                const grant = grants.find((g) => g.uuid === key);
                if (grant && isSkillRankEffect(grant.configuration)) {
                    const value = levelScaledConfiguration(power, grant.configuration).value;
                    if (record.configuration.value === value) return record;
                    return { ...record, configuration: { ...deepCopyObject(record.configuration), value } };
                }
                if (grant?.configuration?.valueType !== 'step') return record;
                if (records.filter((r) => resolvedModificationUuid(r) === key).length !== 1) return record;
                if (Number(record.configuration.value) === levelCount) return record;
                return { ...record, configuration: { ...deepCopyObject(record.configuration), value: levelCount } };
            });
            if (next.every((record, i) => record === records[i])) return;
            await this.reconcileRecordSets(character, { uuid: power.uuid as string, name: power.selection?.name ?? '' }, power.selection, next);
        },

        /**
         * The level menu's choice (spec §5.5): a linear count or a table row. Grants are re-ticked by
         * the level delta, a lone stepped record follows, the copies and basePowerPoints are rewritten,
         * then the fold, the carry sync (which re-scales carried effects) and the tracker.
         */
        async setPowerLevel(character: any, power: SwadePowerContainerInterface, option: PowerLevelOption): Promise<void> {
            if (!character || !power || !option) return;
            const before = grantMultiplier(power);

            if (option.level) {
                power.configuration.range = option.level.range ?? power.configuration.range;
                power.configuration.duration = option.level.duration ?? power.configuration.duration;
                power.configuration.damage = option.level.damage ?? power.configuration.damage;
                power.configuration.multiplier = 1;
            }
            power.basePowerPoints = option.basePowerPoints;

            const after = grantMultiplier(power);
            if (!isLegacyPowerContainer(power) && after !== before) {
                await this.tickPowerGrants(character, power, Math.abs(after - before), after > before ? 'apply' : 'rollback');
                await this.followTheLevel(character, power, after);
            }

            this.recalculatePower(character, power);
            await this.syncCarriedPayloads(character);
            this.updateProtoPowers(character);
        },

        /**
         * Pick an offered set on a power (spec §5.2): ONE record in the shape ChoiceApplicator writes,
         * keyed by the set uuid, holding a copy of the chosen rung — the first when none is named. A
         * repeat multiplies the value, clamped to maxPicks, and never rewrites the label. Applied
         * through reconcileRecordSets, which registers the self effect and syncs the carry.
         */
        async pickPowerOption(
            character: any,
            power: SwadePowerContainerInterface,
            set: SwadeModificationInterface,
            rung?: Partial<SwadeModificationInterface>,
            count: number = 1,
        ): Promise<void> {
            const options = Array.isArray(set?.configuration?.element) ? set.configuration.element as Partial<SwadeModificationInterface>[] : [];
            const chosen = rung ?? options[0];
            if (!chosen?.configuration || typeof power?.uuid !== 'string' || typeof set?.uuid !== 'string') return;

            const configuration = deepCopyObject(chosen.configuration) as SwadeModConfigurationInterface;
            const limit = typeof set.configuration.maxPicks === 'number' ? set.configuration.maxPicks : 1;
            const repeats = Math.max(1, Math.min(Math.floor(count), limit));
            if (repeats > 1) configuration.value = Number(configuration.value ?? 0) * repeats;

            // The count travels on the record beside the multiplied value (ruling 45, 2026-09-23): the carry
            // sync scales the rung's carried effects by it, and it must not depend on the set staying paired.
            const record: AppliedConfiguration = { uuid: power.uuid, sourceModUuid: set.uuid, choiceSetUuid: set.uuid, configuration, ...(repeats > 1 ? { count: repeats } : {}) };
            // A set owns its rung record AND every target record of its effects (spec 2026-09-22 v2.2
            // §3.4): re-picking replaces them all. `previous` pre-selects the prompt below.
            const previous = recordsOfSet(ownPowerRecords(character, power), set.uuid);

            // Effects the player targets are resolved BEFORE any record is written (spec v2.2 §3.2): a
            // cancelled prompt changes nothing. Once the whole answer is in, a target that is a stack
            // has one unit split off for it (spec 2026-10-13 §6).
            let targets: AppliedConfiguration[];
            try {
                targets = await this.resolveRungTargets(character, power, set, chosen, previous, repeats);
            } catch (err) {
                if (err instanceof ChoiceCancelledError) return;
                throw err;
            }

            // Re-read after the await: the prompt is modal, but nothing here should rely on that.
            const own = ownPowerRecords(character, power);
            const previousNow = recordsOfSet(own, set.uuid);
            const others = own.filter((r) => !previousNow.includes(r));

            // A re-pick replaces the WHOLE set (spec v2.2 §3.4, ruling 37). reconcileRecordSets only
            // touches keys present in `next`, so a previous target record whose key this round did
            // not reproduce — the targeted weapon left the sheet, or the new rung carries fewer
            // effects — must be rolled back and dropped here, or it would outlive its pick.
            const nextKeys = new Set([record, ...targets].map((r) => resolvedModificationUuid(r) ?? ''));
            const stale = previousNow.filter((r) => !nextKeys.has(resolvedModificationUuid(r) ?? ''));
            for (const r of stale) {
                await this.rollbackModification(character, deepCopyObject(r.configuration), true, 'permanent', power.uuid);
            }
            if (stale.length > 0) {
                character.additionalData.choices = (character.additionalData?.choices ?? []).filter((r: AppliedConfiguration) => !stale.includes(r));
            }

            await this.reconcileRecordSets(
                character,
                { uuid: power.uuid, name: power.selection?.name ?? '' },
                { modifications: [...(power.selection?.modifications ?? []), set] },
                [...others, record, ...targets],
            );
            this.recalculatePower(character, power);
            this.updateProtoPowers(character);
        },

        /**
         * The records for a picked rung's record-resolved effects (spec 2026-09-22 v2.2 §3.2, ruling 35),
         * by the container-target resolver's own rules: a specific list with exactly one free candidate
         * records it without a prompt; NO free candidate — none on the sheet, or every one taken by
         * another copy of this power (spec 2026-10-13 §4a) — records nothing and the pick still lands
         * (the pencil offers the target when one arrives, as the resolver used by a container's own
         * modifications now does too); everything else is ONE prompt, pre-selected from the
         * set's previous records. A choice-set effect (owner's ruling 2026-09-24, Additional Sense) is
         * its own prompt: the authored set, its options the effects, offered multi-select up to the
         * points invested when the rung is repeated, and answered with one record per chosen option —
         * which the record loop applies to the power like any target record. Cancel throws
         * ChoiceCancelledError.
         */
        async resolveRungTargets(
            character: any,
            power: SwadePowerContainerInterface,
            set: SwadeModificationInterface,
            rung: Partial<SwadeModificationInterface>,
            previous: AppliedConfiguration[],
            repeats: number = 1,
        ): Promise<AppliedConfiguration[]> {
            const source = { uuid: power.uuid as string, name: power.selection?.name ?? '' };
            const applied: AppliedConfiguration[] = [];
            const synthetics: SwadeModificationInterface[] = [];
            const preselection: AppliedConfiguration[] = [];
            for (const mod of rungChoiceSetPrompts(set, rung, repeats)) {
                synthetics.push(mod);
                // The previous answer pre-selects: a record's configuration IS an option's.
                preselection.push(...recordsForSourceModification(previous, source.uuid, mod.uuid));
            }
            for (const mod of rungEffectModifications(set, rung)) {
                if (mod.configuration.path === 'character.choiceSet') continue;
                // Never a weapon another copy of this power already picked with this effect (spec
                // 2026-10-13 §4a); with none free the effect waits, as with none at all.
                const candidates = candidateContainerTargets(character, mod.configuration, { sourceUuid: source.uuid, modUuid: mod.uuid });
                if (candidates.length === 0) continue;
                if (authoredTargetMode(mod.configuration) === 'specific' && candidates.length === 1) {
                    applied.push(containerTargetRecord(source.uuid, mod, candidates[0]));
                    continue;
                }
                // The prompt's question is the effect's label; an effect authored without one — the
                // Craft default — asks under the rung's, else the set's, so the modal never shows a
                // blank line. The target RECORD's label stays the chosen container's name regardless.
                mod.configuration.label = mod.configuration.label ?? rung.configuration?.label ?? set.configuration?.label ?? null;
                const synthetic = buildContainerTargetChoiceSet(character, mod, source.uuid);
                if (!synthetic) continue;
                synthetics.push(synthetic);
                preselection.push(...preselectionFor(character, source.uuid, mod, previous));
            }
            if (synthetics.length > 0) {
                applied.push(...await useAppDataStore().requestChoices(source, synthetics, preselection));
            }
            // The whole answer is in: a pick on a stack takes one unit of it (spec 2026-10-13 §6).
            return this.splitTargetedStacks(character, applied);
        },

        async unpickPowerOption(character: any, power: SwadePowerContainerInterface, set: SwadeModificationInterface): Promise<void> {
            // The set's rung record and every target record of its effects go together (spec v2.2 §3.4).
            const records = recordsOfSet(ownPowerRecords(character, power), set?.uuid ?? '');
            if (records.length > 0) await this.removePowerRecords(character, power, records);
        },
        /** Remove one container record: roll it back (which deregisters its effect), drop it, re-derive. */
        async removePowerRecord(character: any, power: SwadePowerContainerInterface, record: AppliedConfiguration): Promise<void> {
            await this.removePowerRecords(character, power, [record]);
        },
        /** Remove several of one power's records at once: roll each back, drop them, re-derive once. */
        async removePowerRecords(character: any, power: SwadePowerContainerInterface, records: AppliedConfiguration[]): Promise<void> {
            const all: AppliedConfiguration[] = character?.additionalData?.choices ?? [];
            const doomed = records.filter((r) => all.includes(r));
            if (doomed.length === 0 || typeof power?.uuid !== 'string') return;
            for (const record of doomed) {
                await this.rollbackModification(character, deepCopyObject(record.configuration), true, 'permanent', power.uuid);
            }
            // The LIVE array, never `all`: see rollbackAppliedChoicesFor.
            character.additionalData.choices = (character.additionalData?.choices ?? []).filter((r: AppliedConfiguration) => !doomed.includes(r));
            await this.syncCarriedPayloads(character);
            this.recalculatePower(character, power);
            this.updateProtoPowers(character);
        },

        /**
         * Pick a target for a blank-element grant (spec §5.4). An attribute grant holds ONE record whose
         * value is the level count, so a new attribute replaces the old; a skill or edge grant adds a
         * record with the value given (1 by default) beside the others.
         */
        async pickGrantTarget(
            character: any,
            power: SwadePowerContainerInterface,
            grant: SwadeModificationInterface,
            target: { element: string, label: string, value?: number },
        ): Promise<void> {
            if (typeof power?.uuid !== 'string' || typeof grant?.uuid !== 'string' || !target?.element) return;
            const single = grant.configuration.path === 'character.attributes';
            const configuration = deepCopyObject(grant.configuration) as SwadeModConfigurationInterface;
            configuration.element = target.element;
            configuration.label = target.label;
            configuration.value = single ? grantMultiplier(power) : (target.value ?? configuration.value ?? 1);

            const record: AppliedConfiguration = { uuid: power.uuid, sourceModUuid: grant.uuid, configuration };
            const existing = ownPowerRecords(character, power);
            const kept = existing.filter((r) => resolvedModificationUuid(r) !== grant.uuid || (!single && r.configuration.element !== target.element));
            await this.reconcileRecordSets(character, { uuid: power.uuid, name: power.selection?.name ?? '' }, power.selection, [...kept, record]);
            this.updateProtoPowers(character);
        },

        async unpickGrantTarget(character: any, power: SwadePowerContainerInterface, record: AppliedConfiguration): Promise<void> {
            await this.removePowerRecord(character, power, record);
        },

        /** A skill split's per-skill steps: the changed record is rolled back and re-applied by the reconcile diff. */
        async setGrantValue(character: any, power: SwadePowerContainerInterface, record: AppliedConfiguration, value: number): Promise<void> {
            if (typeof power?.uuid !== 'string' || !Number.isFinite(value)) return;
            const records = ownPowerRecords(character, power);
            if (!records.includes(record)) return;
            const next = records.map((r) => r === record ? { ...r, configuration: { ...deepCopyObject(r.configuration), value } } : r);
            await this.reconcileRecordSets(character, { uuid: power.uuid, name: power.selection?.name ?? '' }, power.selection, next);
            this.updateProtoPowers(character);
        },
        modificationTotalSteps(modifications: SwadeModificationInterface[]) {
            let total = 0;

            if (Array.isArray(modifications)) {
                modifications.forEach((item: SwadeModificationInterface) => {
                    if (!Number.isInteger(item.configuration.value)) return;

                    // Number.isInteger is not a TS type predicate, but it guarantees a number here.
                    total = total + (item.configuration.value as number);
                })
            }

            return total;
        },

        resetBuildValidation() {
            this.build = {
                attributes: {
                    allocated: 0,
                    total: 0,
                    unspentPoints: true,
                    fullyAllocated: false,
                    overAllocated: false,
                },
                traits: {
                    allocated: 0,
                    total: 0,
                    unspentPoints: true,
                    fullyAllocated: false,
                    overAllocated: false,
                },
                perkPoints: {
                    allocated: 0,
                    total: 0,
                    max: 0,
                    unspentPoints: true,
                    fullyAllocated: false,
                    overAllocated: false,
                },
                languages: null,
                other: null,
            }

            this.buildOptions = null;
        },
        updateBuildValidation(character: CharacterSheetInterface, settingRules: SystemDefaultsInterface) {
            console.info("SwadeData | updateBuildValidation");
            console.debug("SwadeData | updateBuildValidation | ", character, settingRules);
            if (!character.protoData?.locked) {
                // Carried entries are re-derived here for exactly the reason edge-borne ones are.
                // languageSelections is the one buildPoints element that still falls through to
                // store-only state, so it is re-derived here (ledger included); powerPoints joined
                // the derived pools on 2026-09-21 (see the character-points block below). Without
                // this read a carried language grant works until the character is reloaded and
                // then silently disappears; spec §3.4's reasoning covers only the four DERIVED
                // pools. The ledger, not this walker, decided.
                const languageModifiers: SwadeModConfigurationInterface[] = [
                    ...this.getEdgeModificationConfigs(character.edges ?? [], 'buildPoints', 'languageSelections'),
                    ...carriedBuildOptionConfigs(character.additionalData?.carriedPayloads, 'languageSelections'),
                ];

                languageModifiers.forEach((configuration: SwadeModConfigurationInterface) => {
                    this.applyCharacterBuildOption(character, configuration);
                })

                this.build.attributes.total = (settingRules?.attributes?.creationPoints ?? 5)
                    + this.characterBuildPointModifier(character, 'attributes');

                if (settingRules?.skills?.creationPoints) {
                    this.build.traits.total = settingRules.skills?.creationPoints
                        + this.characterBuildPointModifier(character, 'traits');
                }

                // perkPoints is a CEILING (max only); extraPerkPoints is a GRANT (max AND
                // total, so it can never be clipped by its own ceiling raise). The world's
                // grant lives in hindrances.extraPoints, separate from maxPoints, because
                // .total adds the grant and must never add the ceiling.
                this.build.perkPoints.max =
                    (settingRules?.hindrances?.maxPoints ?? 0)
                    + (!character?.ancestry ? 2 : 0)
                    + this.perkPointModifier(character)
                    + (settingRules?.hindrances?.extraPoints ?? 0)
                    + this.characterBuildPointModifier(character, 'extraPerkPoints');

                if (settingRules?.languages) {
                    const attributeRank = this.dieRank(character[settingRules?.languages.attribute ?? 'smarts'])
                    const languageSlots = 1 + (attributeRank.getValue() * (settingRules?.languages.multiplier ?? 0.5));

                    this.build.languages = {
                        allocated: 0,
                        total: languageSlots,
                        unspentPoints: true,
                        fullyAllocated: false,
                        overAllocated: false,
                    }
                }
                else {
                    this.build.languages = null;
                }
            }

            if (Array.isArray(character?.protoData?.perkPointAllocation)) {
                character.protoData.perkPointAllocation.forEach((item: any) => {
                    if (item.uuid === 'attributeIncrease') {
                        this.build.attributes.total = this.build.attributes.total + (item.value / 2);
                    }
                    else if (item.uuid === 'skillPoints') {
                        this.build.traits.total = this.build.traits.total + item.value;
                    }
                })
            }

            console.debug("CharacterData | updateBuildValidation", this.build, settingRules);
            // Character (power) points: the world's base plus every character-level
            // buildPoints/powerPoints modifier, derived here like the other pools. The block is
            // shown only while the total is non-zero — the same gate the base alone used to pass —
            // and cleared otherwise, so removing the last granting source does not leave the
            // previous figure on the sheet.
            const characterPointsTotal = (settingRules?.characterPoints?.creationPoints ?? 0)
                + this.characterBuildPointModifier(character, 'powerPoints');
            if (this.build && characterPointsTotal) {
                this.build.other = {
                    allocated: 0,
                    total: characterPointsTotal,
                    unspentPoints: true,
                    fullyAllocated: false,
                    overAllocated: false,
                    label: settingRules?.characterPoints?.label ?? null,
                }
            }
            else if (this.build) {
                this.build.other = null;
            }
        },

        perkPointModifier(character: CharacterSheetInterface): number {
            let modifierTotal = 0;
            const modifiersToPerkPoints = [];

            // Framework and edges are first-class sources, exactly as they are for the
            // traits/attributes pools in characterBuildPointModifier. applyCharacterBuildOption
            // early-returns for buildPoints/perkPoints and delegates the whole pool here, so a
            // source this function does not walk is applied by NOTHING — it is dropped silently,
            // with no error and no rollback. That has now happened four times (ancestry grants,
            // then ancestry's own modifications, then framework and edges, then hindrances on
            // 2026-09-18); whenever a new place
            // can carry a buildOption modification, it must be added here too. Carried buildPoints
            // entries are read from the carriedPayloads ledger below, in both walkers.
            if (character.framework) {
                const frameworkModifiers = (character.framework.modifications ?? [])
                    .filter((mod: SwadeModificationInterface) => {
                        return mod.configuration?.path === 'buildPoints' &&
                            mod.configuration?.element === 'perkPoints';
                    })
                    .map((mod: SwadeModificationInterface) => mod.configuration)
                ;

                modifiersToPerkPoints.push(...frameworkModifiers);
            }

            modifiersToPerkPoints.push(
                ...this.getEdgeModificationConfigs(character.edges ?? [], 'buildPoints', 'perkPoints')
            );

            // Hindrances (drawback.hindrance.modifications), read the way edges are.
            if (Array.isArray(character.hindrances)) {
                for (const drawback of character.hindrances) {
                    modifiersToPerkPoints.push(...((drawback as any)?.hindrance?.modifications ?? [])
                        .filter((mod: SwadeModificationInterface) => mod.configuration?.path === 'buildPoints' && mod.configuration?.element === 'perkPoints')
                        .map((mod: SwadeModificationInterface) => mod.configuration));
                }
            }

            if (Array.isArray(character.ancestry?.grants)) {
                // `modifications` is optional on the element interfaces but dereferenced unguarded
                // here; asserted rather than defaulted so an absent list keeps throwing as it does
                // today. The callback element type is SwadeModificationInterface — the annotation
                // said SwadeAbilityInterface, which is what made `configuration` look missing.
                const ancestryModifiers = character.ancestry?.grants?.filter((item: SwadeAbilityInterface) => {
                        console.debug("CharacterData | perkPointModifier", item);
                        return item.type === 'ability' &&
                            item.modifications!.filter((mod: SwadeModificationInterface) => {
                                return mod.configuration.path === 'buildPoints' &&
                                    mod.configuration.element === 'perkPoints';
                            }).length > 0
                    })
                        .map((item: SwadeAbilityInterface) => {
                            return item.modifications!.filter((mod: SwadeModificationInterface) => {
                                return mod.configuration.path === 'buildPoints' &&
                                    mod.configuration.element === 'perkPoints';
                            }).map((mod: SwadeModificationInterface) => {
                                return mod.configuration;
                            });
                        })
                        .reduce((accumulator: SwadeModConfigurationInterface[], value: SwadeModConfigurationInterface[]) => accumulator.concat(value), [] as SwadeModConfigurationInterface[])
                ;

                modifiersToPerkPoints.push(...ancestryModifiers);
            }

            if (Array.isArray(character.ancestry?.modifications)) {
                const ancestryOwnModifiers = character.ancestry!.modifications.filter((mod: SwadeModificationInterface) => {
                        return mod.configuration.path === 'buildPoints' &&
                            mod.configuration.element === 'perkPoints';
                    })
                        .map((mod: SwadeModificationInterface) => {
                            return mod.configuration;
                        })
                ;

                modifiersToPerkPoints.push(...ancestryOwnModifiers);
            }

            if (Array.isArray(character.abilities)) {
                const abilityModifiers =  character.abilities?.filter((item: SwadeAbilityInterface) => {
                        console.debug("CharacterData | perkPointModifier", item);
                        return item.type === 'ability' &&
                            item.modifications!.filter((mod: SwadeModificationInterface) => {
                                return mod.configuration.path === 'buildPoints' &&
                                    mod.configuration.element === 'perkPoints';
                            }).length > 0
                    })
                        .map((item: SwadeAbilityInterface) => {
                            return item.modifications!.filter((mod: SwadeModificationInterface) => {
                                return mod.configuration.path === 'buildPoints' &&
                                    mod.configuration.element === 'perkPoints';
                            }).map((mod: SwadeModificationInterface) => {
                                return mod.configuration;
                            });
                        })
                        .reduce((accumulator: SwadeModConfigurationInterface[], value: SwadeModConfigurationInterface[]) => accumulator.concat(value), [] as SwadeModConfigurationInterface[])
                ;

                modifiersToPerkPoints.push(...abilityModifiers);
            }

            if (Array.isArray(character.additionalData?.choices)) {
                const choiceModifiers = character.additionalData.choices
                    .filter((c: AppliedConfiguration) => {
                        return c.configuration?.path === 'buildPoints' &&
                            c.configuration?.element === 'perkPoints';
                    })
                    .map((c: AppliedConfiguration) => c.configuration);

                modifiersToPerkPoints.push(...choiceModifiers);
            }

            // Carried perkPoints entries: the same ledger read as characterBuildPointModifier (§3.4).
            modifiersToPerkPoints.push(...carriedBuildOptionConfigs(character.additionalData?.carriedPayloads, 'perkPoints'));

            modifiersToPerkPoints.forEach((modifier: WalkerModConfiguration) => {
                console.debug("CharacterData | perkPointModifier", modifier.path, modifier.element);
                // String() only spells out the ToString parseInt already performs.
                let modificationValue: number = parseInt(String(modifier.value)) * (modifier.multiplier ?? 1);
                if (modifier.operator === 'subtract') {
                    modificationValue = modificationValue * -1;
                }

                modifierTotal = modifierTotal + modificationValue;
            })

            return modifierTotal;
        },

        // Sums character-level buildPoints modifiers for a pool: 'traits' → skill points,
        // 'attributes' → attribute points, 'extraPerkPoints' → perk-point GRANTS, 'powerPoints' →
        // character (power) points. All four are DERIVED fresh from character sources (framework,
        // abilities, ancestry, ancestry grants, edges, hindrances, applied choices, carried
        // entries) each recalc so the totals survive a buildOptions rebuild (resetBuildValidation
        // fires on character load and world re-retrieval). The world's own buildPoints mods are
        // NOT included here — they live in the settingRules base via combineSettingRules.
        characterBuildPointModifier(character: CharacterSheetInterface, element: 'traits' | 'attributes' | 'extraPerkPoints' | 'powerPoints'): number {
            const configs: SwadeModConfigurationInterface[] = [];
            const collect = (mods: any[] | undefined | null) => {
                if (!Array.isArray(mods)) return;
                for (const mod of mods) {
                    const cfg = mod?.configuration;
                    if (cfg?.path === 'buildPoints' && cfg?.element === element) configs.push(cfg);
                }
            };

            if (character.framework) collect(character.framework.modifications);
            if (Array.isArray(character.abilities)) character.abilities.forEach((a: any) => collect(a.modifications));
            if (Array.isArray(character.ancestry?.grants)) character.ancestry!.grants.forEach((g: any) => collect(g.modifications));
            if (character.ancestry) collect(character.ancestry.modifications);
            configs.push(...this.getEdgeModificationConfigs(character.edges ?? [], 'buildPoints', element));
            // Hindrances carry their modifications on drawback.hindrance (Pathfinder's Very Young: 2
            // fewer skill and attribute points). Missed until 2026-09-18 — the fourth source found
            // missing from these walkers; see the note in perkPointModifier.
            if (Array.isArray(character.hindrances)) character.hindrances.forEach((d: any) => collect(d?.hindrance?.modifications));
            if (Array.isArray(character.additionalData?.choices)) {
                character.additionalData!.choices.forEach((c: AppliedConfiguration) => {
                    if (c.configuration?.path === 'buildPoints' && c.configuration?.element === element) {
                        configs.push(c.configuration);
                    }
                });
            }

            // Carried buildPoints entries (spec 2026-09-10 carried modifications §3.4): read from the
            // ledger, which syncCarriedPayloads keeps at the in-use count — this walker decides nothing.
            configs.push(...carriedBuildOptionConfigs(character.additionalData?.carriedPayloads, element));

            let total = 0;
            configs.forEach((cfg: SwadeModConfigurationInterface) => {
                let value = parseInt(cfg.value as any) * ((cfg as any).multiplier ?? 1);
                if (isNaN(value)) return;
                if (cfg.operator === 'subtract') value = value * -1;
                total = total + value;
            });

            return total;
        },

        adjustPerkPointAllocation(character: CharacterSheetInterface, allocation: SwadePerkPointAllocation) {
            console.debug("CharacterData | adjustPerkPointAllocation", character);
            if (!character) return;

            if (!Array.isArray(character.protoData.perkPointAllocation)) {
                character.protoData.perkPointAllocation = [];
            }

            character.protoData.perkPointAllocation.push(allocation);

            console.debug("CharacterData | adjustPerkPointAllocation", character.protoData.perkPointAllocation);
            character.protoData.perkPointAllocation = character.protoData.perkPointAllocation.filter((item: SwadePerkPointAllocation) => {
                return item.value > 0;
            })
        },
        adjustStartingFundsPerk(character: CharacterSheetInterface, multiplier: number = 2) {
            if (isNaN(character.purchasingPower)) {
                const wealthRank = new DieRank(character.purchasingPower);
                if (multiplier < 0) {
                    wealthRank.subtractSteps(Math.abs(multiplier));
                }
                else {
                    wealthRank.addSteps(multiplier);
                }
                character.purchasingPower = wealthRank.getRankAsString()
            }
            else {
                const additionalFunds = (this.buildOptions.startingFunds.amount ?? 0) * multiplier;
                // isNaN() above reads purchasing power with Number(), so a numeric string such as '500'
                // reaches this branch; read it the same way, or `+` concatenates ('500' + 1000 was '5001000').
                character.purchasingPower = Number(character.purchasingPower) + additionalFunds;
            }
        },

        combineSettingRules(systemDefaults: SystemDefaultsInterface, world: WorldInterface | null | undefined): SystemDefaultsInterface {
            console.info("SwadeData | combineSettingRules");
            console.debug("SwadeData | combineSettingRules | arguments", systemDefaults, world);
            if (this.buildOptions !== null) return this.buildOptions;

            const defaultsCopy = deepCopyObject<SystemDefaultsInterface>(systemDefaults);
            let mergedDefaults: SystemDefaultsInterface = defaultsCopy;
            if (world?.defaults) {
                // Spread, do not nest. `{ defaultsCopy, ... }` buries every system default under
                // a literal key and loses it. A world blob saved before a defaults field existed
                // simply has no key for it, so spreading lets the system value survive while any
                // key the world DOES carry still wins.
                // Deep-copy the world half for the same reason the system half is copied: a
                // shallow spread aliases world.defaults' nested objects into buildOptions, and
                // applyBuildOptionCoreSkills' 'add' branch pushes in place — which would mutate
                // the stored world detail, accumulating a duplicate on every re-merge.
                mergedDefaults = {
                    ...defaultsCopy,
                    ...deepCopyObject<SystemDefaultsInterface>(world.defaults),
                };
            }

            console.debug("SwadeData | combineSettingRules | mergedDefaults | ", mergedDefaults);
            world?.settingRules?.forEach((rule: SettingRuleInterface) => {
                rule.modifications?.forEach((modification: SwadeModificationInterface) => {
                    this.applyBuildOption(modification.configuration, mergedDefaults);
                })
            })

            console.debug("SwadeData | combineSettingRules | mergedDefaults | ", mergedDefaults);
            world?.modifications?.forEach(modification => {
                this.applyBuildOption(modification.configuration, mergedDefaults);
            })

            // Normalize a wealth amount to a die code, or to null. Only two things are usable: a
            // die SIZE (6 or '6' -> 'd6'), and a code that is already well formed ('d6' -> 'd6',
            // trimmed). ANYTHING else becomes null, because there is no value it could mean and a
            // malformed code fails DieRank parsing downstream via character.purchasingPower.
            //
            // That covers a wide net on purpose: null, undefined, '', whitespace, NaN, 'abc', and
            // sizes that cannot be a die (0, -4, 2.5) all land on null. This previously used
            // isNaN(), which coerces before testing — Number(null) and Number('') are both 0, so
            // an unset amount answered "not NaN", took the numeric branch, and produced the
            // literal 'dnull' or 'd'.
            //
            // The size in an already-formed code is deliberately NOT checked against
            // SwadeWealthDieCode's d4-d12: a setting using a 'd20' wealth die keeps it rather than
            // silently losing it.
            if (mergedDefaults.startingFunds.type === 'wealth') {
                const text = `${mergedDefaults.startingFunds.amount ?? ''}`.trim();
                const dieSize = Number(text);

                mergedDefaults.startingFunds.amount =
                    /^d\d+$/.test(text)
                        ? text as SwadeWealthDieCode
                        : (Number.isInteger(dieSize) && dieSize > 0
                            ? `d${dieSize}` as SwadeWealthDieCode
                            : null)
                ;
            }

            console.debug("SwadeData | combineSettingRules | mergedDefaults | ", mergedDefaults);
            this.buildOptions = mergedDefaults;

            return mergedDefaults;
        },
        applyBuildOption(modification: SwadeModConfigurationInterface, buildOptions: SystemDefaultsInterface) {
            if (!buildOptions) return;
            console.info("SwadeData | applyBuildOption");
            console.debug("SwadeData | applyBuildOption | buildOptions | ", buildOptions);
            console.debug("SwadeData | applyBuildOption | modification | ", modification);
            if (modification?.path === 'buildPoints' && modification?.element === 'languageSelections') {
                this.applyBuildOptionLanguageSlots(modification, buildOptions);
            }
            else if (modification?.path === 'buildPoints') {
                this.applyBuildOptionPoints(modification, buildOptions);
            }
            else if (modification?.path === 'coreSkills') {
                this.applyBuildOptionCoreSkills(modification, buildOptions);
            }
            else if (modification?.path === 'superPowerPoints') {
                this.applyBuildOptionSuperPowerPoints(modification, buildOptions);
            }
            else if (modification?.path === 'edge' || modification?.path === 'character.edges') {
                this.applyBuildOptionEdge(modification, buildOptions);
            }
            else if (modification?.path === 'hindrance' || modification?.path === 'character.hindrances') {
                this.applyBuildOptionHindrance(modification, buildOptions);
            }
            else if (modification?.path === 'startingFunds') {
                this.applyBuildOptionPurchasingPower(modification, buildOptions);
            }
            else if (modification?.path === 'languageSelections') {
                this.applyBuildOptionLanguageSelections(modification, buildOptions);
            }
            else if (modification?.path === 'languageSkill') {
                this.applyBuildOptionLanguageSkill(modification, buildOptions);
            }
            else if (modification?.path === 'additionalAttribute') {
                this.applyBuildOptionAdditionalAttribute(modification, buildOptions);
            }
            else if (modification?.path === 'armorStackingLimit') {
                this.applyBuildOptionArmorStackingLimit(modification, buildOptions);
            }
            else if (modification?.path === 'character.equipment') {
                this.applyBuildOptionEquipment(modification, buildOptions);
            }
            else if (modification?.path === 'character.skills') {
                this.applyBuildOptionSkillGrant(modification, buildOptions);
            }
        },
        applyBuildOptionArmorStackingLimit(
            modification: SwadeModConfigurationInterface,
            buildOptions: SystemDefaultsInterface,
        ) {
            // No ceiling: 99 is an authoring affordance, not a domain rule.
            const limit = normalizeArmorStackingLimit(modification.value);
            // Ignore a value that is not a number at all rather than writing it through. A world
            // whose system default is 0 and whose modification is null must keep the 0 — writing
            // the null through would make calculateToughness fall back to 1, i.e. stock stacking,
            // the exact opposite of the world's intent. An out-of-range NUMBER is clamped instead,
            // because its intent is legible.
            if (limit === null) return;

            buildOptions.armorStackingLimit = limit;
        },
        applyBuildOptionAdditionalAttribute(
            modification: SwadeModConfigurationInterface,
            buildOptions: SystemDefaultsInterface,
        ) {
            if (!modification.element || !modification.valueType) return;

            if (!Array.isArray(buildOptions.attributes.additional)) {
                buildOptions.attributes.additional = [];
            }

            // Collision: auto-suffix the frozen slug with a numeric index (honor → honor2 → honor3…).
            //
            // The suffix is recomputed from scratch on every merge rather than cached on the
            // modification record. This means: if an upstream collision is later resolved
            // (one of the duplicates is renamed or removed), characters that were added under
            // the suffixed slug (e.g. `honor2`) will orphan and surface as "removed" in
            // AdditionalAttributes.vue — the player must remove and re-add to pick up the
            // unsuffixed slug. This is an accepted trade-off; see the design spec
            // (docs/superpowers/specs/2026-04-17-additional-attributes-design.md).
            const baseSlug = modification.element as string;
            let slug = baseSlug;
            let suffix = 2;
            while (buildOptions.attributes.additional.some((a: any) => a.slug === slug)) {
                slug = `${baseSlug}${suffix}`;
                suffix++;
            }

            buildOptions.attributes.additional.push({
                label: modification.label ?? slug,
                slug,
                type: modification.valueType as 'die' | 'text' | 'numeric',
                defaultValue: modification.value ?? null,
            });
        },
        applyBuildOptionPoints(modification: SwadeModConfigurationInterface, buildOptions: SystemDefaultsInterface) {
            // `value` is `string | number | null` — the field also carries die codes, currency
            // symbols and uuids. RE-TYPED here, never converted: the arithmetic below is raw JS and
            // always has been, so a cleared numeric input ('') still CONCATENATES rather than adds.
            // That is the exact hazard the extraPerkPoints branch documents and deliberately guards
            // with Number(); coercing here would change results, which this task must not do.
            const value = modification.value as number;

            if (modification.element === 'attributes') {
                if (modification.operator === 'add') {
                    buildOptions.attributes.creationPoints = buildOptions.attributes.creationPoints + value;
                }
                else if (modification.operator === 'subtract') {
                    buildOptions.attributes.creationPoints = buildOptions.attributes.creationPoints - value;
                }
                else if (modification.operator === 'alter') {
                    buildOptions.attributes.creationPoints = value;
                }
            }
            else if (modification.element === 'traits') {
                if (modification.operator === 'add') {
                    buildOptions.skills.creationPoints = buildOptions.skills.creationPoints + value;
                }
                else if (modification.operator === 'subtract') {
                    buildOptions.skills.creationPoints = buildOptions.skills.creationPoints - value;
                }
                else if (modification.operator === 'alter') {
                    buildOptions.skills.creationPoints = value;
                }
            }
            else if (modification.element === 'perkPoints') {
                if (modification.operator === 'add') {
                    buildOptions.hindrances.maxPoints = buildOptions.hindrances.maxPoints + value;
                }
                else if (modification.operator === 'subtract') {
                    buildOptions.hindrances.maxPoints = buildOptions.hindrances.maxPoints - value;
                }
                else if (modification.operator === 'alter') {
                    buildOptions.hindrances.maxPoints = value;
                }
            }
            else if (modification.element === 'extraPerkPoints') {
                // Grants are stored separately from the ceiling: `.total` adds this and must
                // never add maxPoints. `?? 0` because every world blob saved before this field
                // existed has no key, and undefined + value is NaN.
                //
                // Number() is load-bearing too, and for a different reason. A cleared numeric
                // input stores '', so `current + modification.value` would CONCATENATE: 0 + ''
                // gives the string "0", and `.total` reads this field raw (unlike the character
                // path, which parseInts inside characterBuildPointModifier). That turns a total
                // of 4 into 400 on the character sheet, with no error anywhere.
                const current = buildOptions.hindrances.extraPoints ?? 0;
                const delta = Number(modification.value) || 0;

                if (modification.operator === 'add') {
                    buildOptions.hindrances.extraPoints = current + delta;
                }
                else if (modification.operator === 'subtract') {
                    buildOptions.hindrances.extraPoints = current - delta;
                }
                else if (modification.operator === 'alter') {
                    buildOptions.hindrances.extraPoints = delta;
                }
            }
            else if (modification.element === 'powerPoints') {
                if (!buildOptions.characterPoints) {
                    buildOptions.characterPoints = {
                        creationPoints: 0,
                    }
                }

                if (modification.operator === 'add') {
                    buildOptions.characterPoints.creationPoints = buildOptions.characterPoints.creationPoints + value;
                }
                else if (modification.operator === 'subtract') {
                    buildOptions.characterPoints.creationPoints = buildOptions.characterPoints.creationPoints - value;
                }
                else if (modification.operator === 'alter') {
                    buildOptions.characterPoints.creationPoints = value;
                }
            }
        },
        applyBuildOptionCoreSkills(modification: SwadeModConfigurationInterface, buildOptions: SystemDefaultsInterface) {
            console.info("SwadeData | applyBuildOptionCoreSkills");
            // A coreSkills modification always authors a chip list — the `{ uuid, label }` rows
            // elementEntryUuid reads. `.map` below has always thrown a TypeError on any other shape
            // of `element`, so this asserts the contract the code already relies on; it adds no guard.
            const skillList = (modification.element as { uuid: string, label?: string }[]).map((item) => {
                return item.uuid;
            });

            console.debug("SwadeData | applyBuildOptionCoreSkills | skillList | ", skillList);
            if (modification.operator === 'add') {
                buildOptions.skills.core.push(...skillList);
            }
            else if (modification.operator === 'subtract') {
                buildOptions.skills.core = buildOptions.skills.core.filter((uuid: string) => {
                    return !skillList.includes(uuid);
                })
            }
            else if (modification.operator === 'alter') {
                buildOptions.skills.core = skillList;
            }
            console.debug("SwadeData | applyBuildOptionCoreSkills | buildOptions | ", buildOptions);
        },
        applyBuildOptionSuperPowerPoints(modification: SwadeModConfigurationInterface, buildOptions: SystemDefaultsInterface) {
            if (!buildOptions.characterPoints) {
                buildOptions.characterPoints = {
                    label: modification.label,
                    creationPoints: 0,
                }
            }
        },
        applyBuildOptionEdge(modification: SwadeModConfigurationInterface, buildOptions: SystemDefaultsInterface) {
            if (!buildOptions.edges || !Array.isArray(buildOptions.edges?.core)) {
                buildOptions.edges = {
                    core: [],
                }
            }

            // Read through elementUuids, like the equipment and skill grants below: the edge atom and
            // the import write a `{ uuid, label }` chip list, and stored whole it reached
            // applySettingRulesToCharacter as the uuid — `/elements/[object Object]/one`, a 404, and
            // no edge. A blank element (the dynamic edge pick) folds nothing.
            for (const uuid of elementUuids(modification.element)) {
                if (!buildOptions.edges.core.includes(uuid)) {
                    buildOptions.edges.core.push(uuid);
                }
            }
        },
        applyBuildOptionHindrance(modification: SwadeModConfigurationInterface, buildOptions: SystemDefaultsInterface) {
            if (!buildOptions.hindrances) {
                buildOptions.hindrances = {
                    maxPoints: 0,
                }
            }

            if (!Array.isArray(buildOptions.hindrances.core)) {
                buildOptions.hindrances.core = []
            }

            const alreadyPresent = buildOptions.hindrances.core.some((entry: any) =>
                (typeof entry === 'string' ? entry : entry?.uuid) === modification.element
            );
            if (!alreadyPresent) {
                // A hindrance build option names one uuid and one numeric value, and both have
                // always been stored raw. Re-typed, not converted.
                buildOptions.hindrances.core.push({
                    uuid: modification.element as string,
                    value: (modification.value ?? 1) as number,
                })
            }
        },
        // A world's (or setting rule's) `character.equipment` grant — Unarmed by default, say. Only
        // `add` folds: the merged rules are rebuilt from scratch on every commit and reversed through
        // the grants ledger (reconcileGrants), never walked backwards, which is the stance
        // applyBuildOptionEdge takes by ignoring the operator. Each named item folds once; the first
        // mention's quantity wins, as the first mention of a core edge does.
        applyBuildOptionEquipment(modification: SwadeModConfigurationInterface, buildOptions: SystemDefaultsInterface) {
            if (modification.operator !== 'add') return;
            if (!buildOptions.equipment || !Array.isArray(buildOptions.equipment.core)) {
                buildOptions.equipment = { core: [] };
            }

            // The quantity as applyEquipmentModification reads it: a positive number, else 1.
            const rawValue = Number(modification.value ?? 1);
            const quantity = Number.isFinite(rawValue) && rawValue > 0 ? rawValue : 1;

            for (const uuid of elementUuids(modification.element)) {
                if (buildOptions.equipment.core.some((entry) => entry.uuid === uuid)) continue;
                buildOptions.equipment.core.push({ uuid, quantity });
            }
        },
        // A world's (or setting rule's) `character.skills` grant. Distinct from coreSkills, which is
        // the d4 core list: this is the ancestry-style grant ("Fighting d6"), so value and valueType
        // travel untouched and the skill applier owns the die semantics. A blank element folds
        // nothing — a world has no add-time prompt to resolve a pick.
        applyBuildOptionSkillGrant(modification: SwadeModConfigurationInterface, buildOptions: SystemDefaultsInterface) {
            if (modification.operator !== 'add') return;
            if (!Array.isArray(buildOptions.skills.granted)) {
                buildOptions.skills.granted = [];
            }

            const rawValue = Number(modification.value ?? 1);
            const value = Number.isFinite(rawValue) ? rawValue : 1;

            for (const uuid of elementUuids(modification.element)) {
                if (buildOptions.skills.granted.some((entry) => entry.uuid === uuid)) continue;
                buildOptions.skills.granted.push({ uuid, value, valueType: modification.valueType ?? null });
            }
        },
        applyBuildOptionPurchasingPower(modification: SwadeModConfigurationInterface, buildOptions: SystemDefaultsInterface) {
            // Backfilled unconditionally, not only when absent: combineSettingRules virtually
            // always hands this an EXISTING blob (system defaults, or a world's saved defaults
            // replacing them wholesale), and a blob saved before a field existed simply has no
            // key for it. The known-field guard below reads the blob's own keys, so without the
            // backfill a modification touching such a field — abbreviation, and on older blobs
            // currencySymbol — was silently dropped. Keys the blob does carry win the spread.
            // The Partial cast is the honest type: the interface claims every key always
            // exists, which is exactly the assumption that dropped the writes.
            buildOptions.startingFunds = {
                currencySymbol: null,
                abbreviation: null,
                amount: null,
                type: 'funds',
                ...((buildOptions.startingFunds ?? {}) as Partial<SystemDefaultsInterface['startingFunds']>),
            };

            // The key test only ever matched a plain name: `Object.keys()` returns strings, so an
            // array or partial list never matched and still does not. The write then uses that same
            // name, and the blob's fields are number-or-string-or-null, so the value goes in raw.
            const fundsKey = elementName(modification?.element);
            if (fundsKey !== null && Object.keys(buildOptions.startingFunds).includes(fundsKey)) {
                (buildOptions.startingFunds as Record<string, unknown>)[fundsKey] =
                    modification?.value ?? null;
            }
        },
        applyBuildOptionLanguageSelections(modification: SwadeModConfigurationInterface, buildOptions: SystemDefaultsInterface) {
            if (!Array.isArray(modification.element)) return;

            if (!buildOptions.languages) {
                buildOptions.languages = {
                    attribute: null,
                    multiplier: 0,
                    core: [],
                }
            }

            // A languageSelections modification authors the slot rows themselves ({ label, slotsUsed },
            // the shape buildOptions.languages.core and character.additionalData.knownLanguages both
            // use). The spread has always copied them across untouched; re-typed, not converted.
            buildOptions.languages.core.push(...(modification.element as { label: string, slotsUsed: number }[]))
        },
        applyBuildOptionLanguageSkill(modification: SwadeModConfigurationInterface, buildOptions: SystemDefaultsInterface) {
            // An explicit "no language skill" declaration CLEARS rather than no-ops, because it has
            // to beat a skill an earlier write already set. combineSettingRules applies setting-rule
            // modifications before the world's own (see the two forEach loops above), so a world mod
            // authored to switch a rule-supplied world to the binary variant is the LATER write —
            // and while this returned early on a null element it lost to the very rule it exists to
            // override, leaving the world silently on skill ranks.
            //
            // Gated on the sentinel, not on a bare null element: a languageSkill modification the
            // author has added but not yet pointed at a skill is also null, and that half-configured
            // state must stay inert instead of disabling an inherited skill.
            //
            // Only clears an existing block, never creates one. The declaration means "no language
            // skill", not "this world has languages" — materialising a block here would flip
            // isBinary on for a world that never asked for languages, rendering an empty picker.
            if (!modification.element) {
                if (modification.label === NO_LANGUAGE_SKILL && buildOptions.languages) {
                    buildOptions.languages.skill = null;
                }

                return;
            }

            if (!buildOptions.languages) {
                buildOptions.languages = {
                    skill: null,
                    attribute: null,
                    multiplier: 0,
                    core: [],
                }
            }

            // A languageSkill modification names one skill uuid; the assignment has always stored
            // whatever `element` held, so it is re-typed rather than converted.
            buildOptions.languages.skill = modification.element as string;
        },
        applyBuildOptionLanguageSlots(modification: SwadeModConfigurationInterface, buildOptions: SystemDefaultsInterface) {
            if (modification.operator === 'subtract') {
                buildOptions.languages = null;
                return;
            }
            else if (!buildOptions.languages) {
                buildOptions.languages = {
                    attribute: null,
                    multiplier: 0,
                    core: [],
                }
            }

            buildOptions.languages.attribute = modification.valueType ?? 'smarts';
            // Stored raw, as always: re-typed, not converted.
            buildOptions.languages.multiplier = (modification.value ?? 0.5) as number;
        },
        applyCharacterBuildOption(character: CharacterSheetInterface, configuration: SwadeModConfigurationInterface) {
            if (!configuration) return;
            console.info("SwadeData | applyCharacterBuildOption");
            console.debug("SwadeData | applyCharacterBuildOption | configuration | ", configuration);

            if (configuration?.path === 'startingFunds') {
                this.applyCharacterPurchasingPowerOption(character, configuration)
            }
            else if (configuration?.path === 'linkedAttribute') {
                this.applyCharacterSkillLinkedAttribute(character, configuration);
            }
            else if (configuration?.path === 'buildPoints' && configuration.element === 'languageSelections') {
                this.applyBuildOptionLanguageSlots(configuration, this.buildOptions);
            }
            else if (
                configuration?.path === 'buildPoints' &&
                (configuration.element === 'perkPoints'
                    || configuration.element === 'extraPerkPoints'
                    || configuration.element === 'traits'
                    || configuration.element === 'attributes'
                    || configuration.element === 'powerPoints')
            ) {
                // Single source of truth: these creation-point pools are DERIVED fresh each
                // recalc from character sources — perkPoints via perkPointModifier, and
                // traits/attributes/extraPerkPoints/powerPoints via characterBuildPointModifier
                // (both read framework, abilities, ancestry grants, edges, hindrances, applied
                // choices and carried entries). Mutating buildOptions here instead would be lost
                // when buildOptions is rebuilt (resetBuildValidation on load / world re-retrieval),
                // dropping framework and choiceSet build-point grants — AND would double-count
                // against the modifier. powerPoints took this memo-only path until 2026-09-21 and
                // vanished on every reload. Skip and let the modifier carry the value.
                return;
            }
            else if (configuration?.path === 'buildPoints') {
                this.applyBuildOptionPoints(configuration, this.buildOptions);
            }
        },
        rollbackCharacterBuildOption(character: CharacterSheetInterface, configuration: SwadeModConfigurationInterface) {
            if (configuration.path === 'linkedAttribute') {
                return Promise.resolve(this.rollbackCharacterSkillLinkedAttribute(character, configuration));
            }
            // Operate on a shallow copy below so we never mutate the caller's modification
            // object. Callers (e.g., applyFramework's rollback-then-reapply loop) iterate the
            // same modification objects again on re-apply; mutating .operator here would
            // permanently flip the source's polarity and cause add-instead-of-subtract on
            // the next apply.
            else if (configuration?.path === 'buildPoints' && configuration.element === 'languageSelections') {
                const flipped = { ...configuration, operator: 'subtract' };
                console.debug("CharacterData | rollbackCharacterBuildOption", flipped, this.buildOptions)
                return Promise.resolve(this.applyBuildOptionLanguageSlots(flipped, this.buildOptions));
            }
            else if (configuration?.path === 'startingFunds' && configuration.operator === 'alter') {
                // Only roll back if our 'alter' is still the current value — i.e., it hasn't been
                // overridden by another modification or by the user. Restore to the world default.
                // String() only spells out the ToString parseFloat already performs.
                if (parseFloat(character.purchasingPower) === parseFloat(String(configuration.value))) {
                    character.purchasingPower = this.buildOptions.startingFunds?.amount ?? 0;
                }
                return Promise.resolve();
            }

            const flipped: RollbackModConfiguration = { ...configuration };
            if (configuration.operator === 'add') {
                flipped.operator = "subtract";
            }
            else if (configuration.operator === 'subtract') {
                flipped.operator = "add";
            }
            else if (configuration.operator === 'multiply') {
                flipped.operator = "multiply-subtract";
            }

            // The only consumer of 'multiply-subtract' is applyCharacterPurchasingPowerOption, which
            // declares the wider operator; everything else applyCharacterBuildOption dispatches to
            // sees one of the persisted operators. Narrowed here rather than widening the shared
            // interface, which describes what the API stores.
            return Promise.resolve(this.applyCharacterBuildOption(character, flipped as SwadeModConfigurationInterface));
        },
        applyCharacterPurchasingPowerOption(character: CharacterSheetInterface, configuration: RollbackModConfiguration) {
            if (isNaN(character.purchasingPower)) return;

            // isNaN() reads purchasing power with Number(), so a numeric string such as '500' gets past
            // it; read it the same way here, or each `+` below concatenates ('500' + 25 was '50025').
            const funds = Number(character.purchasingPower);

            // Both rewrites below are exact restatements of what JS already did:
            //   * parseFloat(x) performs ToString on a non-string argument, so String(x) is a no-op;
            //   * `x < 1` with a number on the right performs ToNumeric on the left, which for a
            //     string is StringToNumber — precisely Number(x). '' → 0, 'abc' → NaN, null → 0,
            //     all of which compare exactly as before — though none of them reaches a comparison
            //     now: each parses to NaN, which the guard below turns away.
            const value = parseFloat(String(configuration.value));
            const numericValue = Number(configuration.value);

            // A modification whose value was left blank (the editor's cleared number input sends '') or
            // is not a number moves no money and alters nothing; it used to write NaN (owner's ruling
            // 2026-10-09).
            if (Number.isNaN(value)) return;

            if (configuration.operator === 'add') {
                character.purchasingPower = funds + value;
            }
            else if (configuration.operator === 'subtract') {
                character.purchasingPower = funds - value;
            }
            else if (configuration.operator === 'alter') {
                character.purchasingPower = value;
            }
            else if (configuration.operator === 'multiply' && (numericValue < 1)) {
                console.debug("SwadeData | applyCharacterPurchasingPowerOption | rollback multiply",this.buildOptions.startingFunds.amount * (1 / value));
                character.purchasingPower =
                    funds -
                    (this.buildOptions.startingFunds.amount * value)
                ;
            }
            else if (configuration.operator === 'multiply') {
                console.debug("SwadeData | applyCharacterPurchasingPowerOption | multiply",
                    this.buildOptions.startingFunds.amount * value);
                character.purchasingPower =
                    funds +
                    (this.buildOptions.startingFunds.amount * value);
            }
            else if (configuration.operator === 'multiply-subtract' && (numericValue < 1)) {
                console.debug("SwadeData | applyCharacterPurchasingPowerOption | rollback multiply",this.buildOptions.startingFunds.amount * (1 / value));
                character.purchasingPower =
                    funds +
                    (this.buildOptions.startingFunds.amount * value)
                ;
            }
            else if (configuration.operator === 'multiply-subtract') {
                console.debug("SwadeData | applyCharacterPurchasingPowerOption | multiply",
                    this.buildOptions.startingFunds.amount * value);
                character.purchasingPower =
                    funds -
                    (this.buildOptions.startingFunds.amount * value);
            }
        },
        applyCharacterSkillLinkedAttribute(character: CharacterSheetInterface, configuration: SwadeModConfigurationInterface) {
            if (!Array.isArray(character.protoData?.skills))  {
                return;
            }

            // Get the proto skill data so we can get the value that was modified
            const protoSkill = character.protoData?.skills.find((item: { uuid: string, linkedAttribute: string | number | null }) => {
                return item.uuid === configuration.element;
            })

            if (!protoSkill) return;

            protoSkill.linkedAttribute = configuration.value;
        },
        rollbackCharacterSkillLinkedAttribute(character: CharacterSheetInterface, configuration: SwadeModConfigurationInterface) {
            if (!Array.isArray(character.protoData?.skills) || !Array.isArray(character?.skills))  {
                return;
            }

            // Get the proto skill data so we can get the value that was modified
            const protoSkill = character.protoData?.skills.find((item: { uuid: string, linkedAttribute: string | number | null }) => {
                return item.uuid === configuration.element;
            })

            if (!protoSkill) return;

            // Get the ranked skill so we can get the original linked attribute
            const rankedSkill = character.skills.find((item: SwadeSkillRankInterface) => {
                return item.skill?.uuid === configuration.element;
            })

            if (!rankedSkill) return;

            protoSkill.linkedAttribute = rankedSkill.skill.linkedAttribute;
        },

        // Awaitable by contract: the configurator reads back the skill rank uuids this creates in
        // order to record grants[].rankUuid, and those only exist once the adds resolve. The three
        // loops were previously fire-and-forget, so updateBuildValidation ran against a character
        // that had not received any of them yet.
        async applySettingRulesToCharacter(character: CharacterSheetInterface, settingRules: SystemDefaultsInterface) {
            console.debug("CharacterData | applySettingRulesToCharacter | ", character, settingRules);

            const store = useAppDataStore();

            // World skill grants BEFORE the core list: a granted skill that is also core (or a system
            // default) is created at the granted die, and the core loop's addSkillToCharacter then
            // short-circuits on the existing rank. Guarded on the CHARACTER, like the edges below:
            // applyCharacterLocalSetting re-runs this in full on every commit, and the applier ADDS
            // steps to an existing rank, so a repeat would compound d6 → d8 → d10.
            for (const grant of settingRules?.skills?.granted ?? []) {
                const held = (character.skills ?? []).some(
                    (rank: SwadeSkillRankInterface) => rank.skill?.uuid === grant.uuid
                );
                if (held) continue;

                await this.applySkillModification(character, {
                    path: 'character.skills', operator: 'add', element: grant.uuid,
                    value: grant.value, valueType: grant.valueType, label: null, summary: null,
                } as SwadeModConfigurationInterface, 'add');
            }

            // Sequential, not Promise.all: a granted element can carry a choiceSet, and the
            // ChoiceApplicator supports only one open session — concurrent adds throw a plain
            // Error that withChoiceResolution does not swallow. Serializing costs one round trip
            // per grant and is the accepted trade.
            for (const elementId of settingRules?.skills.core ?? []) {
                const skill: any = await store.retrieveElement({ uuid: elementId });
                if (!skill) continue;
                const protoSkill = character.protoData?.skills.find((protoSkill: any) => {
                    return protoSkill.uuid === skill.uuid;
                });

                if (!protoSkill) {
                    this.addProtoSkillToCharacter(character, skill, 'd4', 'd4');
                }

                await this.addSkillToCharacter(character, skill);
            }

            // Set starting currency
            character.purchasingPower = settingRules.startingFunds?.amount ?? 0;
            console.debug("CharacterData | applySettingRulesToCharacter | ", character.purchasingPower);

            // add core edges from setting rules
            // addEdgeToCharacter only short-circuits on an existing copy when !allowMultiple, and
            // allowMultiple exists so a USER can take an edge twice — it must not let a RULE GRANT
            // re-materialize on every commit, since each repeat re-applies the edge's own
            // modifications. Checked against the CHARACTER, not against additionalData.grants,
            // which is empty for pre-existing characters and would therefore re-add everything.
            for (const elementId of settingRules?.edges.core ?? []) {
                const alreadyGranted = (character.edges ?? []).some(
                    (container: SwadeEdgeContainerInterface) => container.selection?.uuid === elementId
                );
                if (alreadyGranted) continue;

                const edge: SwadeEdgeInterface = await store.retrieveElement({ uuid: elementId });
                if (!edge) continue;
                await this.addEdgeToCharacter(character, edge, 0);
            }

            // add core hindrances from setting rules
            for (const entry of settingRules?.hindrances?.core ?? []) {
                const elementId = typeof entry === 'string' ? entry : entry.uuid;
                const explicitValue = typeof entry === 'string' ? null : entry.value;

                const hindrance: SwadeHindranceInterface = await store.retrieveElement({ uuid: elementId });
                if (!hindrance) continue;

                // addDrawbackToCharacter early-returns when the drawback already exists, so
                // the proto mirror below must be guarded the same way — an unguarded repeat
                // adds a proto row with no matching hindrance and recalculateTotalPerkPoints
                // double-subtracts, permanently costing the character perk points.
                const alreadyHeld = (character.hindrances ?? []).some(
                    (drawback: SwadeDrawbackInterface) => drawback.hindrance?.uuid === hindrance.uuid
                );

                await this.addDrawbackToCharacter(character, hindrance);

                if (alreadyHeld) continue;

                // Forced hindrances must not credit perk points. Mirror the entry
                // in protoData so recalculateTotalPerkPoints subtracts what it adds.
                // Prefer the value carried by the modification that queued this entry;
                // legacy string entries from world.defaults fall back to the hindrance's type.
                character.protoData.hindrances.push({
                    uuid: hindrance.uuid,
                    type: hindrance.type,
                    value: explicitValue ?? hindranceDegrees(hindrance.type),
                });
            }

            // World equipment grants. Guarded on the character for the same reason as the edges:
            // applyEquipmentModification's add branch RAISES the quantity of an item already held,
            // which a recommit must not do. No grantContext: a provenance row marks a framework's
            // gear strain-exempt, and a setting's gear is ordinary gear — it arrives at its type's
            // default status (a weapon as primary) and is reversed through the grants ledger.
            for (const entry of settingRules?.equipment?.core ?? []) {
                const held = (character.equipment ?? []).some(
                    (container: SwadeItemContainerInterface) => container.selection?.uuid === entry.uuid
                );
                if (held) continue;

                await this.applyEquipmentModification(character, {
                    path: 'character.equipment', operator: 'add', element: entry.uuid,
                    value: entry.quantity, valueType: null, label: null, summary: null,
                } as SwadeModConfigurationInterface, 'add');
            }

            this.updateBuildValidation(character, settingRules);
        },

        // Reverses what a previous setting materialized, then rewrites the record from the new
        // core sets. Runs AFTER the new buildOptions is computed and BEFORE
        // applySettingRulesToCharacter materializes the new grants.
        //
        // Removals only — it does not suppress redundant adds; applySettingRulesToCharacter's own
        // guards handle repeats. Provenance is read from additionalData.grants rather than
        // inferred, because every available heuristic (protoData.edges[].value === 0,
        // protoData.skills[].base) is shared with ancestry, framework and choiceSet grants and is
        // itself a live economic input.
        //
        // Accepted: an element the user also acquired independently is still removed when its
        // granting rule is removed. Re-adding it is a normal UI action; reference counting is out
        // of scope.
        // Removing a rule that carried a choice set does not reverse what the choice APPLIED.
        // editChoiceSets' survivor filter deliberately preserves records whose choiceSetUuid is
        // gone from the source, resolveWorldChoiceSets early-returns when no choiceSet mods remain,
        // and cleanupOrphanedChoices whitelists the local-setting container — so nothing else ever
        // clears them. Runs at the TOP of applyCharacterLocalSetting, before resetBuildValidation,
        // so a retired buildOption-tier choice routing through rollbackCharacterBuildOption still
        // reads the pre-reset buildOptions (notably its startingFunds/alter branch), and before the
        // purchasing-power snapshot, since that branch writes it.
        async retireResolvedChoices(
            character: CharacterSheetInterface,
            newModifications: SwadeModificationInterface[],
        ) {
            const containerUuid = character.additionalData?.localSetting?.uuid ?? null;
            if (!containerUuid) return;

            const choices = character.additionalData?.choices ?? [];
            if (choices.length === 0) return;

            // Do NOT restrict this to choiceSet mods: dynamic-resolver records live under the same
            // container uuid with choiceSetUuid = originalMod.uuid, and dropping them would delete
            // the user's edge or skill pick with no re-prompt — the unresolved gate in
            // resolveWorldChoiceSets counts only character.choiceSet mods.
            const liveResolvableUuids = new Set(
                (newModifications ?? [])
                    .filter((mod) => isResolvableModification(mod) && mod.uuid)
                    .map((mod) => mod.uuid)
            );

            const retired = new Set<AppliedConfiguration>();

            for (const record of choices) {
                const isOurs = record.uuid === containerUuid;
                // Anchors carry a null path by convention and hold table-roll results together.
                const isAnchor = (record.configuration?.path ?? null) === null;

                if (!isOurs || isAnchor || liveResolvableUuids.has(record.choiceSetUuid)) continue;

                // Match the updateProto / source that editChoiceSets applied it with for a
                // non-cyberware container.
                await this.rollbackModification(character, record.configuration, true, 'permanent', containerUuid);
                retired.add(record);
            }

            // Filter the LIVE array, never a snapshot: rollbackModification above can itself prune
            // choices (a retired character.edges record cascades through removeEdge ->
            // rollbackAppliedChoicesFor). Re-assigning a stale snapshot would resurrect those
            // records, and cleanupOrphanedChoices would then roll the same modification back twice.
            const current = character.additionalData?.choices ?? [];
            character.additionalData!.choices = current.filter((r: AppliedConfiguration) => !retired.has(r));
            await this.syncCarriedPayloads(character);
        },

        // applySettingRulesToCharacter overwrites purchasingPower unconditionally, so every commit
        // would otherwise reset the character's money. Restoring it is three different problems,
        // because startingFunds.amount is not always a quantity: combineSettingRules rewrites a
        // wealth amount into the die-code string 'd' + amount, and a numeric delta on 'd6' is NaN.
        //
        // The snapshot must be taken BEFORE resetBuildValidation nulls the memo — afterwards the
        // old baseline is unrecoverable.
        preserveStartingFunds(
            character: CharacterSheetInterface,
            snapshot: { purchasingPower: any, startingFunds: { type: string, amount: any } | null },
        ): { changed: boolean, reset: boolean } {
            const next = this.buildOptions?.startingFunds ?? null;
            if (!next) return { changed: false, reset: false };

            const previous = snapshot.startingFunds;

            // No baseline to compare against — buildOptions was null when the snapshot was taken.
            //
            // NOT the same thing as the unit changing, though this used to share that branch. A
            // missing baseline is an absent measurement, not evidence of a loss: reporting a reset
            // makes the panel announce something that did not happen, and taking the new baseline
            // outright OVERWRITES whatever the character had already spent. applySettingRulesToCharacter
            // has already clobbered the live value with that baseline by this point, so preserving
            // means actively putting the snapshot back.
            //
            // Every live caller of resetBuildValidation happens to rebuild before a commit can
            // run, so this is currently unreachable — but nothing enforces that, and the failure
            // mode is silent money loss.
            if (!previous) {
                // Zero is a real balance (spent to the last coin); undefined, null and a blank
                // string are not. newCharacter() seeds purchasingPower as null (d2a4aee) and a
                // character loaded from an older payload may omit the key entirely, so BOTH have
                // to read as "nothing to preserve" and take the baseline instead.
                const held = snapshot.purchasingPower;
                const hasBalance = held !== null && held !== undefined && `${held}`.trim() !== '';

                if (hasBalance) {
                    character.purchasingPower = held;

                    return { changed: false, reset: false };
                }

                character.purchasingPower = next.amount;

                return { changed: true, reset: false };
            }

            // The unit changed (funds <-> wealth <-> points): the snapshot has no meaning in the
            // new unit. Take the new baseline outright and report it, so the caller can state the
            // loss.
            if (previous.type !== next.type) {
                character.purchasingPower = next.amount;

                return { changed: true, reset: true };
            }

            if (next.type === 'wealth') {
                // Die codes. Carry the character's step offset from the old baseline rather than
                // overwriting, or a perk-boosted wealth die is silently undone. Mirrors
                // adjustStartingFundsPerk's DieRank handling.
                const held = new DieRank(snapshot.purchasingPower);
                const oldBase = new DieRank(previous.amount);
                const newBase = new DieRank(next.amount);
                const steps = (newBase.getSteps() - oldBase.getSteps());

                if (steps > 0) held.addSteps(steps);
                else if (steps < 0) held.subtractSteps(Math.abs(steps));

                character.purchasingPower = held.getRankAsString();

                return { changed: steps !== 0, reset: false };
            }

            // Numeric. Preserve what the character already spent by applying only the delta.
            const delta = (parseFloat(next.amount) || 0) - (parseFloat(previous.amount) || 0);
            character.purchasingPower = (parseFloat(snapshot.purchasingPower) || 0) + delta;

            return { changed: delta !== 0, reset: false };
        },

        async reconcileGrants(
            character: CharacterSheetInterface,
            settingRules: SystemDefaultsInterface,
            previousCoreSkills: string[] = [],
        ) {
            const grants = character.additionalData?.grants ?? [];

            // Two removal sources, not one.
            //
            // `grants` covers what a PREVIOUS commit of this setting materialized. It cannot cover
            // the skills the SYSTEM defaults put on the character, because those are deliberately
            // never recorded there (see the rewrite at the end of this method) — so on the first
            // commit against a character that was built with no world, the ledger is empty and a
            // setting using operator 'alter' on core skills drops them from buildOptions while
            // they stay on the sheet forever.
            //
            // The outgoing core list is the missing provenance: a skill that was core a moment ago
            // and is not core now was materialized by the rules, whoever authored them. It is read
            // from the live buildOptions by the caller, since resetBuildValidation drops that memo
            // before this runs.
            //
            // Deliberately NOT merged into the grants rewrite below: `grants` has to keep meaning
            // "what THIS setting added", or a later setting could delete a system default it never
            // granted.
            const recorded = [
                ...grants,
                ...previousCoreSkills
                    .filter((uuid: string) => !grants.some((grant: any) => grant.uuid === uuid))
                    // No rankUuid: these were never claimed by a grant record, so removal takes the
                    // fallback branch below, which is specialization-safe by design.
                    .map((uuid: string) => ({ type: 'skill' as const, uuid })),
            ];

            // Hindrance core entries are either a bare uuid string or { uuid, value }.
            const coreHindranceUuids = new Set(
                (settingRules?.hindrances?.core ?? []).map(
                    (entry: any) => (typeof entry === 'string' ? entry : entry?.uuid)
                )
            );
            // Granted skills sit beside the core list: both are skills the rules materialised, and
            // both are reversed by rankUuid when their rule goes.
            const coreSkillUuids = new Set([
                ...(settingRules?.skills?.core ?? []),
                ...(settingRules?.skills?.granted ?? []).map((grant) => grant.uuid),
            ]);
            const coreEdgeUuids = new Set(settingRules?.edges?.core ?? []);
            const coreEquipmentUuids = new Set((settingRules?.equipment?.core ?? []).map((entry) => entry.uuid));

            const stillGranted = (grant: { type: string, uuid: string }) => {
                if (grant.type === 'skill') return coreSkillUuids.has(grant.uuid);
                if (grant.type === 'edge') return coreEdgeUuids.has(grant.uuid);
                // An unrecognized grant.type must never be treated as "no longer granted" — that
                // would drive it into the hindrance removal branch below by elimination.
                if (grant.type === 'hindrance') return coreHindranceUuids.has(grant.uuid);
                if (grant.type === 'equipment') return coreEquipmentUuids.has(grant.uuid);
                return true;
            };

            for (const grant of recorded) {
                if (stillGranted(grant)) continue;

                if (grant.type === 'skill') {
                    const rank = (character.skills ?? []).find(
                        (item: SwadeSkillRankInterface) =>
                            (grant.rankUuid
                                ? item.uuid === grant.rankUuid
                                // Fallback (no rankUuid recorded): never match a specialization —
                                // this grant could not have created one, so it must not claim it.
                                : item.skill?.uuid === grant.uuid && !resolveSkillTrapping(item))
                    );
                    // Remove by rankUuid, the only specialization-safe branch: the no-rankUuid
                    // branch compares by object identity and would miss a retrieved element.
                    if (rank) await this.removeSkill(character, rank.skill, rank.uuid);
                }
                else if (grant.type === 'edge') {
                    const container = (character.edges ?? []).find(
                        (item: SwadeEdgeContainerInterface) => item.selection?.uuid === grant.uuid
                    );
                    // removeEdge is the one clean inverse — it rolls back the edge's own
                    // modifications, clears its applied choices, and removes the proto row.
                    if (container) await this.removeEdge(character, container);
                }
                else if (grant.type === 'hindrance') {
                    const drawback = (character.hindrances ?? []).find(
                        (item: SwadeDrawbackInterface) => item.hindrance?.uuid === grant.uuid
                    );
                    if (drawback) {
                        await this.removeDrawback(character, drawback);
                        // removeDrawback mutates character.hindrances only. Left alone, the
                        // protoData row keeps being subtracted by recalculateTotalPerkPoints and
                        // the character permanently loses those perk points.
                        await this.modifyProtoHindrances(
                            character,
                            { element: grant.uuid } as SwadeModConfigurationInterface,
                            'subtract',
                        );
                    }
                }
                else if (grant.type === 'equipment') {
                    const container = (character.equipment ?? []).find(
                        (item: SwadeItemContainerInterface) => item.selection?.uuid === grant.uuid
                    );
                    // removeItem is the clean inverse: it rolls back the item's own in-use
                    // modifications, strips its recorded choices and re-sorts the list.
                    if (container) await this.removeItem(character, container);
                }
            }

            // "Grants" must mean what THIS setting added, which is the premise the whole
            // provenance design rests on. The merged core sets also contain the SYSTEM's defaults,
            // and recording those would let a setting using operator 'alter' on core skills delete
            // skills it never granted.
            const store = useAppDataStore();
            const systemDefaults = store.system?.defaults;
            const systemSkillCore = new Set(systemDefaults?.skills?.core ?? []);
            const systemEdgeCore = new Set(systemDefaults?.edges?.core ?? []);
            const systemHindranceCore = new Set(
                (systemDefaults?.hindrances?.core ?? []).map(
                    (entry: any) => (typeof entry === 'string' ? entry : entry?.uuid)
                )
            );
            const systemEquipmentCore = new Set(
                (systemDefaults?.equipment?.core ?? []).map((entry) => entry.uuid)
            );

            // Rewritten from buildOptions synchronously. rankUuid is filled in by
            // applyCharacterLocalSetting once the adds have resolved and the ranks exist.
            // The removal loop above is NOT filtered by this scoping — a grant recorded by an
            // earlier commit still has to be removable even if it happens to coincide with a
            // system default.
            character.additionalData ??= {};
            character.additionalData.grants = [
                ...[...coreSkillUuids].filter((uuid: any) => !systemSkillCore.has(uuid))
                    .map((uuid: any) => ({ type: 'skill' as const, uuid })),
                ...[...coreEdgeUuids].filter((uuid: any) => !systemEdgeCore.has(uuid))
                    .map((uuid: any) => ({ type: 'edge' as const, uuid })),
                ...[...coreHindranceUuids].filter((uuid: any) => !systemHindranceCore.has(uuid))
                    .map((uuid: any) => ({ type: 'hindrance' as const, uuid })),
                ...[...coreEquipmentUuids].filter((uuid: string) => !systemEquipmentCore.has(uuid))
                    .map((uuid: string) => ({ type: 'equipment' as const, uuid })),
            ];
        },

        // Bind each skill grant to the rank it actually created — the only specialization-safe way
        // to remove it later. Bind ONLY to an untrapped rank — one this grant could actually have
        // created. addSkillToCharacter short-circuits when any rank with this uuid already exists,
        // so if the character already held a specialization ("Fighting (Swords)"), the grant
        // materialized nothing; claiming that rank would let a later reconcile delete the user's
        // specialization, which no re-add path recovers.
        backfillSkillGrantRankUuids(character: CharacterSheetInterface): void {
            for (const grant of character.additionalData?.grants ?? []) {
                if (grant.type !== 'skill') continue;
                const rank = (character.skills ?? []).find(
                    (item: SwadeSkillRankInterface) => item.skill?.uuid === grant.uuid && !resolveSkillTrapping(item)
                );
                if (rank?.uuid) grant.rankUuid = rank.uuid;
            }
        },

        // Committing a setting is a REBUILD, not a diff: applyBuildOptionEdge and
        // applyBuildOptionHindrance have no subtract branch, so buildOptions cannot be walked
        // backwards. Everything derived (max skill points, max hindrance points, starting funds,
        // build-point maxima) recomputes for free; only materialized grants and resolved choices
        // need reversing, and each has its own pass.
        async applyCharacterLocalSetting(
            character: CharacterSheetInterface,
            setting: CharacterLocalSettingInterface,
        ): Promise<{ fundsReset: boolean }> {
            const store = useAppDataStore();
            const name = setting.sourceName
                ?? useUxData().labels?.swade?.localSetting?.name
                ?? 'Custom Setting';
            const nextWorld = localSettingToWorld(setting, name);
            const nextModifications = nextWorld?.modifications ?? [];

            // 1. Reverse choices whose carrying rule is gone. BEFORE resetBuildValidation, so a
            //    retired buildOption-tier choice still reads the pre-reset buildOptions — notably
            //    rollbackCharacterBuildOption's startingFunds/alter branch, which writes
            //    purchasingPower and so must also precede the snapshot below.
            await this.retireResolvedChoices(character, nextModifications);

            // 2. Snapshot what only the OLD buildOptions knows, while it is still readable —
            //    resetBuildValidation (step 4) nulls the memo and both of these become
            //    unrecoverable. The core list is what lets step 5 remove a skill the outgoing
            //    rules granted; without it only a previous commit's own grants can be reversed.
            const previousCoreSkills: string[] = [...(this.buildOptions?.skills?.core ?? [])];
            const fundsSnapshot = {
                purchasingPower: character.purchasingPower,
                startingFunds: this.buildOptions?.startingFunds
                    ? { ...this.buildOptions.startingFunds }
                    : null,
            };

            // 3. Write the blob and install the synthetic world. character.world is never written.
            character.additionalData ??= {};
            character.additionalData.localSetting = setting;
            store.$patch({ world: nextWorld });

            // 4. MANDATORY. combineSettingRules short-circuits on a live memo, so without this the
            //    entire edit is a silent no-op.
            this.resetBuildValidation();
            const merged = this.combineSettingRules(store.system?.defaults as SystemDefaultsInterface, nextWorld);

            // 5-8. MANDATORY finally: resetBuildValidation (step 4) zeroed the allocation counters
            //    and the perk-point total, and nothing else in this chain restores them —
            //    commitCharacterRecalculation MUST still run even if a step below throws, or a
            //    half-committed character is left with zeroed counters, which is worse than a
            //    failed commit. These steps are sequential by necessity, not habit: each mutates
            //    shared character state the next one reads.
            let funds = { changed: false, reset: false };
            try {
                // 5. Remove what the previous rules granted, then materialize what the new ones do.
                await this.reconcileGrants(character, merged, previousCoreSkills);
                await this.applySettingRulesToCharacter(character, merged);

                // 6. applySettingRulesToCharacter clobbered purchasingPower; put the balance back.
                funds = this.preserveStartingFunds(character, fundsSnapshot);

                // 7. Now that the adds have resolved, the created skill ranks exist and can be
                //    linked to their grant records. See backfillSkillGrantRankUuids for why only
                //    an untrapped rank may be claimed.
                this.backfillSkillGrantRankUuids(character);

                await this.recalculateCharacterPoints(character, store.system?.defaults, nextWorld);
                await this.cleanupOrphanedChoices(character);
                await this.resolveWorldChoiceSets(character, nextWorld);
            } finally {
                // 8. MANDATORY, and the step most likely to be omitted. resetBuildValidation
                //    zeroed build.{attributes,traits,perkPoints}.allocated, fullyAllocated,
                //    overAllocated and perkPoints.total; nothing else in this chain restores them.
                //    Runs AFTER resolveWorldChoiceSets, which can still add edges, skills and
                //    build points. Called explicitly because the view's @updated wiring does not
                //    fire on a commit.
                await this.commitCharacterRecalculation(character);
            }

            return { fundsReset: funds.reset };
        },

        addProtoSkillToCharacter(character: CharacterSheetInterface, skill: SwadeSkillInterface, base: string | null, rank: string, rankUuid: string | null = null) {
            character.protoData.skills.push({
                uuid: skill.uuid,
                rankUuid: rankUuid,
                base: base,
                rank: rank,
                linkedAttribute: skill.linkedAttribute,
                max: this.computeSkillMax(base),
            })
        },

        computeSkillMax(base: string | null | undefined): string {
            const baseDie = base ?? 'd4';
            const baseSteps = this.dieRank(baseDie).getValue();
            const stepsAboveD4 = Math.max(0, (baseSteps - 4) / 2);
            return this.dieRank('d12').addSteps(stepsAboveD4).getDieCode() ?? 'd12';
        },

        setProtoSkillBase(
            skill: { base: string | null, max: string },
            newBase: string | null,
        ): void {
            skill.base = newBase;
            skill.max = this.computeSkillMax(newBase);
        },

        async addSkillToCharacter(character: CharacterSheetInterface, skill: SwadeSkillInterface, force: boolean = false, base: string | null = null, trapping: string | null = null) {
            if (!skill) return;
            if (!character) return;

            let filteredSkills = character.skills.filter((item: SwadeSkillRankInterface) => {
                return item.skill.uuid === skill.uuid;
            });
            if (!force && (filteredSkills?.length > 0)) {
                this.selections.skill = null;
                return;
            }

            const newSkillRank = {
                uuid: uuid(),
                rank: base ?? 'd4',
                rankValue: 4,
                modifier: null,
                trapping: trapping,
                skill:  { ...skill },
            }
            character.skills.push(newSkillRank);

            if ((!character.protoData.locked) && (!this.hasAdvances(character))) {
                console.debug("CharacterData | addSkillToCharacter | checking proto skills")
                if (!character.protoData) {
                    character.protoData = this.newProtoData();
                }

                let protoSkill = character.protoData.skills.find((item: any) => {
                    if (item.rankUuid) {
                        return item.rankUuid === newSkillRank.uuid;
                    }

                    return item.uuid === skill.uuid;
                }) ?? null;
                if (!protoSkill) {
                    console.debug("CharacterData | addSkillToCharacter | updating proto skills")
                    this.addProtoSkillToCharacter(character, skill, base, base ?? 'd4', newSkillRank.uuid);
                }
            }

            this.selections.skill = null;
            // Same placement as addItemToCharacter's. Its three UI callers (the skill steps and the
            // sheet's add control) had no sync, so a transfer aimed at a newly added skill stayed
            // unapplied until some other action synced. Safe from inside applyModification's graph
            // too — applySkillModification reaches here, and the guard turns that into a re-run.
            await this.syncCarriedPayloads(character);
            this.sortSkillList(character);
            return this.recalculateDerivedStats(character);
        },
        // Returns the container the item went into — a new one, or the stack it joined — or null when
        // nothing was added: no item or character, or the player cancelled the item's questions.
        async addItemToCharacter(character: CharacterSheetInterface, item: SwadeItemInterface, property?: string): Promise<SwadeItemContainerInterface | null> {
            if (!item) return null;
            if (!character) return null;

            if (!Array.isArray(character.equipment)) {
                character.equipment = [];
            }

            // The autocomplete/list path passes a summary without `modifications`. Fetch the
            // full element so apply-time modifications fire and container.selection carries
            // the modifications array for future status toggles.
            const store = useAppDataStore();
            const fullItem: SwadeItemInterface = Array.isArray(item.modifications)
                ? item
                : ((await store.retrieveElement({ uuid: item.uuid })) ?? item);

            let added: SwadeItemContainerInterface | null = null;
            if (fullItem.type === 'cyberware') {
                added = await this._addCyberwareToCharacter(character, fullItem);
            } else {
                // Only a container that may take another unit (spec 2026-10-13 §5): a copy of an item
                // that asks, or of one a pick has customised, gets a container of its own.
                const [stack] = stackableContainers(character, fullItem.uuid);
                if (stack) {
                    await this.setContainerQuantity(character, stack, (stack.quantity ?? 1) + 1);
                    added = stack;
                } else {
                    const container = this.newItemContainer(fullItem);
                    character.equipment.push(container);

                    // Outside the isItemInUse guard below: charges belong to the item, not to the
                    // fact that it is currently equipped. An item added in a stored/uninstalled
                    // status must still arrive loaded, and it will never be seeded again.
                    this.seedContainerCharges(character, container, fullItem.modifications ?? []);

                    // A container that is not in use asks nothing, and the add stands.
                    const answered = !this.isItemInUse(container) || await this.withChoiceResolution(
                        character,
                        { uuid: container.uuid ?? '', name: fullItem.name ?? '' },
                        fullItem.modifications ?? [],
                        async (resolved: SwadeModConfigurationInterface[]) => {
                            // Apply the chosen choiceSet option(s). Without this the item's
                            // direct mods apply (via withChoiceResolution) but the resolved
                            // choice is dropped — so a chosen character.equipment grant (incl.
                            // multi-uuid element arrays) never reaches the sheet. Mirrors the
                            // finishFn used by applyEquipmentModification / applyAbilityModification.
                            for (const config of resolved) {
                                await this.applyModification(character, config, true, 'permanent', container.uuid ?? '');
                            }
                            // true marks a finished add: withChoiceResolution returns undefined only on
                            // cancel (spec 2026-10-13 §5).
                            return true;
                        },
                    ) === true;

                    if (answered) {
                        added = container;
                    } else {
                        // Cancelled: the item was never added (spec 2026-10-13 §5). withChoiceResolution
                        // returns before it records or applies anything, and seeding wrote only this
                        // container's charges, so taking the container back out undoes the whole add.
                        const index = character.equipment.findIndex((c: SwadeItemContainerInterface) => c.uuid === container.uuid);
                        if (index >= 0) character.equipment.splice(index, 1);
                    }
                }
            }

            if (property) {
                this.selections[property ?? ""] = null;
            }

            await this.syncCarriedPayloads(character);
            this.sortEquipmentList(character);
            await this.recalculateDerivedStats(character);
            return added;
        },

        // Returns the container the implant went into — a new one, or the stack with identical
        // answers that it joined — or null when the player cancelled its questions.
        async _addCyberwareToCharacter(character: CharacterSheetInterface, fullItem: SwadeItemInterface): Promise<SwadeItemContainerInterface | null> {
            // Resolve choices once with a placeholder uuid; rewrite later based on stack-or-new decision.
            let applied: AppliedConfiguration[] = [];
            let resolved: SwadeModConfigurationInterface[] = [];
            try {
                ({ applied, resolved } = await this.resolveChoiceSets(
                    character,
                    { uuid: '', name: fullItem.name ?? '' },
                    fullItem.modifications ?? [],
                ));
            } catch (err) {
                if (err instanceof ChoiceCancelledError) return null;
                throw err;
            }

            // Never a container another source has picked (spec 2026-10-13 §5): that implant is customised.
            const candidates = stackableContainers(character, fullItem.uuid);

            const matchTarget = candidates.find((candidate: SwadeItemContainerInterface) => {
                const candidateChoices = (character.additionalData?.choices ?? [])
                    .filter((c: AppliedConfiguration) => c.uuid === candidate.uuid);
                if (applied.length !== candidateChoices.length) return false;

                // Per modification, the two record SETS must be equal — order-independent, and a
                // pick that targets two containers is two records with one modification uuid.
                const modUuids = new Set([...applied, ...candidateChoices].map(resolvedModificationUuid));
                for (const modUuid of modUuids) {
                    const mine = applied.filter((r) => resolvedModificationUuid(r) === modUuid).map((r) => r.configuration);
                    const theirs = candidateChoices.filter((r: AppliedConfiguration) => resolvedModificationUuid(r) === modUuid).map((r: AppliedConfiguration) => r.configuration);
                    if (!sameConfigurationSet(mine, theirs)) return false;
                }
                return true;
            });

            if (matchTarget) {
                // The one quantity writer (spec 2026-10-13 §5): it ticks an installed implant once more.
                await this.setContainerQuantity(character, matchTarget, (matchTarget.quantity ?? 1) + 1);
                return matchTarget;
            }

            // No match — create a new container, claim the resolved choices under its uuid.
            const container = this.newItemContainer(fullItem);

            for (const a of applied) {
                a.uuid = container.uuid ?? '';
            }
            if (applied.length > 0) {
                character.additionalData ??= {};
                character.additionalData.choices ??= [];
                character.additionalData.choices.push(...applied);

                const labels = applied
                    .map(a => a.configuration?.label ?? (typeof a.configuration?.element === 'string' ? a.configuration.element : ''))
                    .filter((label): label is string => typeof label === 'string' && label.length > 0);
                if (labels.length > 0) {
                    container.name = `${fullItem.name} (${labels.join(', ')})`;
                }
            }

            character.equipment!.push(container);

            // Only the no-match branch reaches here. The stack-onto-existing branch returns above
            // without a new container, so a second copy of the same cyberware raises quantity and
            // does NOT re-seed the charges the player has already spent.
            this.seedContainerCharges(character, container, fullItem.modifications ?? []);

            // If the container starts in-use (won't happen with the uninstalled default, but guard anyway):
            // apply the resolved records, then the direct modifications that have no record. Cyberware
            // adds default to uninstalled, so this is mostly defensive.
            if (this.isItemInUse(container)) {
                for (const config of resolved) await this.applyModification(character, config, true, 'temporary', container.uuid ?? '');
                // The records were claimed under this container's uuid above, so a container
                // modification that resolved to one applies once, as that record: route the direct
                // pass through the shared filter, which also drops choice-set modifications.
                for (const mod of directModificationsWithoutOverrides(character, container.uuid, fullItem.modifications)) {
                    await this.applyModification(character, mod.configuration, true, 'temporary', container.uuid ?? '');
                }
            }
            return container;
        },
        async addEdgeToCharacter(character: CharacterSheetInterface, item: SwadeEdgeInterface, value: number = 2) {
            if (!item) return;

            const store = useAppDataStore();
            if (!character) return;

            if (!item.allowMultiple) {
                const existingEdge = character.edges?.find((container: SwadeEdgeContainerInterface) => {
                    return container.selection.uuid === item.uuid;
                });
                if (existingEdge) {
                    this.selections.edge = null;
                    return;
                }
            }

            return store.retrieveElement(item)
                .then(async (edge: SwadeEdgeInterface) => {
                    if (!character) return;

                    const container: SwadeEdgeContainerInterface = {
                        uuid: uuid(),
                        selection: edge,
                        configuration: null,
                    };

                    // Pushed BEFORE withChoiceResolution, matching how items and cyberware are
                    // added. withChoiceResolution applies the source's direct modifications before
                    // it calls finishFn, so a self-targeting container.edges modification (absent
                    // `element` → resolveContainerTargets mode 'self') can only find this container
                    // if it is already on the character by then.
                    character.edges.push(container);

                    // Set at the point the push used to happen, so the cleanup below is an exact
                    // stand-in for "finishFn never got as far as inserting". A plain
                    // `result === undefined` check cannot be used: finishFn returns nothing, so
                    // withChoiceResolution yields undefined on SUCCESS too, and the container
                    // would be torn out of every successful add.
                    let inserted = false;
                    try {
                        await this.withChoiceResolution(
                            character,
                            { uuid: container.uuid, name: edge.name },
                            edge.modifications ?? [],
                            async (resolved: SwadeModConfigurationInterface[]) => {
                                for (const config of resolved) await this.applyModification(character, config, true, 'permanent', container.uuid ?? '');
                                inserted = true;

                                if (!character.protoData) {
                                    character.protoData = this.newProtoData();
                                }

                                if ((!character.protoData.locked) && !this.hasAdvances(character)) {
                                    character.protoData.edges.push({
                                        uuid: edge.uuid,
                                        containerUuid: container.uuid,
                                        value: value,
                                    })
                                }

                                this.selections.edge = null;
                                this.sortEdgeList(character);
                                this.recalculateDerivedStats(character);
                            },
                        );
                    } finally {
                        // A cancelled choice must still mean "the edge was never added", as it did
                        // when the push lived inside finishFn. `finally` rather than a post-call
                        // check so a throw from the resolution or the modification loop unwinds the
                        // speculative push too, instead of leaving a half-configured container.
                        if (!inserted) {
                            character.edges = character.edges.filter(
                                (element: SwadeEdgeContainerInterface) => element.uuid !== container.uuid
                            );
                        }
                    }
                })
        },
        async addDrawbackToCharacter(character: CharacterSheetInterface, item: SwadeHindranceInterface, modifiers: Record<string, string> = {}) {
            if (!item) return;

            const store = useAppDataStore();
            if (!character) return;

            let existingDrawback = character.hindrances?.find((value: SwadeDrawbackInterface) => {
                return value.hindrance.uuid === item.uuid;
            });
            if (existingDrawback)
            {
                this.selections.hindrance = null;
                return existingDrawback as SwadeDrawbackInterface;
            }

            return store.retrieveElement(item)
                .then(async (element: SwadeHindranceInterface) => {
                    const drawback: SwadeDrawbackInterface = {
                        uuid: uuid(),
                        type: element.type === 'choice' ? (modifiers.type as 'major' | 'minor' | null) ?? 'minor' : element.type,
                        hindrance: element,
                    }

                    if (!character) return drawback;

                    // Pushed BEFORE withChoiceResolution, for the same reason as the edge path
                    // above: the direct-modification loop runs before finishFn, so a self-targeting
                    // container.hindrances modification only resolves a target if the drawback is
                    // already on the character.
                    character.hindrances.push(drawback);

                    let inserted = false;
                    try {
                        await this.withChoiceResolution(
                            character,
                            { uuid: drawback.uuid, name: element.name },
                            element.modifications ?? [],
                            async (resolved: SwadeModConfigurationInterface[]) => {
                                for (const config of resolved) await this.applyModification(character, config, true, 'permanent', drawback.uuid);
                                inserted = true;
                                this.selections.hindrance = null;
                                this.sortHindranceList(character);
                                this.recalculateDerivedStats(character);
                            },
                        );
                    } finally {
                        // Cancelling a choice still means the hindrance was never added. See the
                        // edge path for why this is a flag in a `finally` and not a check on
                        // withChoiceResolution's return value.
                        if (!inserted) {
                            character.hindrances = character.hindrances.filter(
                                (value: SwadeDrawbackInterface) => value.uuid !== drawback.uuid
                            );
                        }
                    }

                    return drawback;
                })
        },

        async applyAdvanceToCharacter(character: CharacterSheetInterface, advance: SwadeAbilityInterface, updateProto: boolean = true) {
            if (!advance) return;
            if (!character) return;

            if (advance.active) {
                character.protoData.locked = true;
                // Kept by reference, every later recalculation rewrote these pools, and updateAllocatedCharacterPoints'
                // `configuration.other = undefined` emptied the live character-point pool.
                this.freezeCreationPools(character);
            }
            else if (!this.hasActiveAdvances(character)) {
                character.protoData.locked = false;
                character.configuration = null;
            }

            console.debug("CharacterData | applyAdvanceToCharacter | advance | ", advance);
            // Deliberately NOT guarded by directModificationsWithoutOverrides, unlike every other
            // direct-apply loop in this file.
            //
            // 1. Unreachable, not merely dormant: a record's `uuid` always names the container the
            //    container-target affordance was opened on, and that affordance is hosted on
            //    exactly six sources — framework (applyFramework), edges (addEdgeToCharacter),
            //    cyberware and gear (addItemToCharacter / _addCyberwareToCharacter), ancestry
            //    (applyAncestry), and hindrances (addDrawbackToCharacter) — each calling
            //    resolveChoiceSets/withChoiceResolution with its own freshly-created uuid. An
            //    advance is none of them: applyAdvanceToCharacter is invoked only from Details.vue
            //    and AdvancementEditor.vue, and never itself calls resolveChoiceSets or
            //    withChoiceResolution. recordsForSourceModification(records, advance.uuid, …) can
            //    therefore never return anything — the guard would be a provable no-op here.
            // 2. A filter alone would make it worse: every guarded site pairs its filtered direct
            //    loop with a record-application loop that applies the redirected configuration in
            //    its place. This loop has no such pairing, here or in either caller, so filtering it
            //    would silently drop the modification's effect instead of retargeting it.
            //
            // If an affordance is ever hosted on an advance, add the record-resolution loop FIRST —
            // only then does adding this filter make sense.
            advance.modifications?.forEach((modification: SwadeModificationInterface) => {
                if (advance.active) {
                    this.applyModification(character, modification.configuration, updateProto, 'permanent', advance.uuid);
                }
                else {
                    this.rollbackModification(character, modification.configuration, updateProto, 'permanent', advance.uuid);
                }
            });
        },
        async applyAncestry(character: CharacterSheetInterface, ancestry: SwadeAncestryInterface|null) {
            console.info("CharacterData | applyAncestry")
            if (!character) return Promise.resolve(null);

            if (!Array.isArray(character.abilities)) {
                character.abilities = [];
            }

            console.debug("CharacterData | applyAncestry | ", character.ancestry)
            if (character.ancestry) {
                for (const modification of (character.ancestry.modifications ?? [])) {
                    console.debug("CharacterData | applyAncestry | rolling back direct | ", modification)
                    if (modification?.configuration?.path === 'character.choiceSet') continue;
                    // Roll back character-state mods AND build-option mods, matching
                    // applyFramework. BuildOptionConfigurator offers stored effects
                    // (edge, hindrance, superPowerPoints, coreSkills, linkedAttribute…);
                    // without the buildOption arm a swapped ancestry leaks every one.
                    const modType = modification?.configuration?.mod_type ?? 'character';
                    if (
                        modType === 'character'
                        || modType === 'buildOption'
                        || modification?.configuration?.path === 'startingFunds'
                    ) {
                        await this.rollbackModification(character, modification.configuration, true, 'ancestry', character.ancestry?.uuid ?? '');
                    }
                }

                const grantedAbilities = Array.isArray(character?.ancestry?.grants) ? character.ancestry.grants : [];
                for (const grantedAbility of grantedAbilities) {
                    for (const modification of (grantedAbility.modifications ?? [])) {
                        console.debug("CharacterData | applyAncestry | rolling back | ", modification)
                        if (modification?.configuration?.path === 'character.choiceSet') continue;
                        const modType = modification?.configuration?.mod_type ?? 'character';
                        if (
                            modType === 'character'
                            || modType === 'buildOption'
                            || modification?.configuration?.path === 'startingFunds'
                        ) {
                            // Combined into ONE withChoiceResolution dispatch at apply time
                            // (combinedModifications below), under the ancestry's own source uuid —
                            // not the granted ability's — so rollback must key on the same uuid to
                            // find what was actually registered.
                            await this.rollbackModification(character, modification.configuration, true, 'ancestry', character.ancestry?.uuid ?? '');
                        }
                    }
                    const grantedContainer = character.abilities.find((a: any) => a?.selection?.uuid === grantedAbility.uuid);
                    if (grantedContainer) await this.rollbackAppliedChoicesFor(character, grantedContainer.uuid);
                }
                if (character.ancestry?.uuid) await this.rollbackAppliedChoicesFor(character, character.ancestry.uuid);
            }

            if (ancestry === null) {
                character.ancestry = null;
                await this.cleanupOrphanedChoices(character);
                await this.commitCharacterRecalculation(character);
                return;
            }

            // character.ancestry is the only home for the character's ancestry — the
            // backend deleted the character_sheet_ability rows in Version20260818120000,
            // and character.abilities is typed to Ability there now. Mirrors applyFramework.
            character.ancestry = { ...ancestry }

            const ancestrySourceUuid = character.ancestry?.uuid ?? ancestry.uuid;
            const combinedModifications = [
                ...(ancestry.modifications ?? []),
                ...((ancestry.grants ?? []).flatMap((g: any) => g.modifications ?? [])),
            ];

            await this.withChoiceResolution(
                character,
                { uuid: ancestrySourceUuid, name: ancestry.name },
                combinedModifications,
                async (resolved: SwadeModConfigurationInterface[]) => {
                    for (const config of resolved) {
                        await this.applyModification(character, config, true, 'ancestry', ancestrySourceUuid);
                    }
                },
            );

            await this.commitCharacterRecalculation(character);

            return Promise.resolve({character: character, ancestry: ancestry})
        },
        async commitCharacterRecalculation(character: CharacterSheetInterface) {
            this.updateBuildValidation(character, this.buildOptions);
            await this.recalculateTotalPerkPoints(character);
            this.updateBuildStatus(character);
        },
        async applyFramework(character: CharacterSheetInterface, framework: SwadeCharacterFrameworkInterface|null) {
            console.info("CharacterData | applyFramework")
            if (!character) return Promise.resolve(null);

            console.debug("CharacterData | applyFramework | ", character.framework)
            if (character.framework) {
                for (const modification of (character.framework.modifications ?? [])) {
                    console.debug("CharacterData | applyFramework | rolling back | ", modification)
                    if (modification?.configuration?.path === 'character.choiceSet') continue;
                    // Roll back both character-state mods (mod_type='character') and build-option
                    // mods (mod_type='buildOption', e.g. buildPoints, startingFunds). Without the
                    // buildOption branch a re-applied / swapped framework re-subtracts its
                    // buildPoints without ever undoing the previous subtract, draining the pool.
                    const modType = modification?.configuration?.mod_type ?? 'character';
                    if (
                        modType === 'character'
                        || modType === 'buildOption'
                        || modification?.configuration?.path === 'startingFunds'
                    ) {
                        await this.rollbackModification(character, modification.configuration, true, 'permanent', character.framework?.uuid ?? '');
                    }
                }
                if (character.framework?.uuid) {
                    await this.rollbackAppliedChoicesFor(character, character.framework.uuid);
                }
            }

            if (framework === null) {
                character.framework = null;
                await this.cleanupOrphanedChoices(character);
                await this.commitCharacterRecalculation(character);
                return Promise.resolve({ character, framework: null });
            }

            character.framework = { ...framework };

            // Frameworks grant equipment with provenance + in-use status. withChoiceResolution
            // would silently drop grantContext for direct (non-choiceSet) mods because its
            // inner loop applies them via applyModification BEFORE the finishFn runs. Roll
            // our own resolve-then-apply loop so the grant-aware dispatcher catches both
            // direct and choiceSet-resolved character.equipment adds.
            let appliedChoices: AppliedConfiguration[];
            let resolvedConfigs: SwadeModConfigurationInterface[];
            try {
                ({ applied: appliedChoices, resolved: resolvedConfigs } = await this.resolveChoiceSets(
                    character,
                    { uuid: framework.uuid, name: framework.name },
                    framework.modifications ?? [],
                ));
            } catch (err) {
                if (err instanceof ChoiceCancelledError) {
                    await this.commitCharacterRecalculation(character);
                    return Promise.resolve({ character, framework: null });
                }
                throw err;
            }

            if (appliedChoices.length > 0) {
                character.additionalData ??= {};
                character.additionalData.choices ??= [];
                character.additionalData.choices.push(...appliedChoices);
            }

            const grantContext = { source: 'framework' as const, sourceUuid: framework.uuid };
            const applyOne = async (cfg: SwadeModConfigurationInterface) => {
                if (cfg.path === 'character.equipment' && cfg.operator === 'add') {
                    await this.applyEquipmentModification(character, cfg, 'add', grantContext);
                } else {
                    await this.applyModification(character, cfg, true, 'permanent', framework.uuid);
                }
            };

            // Direct (non-choiceSet) modifications.
            for (const mod of directModificationsWithoutOverrides(character, framework.uuid, framework.modifications)) {
                await applyOne(mod.configuration);
            }
            // Choice-resolved configurations.
            for (const cfg of resolvedConfigs) {
                await applyOne(cfg);
            }

            await this.syncCarriedPayloads(character);
            await this.commitCharacterRecalculation(character);

            return Promise.resolve({ character, framework });
        },
        async applyModification(
            character: CharacterSheetInterface,
            configuration: SwadeModConfigurationInterface,
            updateProto: boolean = true,
            source: ModSource = 'permanent',
            sourceContainerUuid: string | null = null,
        ): Promise<any> {
            console.debug("CharacterData | applyModification | ", configuration);
            if (!character.protoData) {
                character.protoData = this.newProtoData();
            }

            if (configuration.mod_type === 'buildOption') {
                return Promise.resolve(this.applyCharacterBuildOption(character, configuration));
            }

            // Container paths route FIRST. They share no machinery with the character.* branches:
            // no proto update, no operator dispatch — the effect is recorded and the override is
            // rebuilt from the ledger. A skill's rank is the exception (owner, 2026-10-08): it is
            // changed on the skill itself, as a skill modification changes it.
            if ((configuration.path ?? '').startsWith('container.')) {
                if (isSkillRankEffect(configuration)) {
                    return Promise.resolve(this.applySkillRankEffect(character, configuration, sourceContainerUuid, 1, updateProto));
                }
                return Promise.resolve(
                    this.registerContainerEffect(character, configuration, sourceContainerUuid),
                );
            }

            // effective.* rows (spec 2026-09-20 §6.1) have no apply-time effect: recalculateEffectiveAttributes,
            // run by updateBuildStatus and recalculateDerivedStats after every add and remove, rebuilds the
            // offsets from the shared walk. Nothing to write here, nothing to roll back.
            if (isEffectivePath(configuration.path)) {
                return Promise.resolve();
            }

            if (configuration.path === 'character.edges') {
                if (updateProto) {
                    await this.modifyProtoEdges(character, configuration, configuration.operator ?? "")
                }
                return this.applyEdgeModification(character, configuration, configuration.operator ?? "");
            }
            else if (configuration.path === 'character.skills') {
                if (updateProto) {
                    await this.modifyProtoSkills(character, configuration, configuration.operator ?? "")
                }

                return this.applySkillModification(character, configuration, configuration.operator ?? "")
            }
            else if (configuration.path === 'character.attributes') {
                if (updateProto) {
                    await this.modifyProtoAttributes(character, configuration, configuration.operator ?? "")
                }

                return this.applyAttributeModification(character, configuration, configuration.operator ?? "", source)
            }
            else if (configuration.path === 'character.hindrances') {
                if (updateProto) {
                    await this.modifyProtoHindrances(character, configuration, configuration.operator ?? "")
                }

                return this.applyHindranceModification(character, configuration, configuration.operator ?? "");
            }
            else if (configuration.path === 'character.abilities') {
                return this.applyAbilityModification(character, configuration, configuration.operator ?? "")
            }
            else if (configuration.path === 'character.languages') {
                return this.applyLanguageModification(character, configuration, configuration.operator ?? "")
            }
            else if (configuration.path === 'character.equipment') {
                return this.applyEquipmentModification(character, configuration, configuration.operator ?? "")
            }
            else if (configuration.path === 'character.powers') {
                return this.applyPowersModification(character, configuration, configuration.operator ?? "")
            }
            else if (configuration.path === 'character.powerPoints') {
                return this.applyPowerPointModification(character, configuration, configuration.operator ?? "")
            }
        },

        /**
         * Record one active container modification and rebuild the affected overrides.
         *
         * Records nothing when the modification targets no container, when its element mode is
         * unsupported, or when its detail is construction-time — a `charges` modification is applied
         * by the item-creation path, never here, so that a status transition cannot replenish it.
         */
        registerContainerEffect(
            character: any,
            configuration: SwadeModConfigurationInterface,
            sourceContainerUuid: string | null,
        ): void {
            // '' as well as null: the blank property option wrote the empty string before it bound
            // null, and stored modifications (transfer-only edges) still carry it. Same meaning. A
            // transfer-only modification folds nothing, so it stays out of the ledger — EXCEPT an All
            // Specified one that carries entries (2026-09-24, Melee Attack's wrapper over Unarmed lending
            // the power's damage): the carry sync's third source walks All Specified LEDGER entries'
            // carry and nothing else resolves them, whereas a record's or a self-mode direct
            // modification's carry is walked from the record or the container itself. The rebuild skips
            // a detail-less entry, so the row itself folds nothing.
            const detail = configuration.detail ?? null;
            const transferOnly = detail === null || detail === '';
            if (transferOnly && !(authoredTargetMode(configuration) === 'allSpecified' && carriedEntries(configuration).length > 0)) return;

            if (detail !== null && detail !== '' && containerFieldPolicy(detail)?.timing === 'construction') return;

            if (!sourceContainerUuid) {
                // A blank sourceUuid is not a harmless default: rollback and cleanupOrphanedChoices
                // both key on it, so every blank-sourced effect shares one bucket and is destroyed
                // together by any unrelated removal. Refusing is safer than recording a collision.
                console.warn('CharacterData | registerContainerEffect | refused: no source container uuid', configuration);
                return;
            }

            const { targets, mode } = resolveContainerTargets(configuration, character, sourceContainerUuid);
            // A positive allow-list. `self` and `byContainer` (a record naming a container) register;
            // `all` (an All Specified list, spec 2026-09-13 §4.1) does too, even with zero current
            // matches — see below. `bySelection` — an authored list matched by catalog element — is
            // refused since spec 2026-09-13 §3.2: a Specific Choices modification reaches the ledger
            // only through a recorded pick, never through the direct loop, source ticking or a
            // choice-set option's record, so ordering can no longer fan it out. `prompt` and
            // `unsupported` always pair with an empty target list and fall out below either way.
            if (mode === 'bySelection') {
                console.debug('CharacterData | registerContainerEffect | authored list refused: a Specific Choices modification applies only through a recorded pick', configuration);
                return;
            }
            if (mode !== 'self' && mode !== 'byContainer' && mode !== 'all') return;
            // An All Specified entry is registered even when nothing matches yet — applying to a
            // container added later is the point — and stores no targets: effectTargets resolves it
            // against the sheet on every read (spec 2026-09-13 §4.1).
            if (mode !== 'all' && targets.length === 0) return;

            character.additionalData ??= {};
            // ??= does not repair a non-array (e.g. `containerEffects: {}`, the malformed shape
            // recomputeContainerOverrides already guards against) — only a missing/undefined key.
            // Assigning a fresh array here whenever the current value is not one self-heals it,
            // matching how recomputeContainerOverrides/deregisterContainerEffects read it.
            if (!Array.isArray(character.additionalData.containerEffects)) {
                character.additionalData.containerEffects = [];
            }
            character.additionalData.containerEffects.push({
                effectId: uuid(),
                sourceUuid: sourceContainerUuid,
                sourceModUuid: deriveContainerModKey(configuration),
                targetContainerUuids: mode === 'all' ? [] : targets.map((target: any) => target.uuid).filter(Boolean),
                // Stored by reference otherwise: mutating the caller's configuration object after
                // this call would silently change the resolved override. The choices ledger already
                // deep-copies before dispatch (resolveChoiceSets); this ledger did not.
                configuration: deepCopyObject(configuration),
            });

            this.recomputeContainerOverrides(character);
        },

        /**
         * Drop effects granted by one source, then rebuild. Mutate the ledger BEFORE recomputing.
         *
         * Registration is per-modification (registerContainerEffect), NOT per-instance — N
         * identical registrations (e.g. N cyberware-quantity ticks of the same modification) share
         * one sourceModUuid. Without `sourceModUuid`, every effect from `sourceUuid` is removed —
         * what rollbackAppliedChoicesFor (the whole source is gone) and the in-use transition (Task
         * 11) both need. With it, exactly ONE matching effect is removed — the most recently
         * registered one — leaving the source's other instances/modifications intact, and moving
         * any player-entered values from the removed instance to the instance that is now last
         * (spec 2026-10-13 §2). Removing every match instead of one would let a single
         * quantity-decrease tick destroy an entire stack at once instead of unwinding it one tick
         * at a time.
         */
        deregisterContainerEffects(character: any, sourceUuid: string, sourceModUuid?: string): void {
            const effects: ContainerEffect[] = Array.isArray(character?.additionalData?.containerEffects)
                ? character.additionalData.containerEffects
                : [];

            // Guard, matching registerContainerEffect above: this is called unconditionally from
            // rollbackAppliedChoicesFor (character-data.ts) for every container removal, including
            // one whose character carries no additionalData at all (see
            // character-data-rollback-applied-choices.test.ts's "handles missing additionalData /
            // choices gracefully"). Without it, the assignment below throws on that bare character
            // instead of leaving it untouched.
            character.additionalData ??= {};

            if (sourceModUuid === undefined) {
                // No narrowing key: whole-source removal. Drop every effect this source granted.
                character.additionalData.containerEffects = effects.filter((e) => e.sourceUuid !== sourceUuid);
            } else {
                // Narrowed removal: remove exactly the LAST matching entry, not every match — see
                // the doc comment above. Iterating from the end and stopping at the first hit keeps N
                // applies followed by N rollbacks symmetric, one instance at a time, regardless of how
                // many identical instances are still stacked.
                const next = effects.slice();
                const isInstance = (e: ContainerEffect) => e.sourceUuid === sourceUuid && e.sourceModUuid === sourceModUuid;
                for (let i = next.length - 1; i >= 0; i--) {
                    if (isInstance(next[i])) {
                        const [removed] = next.splice(i, 1);
                        // One of N identical instances goes, not the modification. The values the player
                        // entered sit on the last instance, the one the field binds to; they move to the
                        // instance that is last now, so lowering a stack does not blank its field
                        // (spec 2026-10-13 §2).
                        const survivor = next.slice(0, i).reverse().find(isInstance);
                        if (removed.entered && survivor) survivor.entered = { ...(survivor.entered ?? {}), ...removed.entered };
                        break;
                    }
                }
                character.additionalData.containerEffects = next;
            }

            this.recomputeContainerOverrides(character);
        },

        /**
         * The player's answer to a container modification authored with a null value on a text
         * field (spec 2026-09-15): stored on the ledger entry, keyed by target container, and read
         * back by the rebuild through effectiveEffect. Never written into configuration.value —
         * deriveContainerModKey stringifies it, and the narrowed rollback finds the entry by that
         * key. A blank answer deletes the key; an emptied map is removed altogether, so a cleared
         * answer leaves the entry byte-identical to one that never had a field. Refusals are
         * silent no-ops: no write, no recompute. The row that calls this owns the `updated` emit.
         */
        enterContainerEffectValue(character: any, effectId: string, containerUuid: string, value: string | null): void {
            const ledger: unknown = character?.additionalData?.containerEffects;
            if (!Array.isArray(ledger)) return;
            if (typeof effectId !== 'string' || effectId.length === 0) return;
            // The uuid is about to be a bracket-written key: the same guard the detail accumulators use.
            if (typeof containerUuid !== 'string' || containerUuid.length === 0 || isUnsafeDetailKey(containerUuid)) return;
            const effect = (ledger as ContainerEffect[]).find((entry) => entry?.effectId === effectId);
            if (!effect || !isEnterable(effect.configuration)) return;

            const trimmed = typeof value === 'string' ? value.trim() : '';
            const current: unknown = effect.entered;
            const entered: Record<string, string> = current !== null && typeof current === 'object' && !Array.isArray(current)
                ? { ...(current as Record<string, string>) }
                : {};
            if (trimmed.length > 0) entered[containerUuid] = trimmed;
            else delete entered[containerUuid];
            if (Object.keys(entered).length > 0) effect.entered = entered;
            else delete effect.entered;

            this.recomputeContainerOverrides(character);
        },

        /**
         * Write a new container's starting charges. Called ONLY from the item-creation path.
         *
         * `charges` is deliberately absent from applyModification's dispatch: that dispatch is
         * reached by status transitions (_ticksContainerMods) and re-application flows, any of which
         * would replenish the player's charges. Keeping the field out of the dispatch entirely means
         * no future caller can reintroduce that bug by forgetting a guard.
         *
         * Nothing is recorded in the containerEffects ledger. That is the point: every other
         * container field is rebuilt from the ledger on any change, and charges must survive being
         * spent. The consequence is that charges is NOT reversed when the granting source is
         * removed — consumable player state, once handed over, is the player's.
         *
         * `container` MUST already be present on `character`: resolveContainerTargets searches the
         * character's arrays, so a self-targeting modification (absent `element`) finds nothing
         * until the push has happened.
         *
         * Takes whole modifications rather than bare configurations so the three call sites do not
         * each repeat the same unwrap-and-cast.
         */
        seedContainerCharges(
            character: any,
            container: any,
            modifications: SwadeModificationInterface[],
        ): void {
            // The registry, not this function, decides that charges is construction-time. The two
            // ledger paths (registerContainerEffect, rebuildContainerOverrides) read the same
            // marker to REFUSE the field; this is the one writer permitted to set it. Reading the
            // registry rather than testing `detail === 'charges'` is what makes reclassifying it an
            // `override` field switch every path together, instead of leaving this writer seeding a
            // field the ledger also rebuilds.
            //
            // Hoisted out of the filter below: the lookup is loop-invariant, and its argument is a
            // literal only because the filter's first line constrains `detail` to exactly 'charges'.
            // Note this reads `timing` alone — the policy's `fold` is not consulted; see the
            // registry's own comment in @/appdata/container-fields for why.
            if (containerFieldPolicy('charges')?.timing !== 'construction') return;

            const applicable = (modifications ?? [])
                .map((modification) => modification?.configuration)
                .filter((configuration) => {
                    if (configuration?.detail !== 'charges') return false;

                    const { targets, mode } = resolveContainerTargets(configuration, character, container?.uuid ?? null);
                    // A positive allow-list, NOT `mode === 'unsupported' || mode === 'prompt'`: those
                    // two modes always pair with an empty targets list, so naming them is redundant
                    // with the `targets.some(...)` check below, while naming the modes this DOES
                    // handle still refuses a future mode that is neither self, byContainer, bySelection
                    // nor all yet does resolve targets. The disjunct form would have seeded from it
                    // silently.
                    //
                    // Unlike registerContainerEffect's allow-list, this one keeps `bySelection`: this
                    // guard is no longer identical to that one, since spec 2026-09-13 §3.2 narrowed
                    // registration to refuse it. An item's own catalog modification (e.g. a weapon's
                    // starting-charges detail) names its own element by selection identity, not by
                    // container uuid, so refusing bySelection here would leave a brand-new container's
                    // own charges unseeded. `all` (spec 2026-09-13 §4.1) joins both allow-lists: an All
                    // Specified charges modification seeds the container being constructed exactly
                    // when that container is itself one of the eligible targets.
                    if (mode !== 'self' && mode !== 'byContainer' && mode !== 'bySelection' && mode !== 'all') return false;

                    // A bySelection resolution resolves EVERY container holding that catalog element,
                    // including ones the player already owned. Only the container being constructed is
                    // seeded; the others keep whatever charges they have spent down to.
                    return targets.some((target: any) => target?.uuid === container?.uuid);
                });

            // Additive only. alter and multiply are ignored because newItemContainer sets
            // charges: null and no detail interface carries the field, so there is no base.
            //
            // The value goes through parseScalar — "the one place a modification value becomes a
            // number", per its own doc — and NOT through an inline Number.isFinite(Number(value)).
            // That inline form was a fourth copy of a rule this codebase has already been bitten by
            // three times: Number(null), Number('') and Number('   ') are all a FINITE 0, so a
            // half-authored charges modification passed the check and wrote `charges: 0` over
            // newItemContainer's null — marking a brand-new item as spent. `value: null` is the
            // shape a blank form template hands back, so that was reachable from ordinary UI state.
            // parseScalar refuses null, non-string/number values and whitespace-only strings, which
            // is exactly contributesSomething's value rule plus the finiteness test; unlike
            // contributesSomething it also returns the number, so the coercion happens ONCE per
            // entry here instead of once in a filter and again in a reduce.
            //
            // Mapping to signed deltas rather than carrying configurations forward is what makes
            // that single call possible: after this pipeline there is nothing left to re-parse.
            const contributing = applicable
                .filter((configuration) => configuration.operator === 'add' || configuration.operator === 'subtract')
                .map((configuration) => {
                    const value = parseScalar(configuration.value);
                    if (value === null) return null;
                    return configuration.operator === 'subtract' ? -value : value;
                })
                .filter((delta): delta is number => delta !== null);

            // Counted AFTER the operator and value filters, not before them. An alter-only,
            // multiply-only or unparseable-value charges modification is applicable but contributes
            // nothing, and reducing an empty list yields 0 — which would write 0 over the null
            // newItemContainer set, and 0 charges is a spent item, not an unlimited one.
            // "Contributes nothing" must leave the field alone, exactly as "targets nothing" does.
            if (contributing.length === 0) return;

            const total = contributing.reduce((sum, delta) => sum + delta, 0);

            container.charges = Math.max(0, Math.floor(total));
        },

        // Reads `element` through elementUuids for the same reason applyEdgeModification does: the
        // dispatch calls both for one modification, so a chip list the applier grants must be
        // recorded here too, not turned into `/elements/[object Object]/one` and dropped.
        async modifyProtoEdges(character: CharacterSheetInterface, modification: SwadeModConfigurationInterface, operator: string) {
            if (!modification) return Promise.resolve();

            const store = useAppDataStore();
            const uuids = elementUuids(modification.element);

            if (operator === 'add') {
                for (const uuid of uuids) {
                    const edge: SwadeEdgeInterface | null = await store.retrieveElement({ uuid });
                    if (!edge) continue;

                    character.protoData.edges.push({
                        uuid: edge.uuid,
                        containerUuid: undefined,
                        value: 0,
                    });
                }
            }
            else if (operator === 'subtract') {
                character.protoData.edges = character.protoData.edges.filter((item: { uuid: string }) => !uuids.includes(item.uuid));
            }

            return Promise.resolve()
        },
        removeProtoEdge(character: CharacterSheetInterface, edge: SwadeEdgeInterface, containerUuid?: string) {
            if (!character?.protoData?.edges) return;

            if (containerUuid) {
                character.protoData.edges = character.protoData.edges.filter((item: any) => {
                    return item.containerUuid !== containerUuid;
                });
            } else {
                character.protoData.edges = character.protoData.edges.filter((item: any) => {
                    return item.uuid !== edge.uuid;
                });
            }
        },
        getEdgeModificationConfigs(edges: SwadeEdgeContainerInterface[], path: string, element: string): SwadeModConfigurationInterface[] {
            if (!Array.isArray(edges)) return [];

            return edges
                .flatMap((container: SwadeEdgeContainerInterface) => container.selection.modifications ?? [])
                .filter((mod: SwadeModificationInterface) => mod.configuration?.path === path && mod.configuration?.element === element)
                .map((mod: SwadeModificationInterface) => mod.configuration);
        },
        /**
         * `element` is read through elementUuids, like the ability and power appliers: a bare uuid, a
         * uuid list or a { uuid, label } chip list name one catalog edge per entry, and each add or
         * removal is awaited in turn, so every listed edge is on (or off) the character when this
         * resolves. The raw Array.isArray branches this replaces fired each entry without waiting and
         * handed a chip to retrieveElement whole, which asked the API for `/elements/[object Object]/one`.
         */
        async applyEdgeModification(character: CharacterSheetInterface, modification: SwadeModConfigurationInterface, operator: string) {
            if (!modification) return Promise.resolve();

            // Supports add and subtract
            const store = useAppDataStore();
            const uuids = elementUuids(modification.element);

            if (operator === 'add') {
                for (const uuid of uuids) {
                    const edge: SwadeEdgeInterface | null = await store.retrieveElement({ uuid });
                    // A uuid the catalog no longer answers (retrieveElement reports the error and
                    // resolves undefined) is skipped so the rest of the list still lands.
                    if (!edge) continue;

                    await this.addEdgeToCharacter(character, edge, 0);
                }
            }
            else if (operator === 'subtract') {
                for (const uuid of uuids) {
                    const container = character.edges?.find((c: SwadeEdgeContainerInterface) => c.selection.uuid === uuid);
                    if (container) await this.removeEdge(character, container);
                }
            }

            return Promise.resolve()
        },
        /**
         * `element` is read through elementUuids, like applyAbilityModification: a bare uuid, a uuid
         * list or a { uuid, label } chip list name one catalog element per entry. The power atom and
         * the API import both write one uuid, but the type admits the lists and a hand-authored or
         * carried modification can hold one; the whole value used to be handed to ONE lookup as the
         * uuid, so a list became the request `/elements/a,b/one` and granted nothing.
         */
        async applyPowersModification(character: CharacterSheetInterface, modification: SwadeModConfigurationInterface, operator: string) {
            console.info("CharacterData | applyPowersModification")
            if (!modification) return Promise.resolve();

            if (!Array.isArray(character.powers)) {
                character.powers = [];
            }

            if (!Array.isArray(character.abilities)) {
                character.abilities = [];
            }

            // Supports add and subtract
            const store = useAppDataStore();
            const uuids = elementUuids(modification.element);

            if (operator === 'add') {
                for (const uuid of uuids) {
                    const power: SwadePowerInterface | SwadeAbilityInterface | null = await store.retrieveElement({ uuid });
                    console.debug(power);
                    // A uuid the catalog no longer answers (retrieveElement reports the error and
                    // resolves undefined) is skipped so the rest of the list still lands.
                    if (!power) continue;

                    // `details.powerPoints` is a core power's field: an ability has no
                    // `details` at all and a superpower's detail carries `levels` instead, and
                    // both already fell through to the ability branch below.
                    if ((power as { details?: { powerPoints?: number | string | null } }).details?.powerPoints) {
                        const newPower = this.newPowerContainer(character, power as SwadePowerInterface);
                        character.powers.push(newPower);
                    }
                    else {
                        character.abilities.push(power);
                    }
                }

                this.sortPowersList(character);
            }
            else if (operator === 'subtract') {
                for (const uuid of uuids) {
                    await this.removePowerOrAbility(character, { uuid });
                }
            }

            return Promise.resolve()
        },

        /**
         * character.languages (spec 2026-09-21): `element` is a list of language names. Each lands in
         * whichever shape the world's language variant uses, marked FREE with `slotsUsed: 0` so
         * updateAllocatedLanguages (which sums `slotsUsed ?? 1`) never counts it. Skill variant: a rank
         * of the language skill at d6 / base d6 with the name as its trapping, plus a protoData.languages
         * entry — creation data, so gated like proto skills in addSkillToCharacter. Otherwise (the binary
         * variant, or a world with no languages block at all) the knownLanguages entry IS the language
         * and is always written. A paid pick of the same name is refunded, never duplicated (ruling
         * 2026-09-21); subtract removes only free entries, and a rank only when no paid entry claims the
         * name — which is what makes an advance-granted rank, whose creation ledger was never written,
         * roll back too. A free row is removed even while advancing: it is the grant's own
         * creation-time write, and taking it back changes neither the budget nor a paid pick.
         */
        async applyLanguageModification(character: CharacterSheetInterface, modification: SwadeModConfigurationInterface, operator: string) {
            console.info("CharacterData | applyLanguageModification")
            if (!modification) return;
            if (operator !== 'add' && operator !== 'subtract') return;

            const names: string[] = Array.isArray(modification.element)
                ? (modification.element as unknown[])
                    .filter((entry): entry is string => typeof entry === 'string')
                    .map((entry) => entry.trim())
                    .filter((entry) => entry.length > 0)
                : [];
            if (names.length === 0) return;

            const languageSkillUuid: string | null = this.buildOptions?.languages?.skill ?? null;
            const skillBased = !!languageSkillUuid;

            if (!character.protoData) {
                character.protoData = this.newProtoData();
            }
            // protoData.languages is creation data: written under the gate addSkillToCharacter applies to
            // proto skills. knownLanguages is the language itself and is always written.
            const ledgerWritable = !skillBased || (!character.protoData.locked && !this.hasAdvances(character));

            const ledger = (): { label: string, slotsUsed?: number }[] => {
                if (skillBased) {
                    if (!Array.isArray(character.protoData.languages)) character.protoData.languages = [];
                    return character.protoData.languages;
                }
                character.additionalData ??= {};
                if (!Array.isArray(character.additionalData.knownLanguages)) character.additionalData.knownLanguages = [];
                return character.additionalData.knownLanguages;
            };
            // The raw trapping, not resolveSkillTrapping(): the grant wrote the raw property, and a
            // container override of it is a different concept.
            const languageRank = (name: string): SwadeSkillRankInterface | undefined =>
                character.skills?.find((rank: SwadeSkillRankInterface) =>
                    rank.skill?.uuid === languageSkillUuid && rank.trapping === name);

            // The language skill is fetched at most once per call, and only when a rank is missing:
            // retrieveElement is one uncached round-trip, and every name resolves the same uuid.
            let languageSkill: SwadeSkillInterface | null | undefined;
            const fetchLanguageSkill = async (): Promise<SwadeSkillInterface | null> => {
                if (languageSkill === undefined) {
                    const store = useAppDataStore();
                    // A real type on the assignment: retrieveElement resolves `any`, which would otherwise
                    // narrow the memo back to its declared union and fail the return type.
                    const fetched: SwadeSkillInterface | null = (await store.retrieveElement({ uuid: languageSkillUuid })) ?? null;
                    languageSkill = fetched;
                }
                return languageSkill;
            };

            for (const name of names) {
                if (operator === 'add') {
                    if (ledgerWritable) {
                        const entries = ledger();
                        const existing = entries.find((entry) => entry.label === name);
                        if (existing) existing.slotsUsed = 0;
                        else entries.push({ label: name, slotsUsed: 0 });
                    }
                    if (skillBased && !languageRank(name)) {
                        const skill = await fetchLanguageSkill();
                        // A uuid the catalog no longer answers — or a transient fetch failure, which
                        // retrieveElement reports and resolves undefined — is skipped, as the edge applier
                        // skips a missing edge; the ledger entry above still records the language, and a
                        // later re-apply of the source adds the rank.
                        if (skill) await this.addSkillToCharacter(character, skill, true, 'd6', name);
                    }
                }
                else {
                    // A free row is the grant's own write, so it goes whenever it is found — even while
                    // advancing, when the add gate above is closed: taking back a slotsUsed 0 row can
                    // alter neither the budget nor a paid pick. Read, never materialise: a rollback
                    // that finds nothing must not create the arrays.
                    const entries: { label: string, slotsUsed?: number }[] | undefined = skillBased
                        ? character.protoData.languages
                        : character.additionalData?.knownLanguages;
                    const idx = Array.isArray(entries)
                        ? entries.findIndex((entry) => entry.label === name && entry.slotsUsed === 0)
                        : -1;
                    if (idx >= 0) entries!.splice(idx, 1);
                    if (skillBased) {
                        const paid = (character.protoData.languages ?? [])
                            .some((entry: { label: string, slotsUsed?: number }) => entry.label === name && (entry.slotsUsed ?? 1) > 0);
                        const rank = languageRank(name);
                        if (rank && !paid) await this.removeSkill(character, rank.skill, rank.uuid);
                    }
                }
            }

            this.updateAllocatedLanguages(character);
        },
        async applyAbilityModification(character: CharacterSheetInterface, modification: SwadeModConfigurationInterface, operator: string) {
            console.info("CharacterData | applyAbilityModification")
            if (!modification) return Promise.resolve();
            if (!Array.isArray(character.abilities)) {
                character.abilities = [];
            }

            const store = useAppDataStore();
            const uuids = elementUuids(modification.element);

            if (operator === 'add') {
                for (const uuid of uuids) {
                    if (character.abilities.some((a: SwadeAbilityInterface) => a.uuid === uuid)) continue;

                    const ability: SwadeAbilityInterface | null = await store.retrieveElement({ uuid });
                    if (!ability || ability.type !== 'ability') continue;

                    character.abilities.push({ ...ability });

                    await this.withChoiceResolution(
                        character,
                        { uuid: ability.uuid, name: ability.name },
                        ability.modifications ?? [],
                        async (resolved: SwadeModConfigurationInterface[]) => {
                            for (const config of resolved) {
                                await this.applyModification(character, config, true, 'permanent', ability.uuid);
                            }
                        },
                    );
                }
            }
            else if (operator === 'subtract') {
                for (const uuid of uuids) {
                    const existing = character.abilities.find((a: SwadeAbilityInterface) => a.uuid === uuid);
                    if (existing) {
                        await this.removePowerOrAbility(character, { uuid });
                    }
                }
            }

            return Promise.resolve();
        },

        async applyEquipmentModification(
            character: CharacterSheetInterface,
            modification: SwadeModConfigurationInterface,
            operator: string,
            grantContext?: { source: 'framework', sourceUuid: string } | null,
        ) {
            console.info("CharacterData | applyEquipmentModification")
            if (!modification) return Promise.resolve();
            if (!Array.isArray(character.equipment)) {
                character.equipment = [];
            }

            const store = useAppDataStore();
            // Read through elementUuids, like the ability, power and edge appliers: a bare uuid, a uuid
            // list or a { uuid, label } chip list each name one catalog item per entry, and an entry
            // that names no uuid is dropped rather than compared to containers or sent to the API.
            const targetUuids = elementUuids(modification.element);

            // Compute quantity delta once. `modification.value` is typed `string | number | null`,
            // so coerce to a number and guard against 0, negatives, NaN, and non-numeric strings —
            // all treated as 1 so add/subtract remain symmetric for rollback correctness.
            const rawValue = Number(modification.value ?? 1);
            const delta = Number.isFinite(rawValue) && rawValue > 0 ? rawValue : 1;

            if (operator === 'add') {
                for (const targetUuid of targetUuids) {
                    // The same stacking rule as an add (spec 2026-10-13 §8): a copy of an item that asks,
                    // or one a pick has customised, gets a container of its own.
                    const [existing] = stackableContainers(character, targetUuid);
                    if (existing) {
                        await this.setContainerQuantity(character, existing, (existing.quantity ?? 1) + delta);
                        continue;
                    }

                    const item: SwadeItemInterface | null = await store.retrieveElement({ uuid: targetUuid });
                    if (!item) continue;

                    const container = this.newItemContainer(item);
                    container.quantity = delta;

                    if (grantContext) {
                        // Granted items default to in-use status (installed for cyberware, equipped for
                        // armor, etc.) so the player sees the framework benefit immediately.
                        const inUse = rulesFor(item.type).inUseStatuses[0];
                        if (inUse) container.status = inUse;
                    }

                    character.equipment.push(container);

                    // Granted equipment seeds too. A framework-granted blaster is a real item
                    // entering the sheet, and arriving empty while the same item bought in the shop
                    // arrives loaded would be an arbitrary difference. Only THIS branch seeds: the
                    // `existing` branch above raises quantity and continues without building a
                    // container, so re-running a grant for an item already held cannot replenish it.
                    this.seedContainerCharges(character, container, item.modifications ?? []);

                    if (grantContext) {
                        character.additionalData ??= {};
                        character.additionalData.grantedEquipment ??= [];
                        character.additionalData.grantedEquipment.push({
                            containerUuid: container.uuid,
                            source: grantContext.source,
                            sourceUuid: grantContext.sourceUuid,
                        });
                    }

                    await this.withChoiceResolution(
                        character,
                        { uuid: container.uuid, name: item.name ?? '' },
                        item.modifications ?? [],
                        async (resolved: SwadeModConfigurationInterface[]) => {
                            for (const config of resolved) {
                                await this.applyModification(character, config, true, 'permanent', container.uuid ?? '');
                            }
                        },
                    );
                }
                this.sortEquipmentList(character);
            }
            else if (operator === 'subtract') {
                for (const targetUuid of targetUuids) {
                    // `delta` units across the item's containers, plain ones first (spec 2026-10-13 §8): a
                    // unit a pick has customised, or one that answered its own questions, goes last. The
                    // sort is stable, so containers that tie keep their sheet order.
                    const holding = character.equipment
                        .filter((c: SwadeItemContainerInterface) => c.selection?.uuid === targetUuid)
                        .sort((a: SwadeItemContainerInterface, b: SwadeItemContainerInterface) =>
                            Number(isUniqueContainer(character, a)) - Number(isUniqueContainer(character, b)));
                    let remaining = delta;
                    for (const existing of holding) {
                        if (remaining <= 0) break;
                        const quantity = existing.quantity ?? 1;

                        // Surviving items lose grant status per spec § Section 3. Stripped up front for a
                        // removal too, as belt-and-suspenders — removeItem also calls this helper, so the
                        // call is idempotent.
                        if (existing.uuid) this._stripGrantedEquipmentFor(character, existing.uuid);
                        if (quantity > remaining) {
                            await this.setContainerQuantity(character, existing, quantity - remaining);
                            remaining = 0;
                        } else {
                            remaining -= quantity;
                            await this.removeItem(character, existing);
                        }
                    }
                }
            }

            return Promise.resolve();
        },

        async modifyProtoHindrances(character: CharacterSheetInterface, modification: SwadeModConfigurationInterface, operator: string) {
            if (!modification) return Promise.resolve();

            // Supports add and subtract
            const store = useAppDataStore();
            if (operator === 'add') {
                const existingDrawback = character?.hindrances?.find((d: SwadeDrawbackInterface) =>
                    d.hindrance.uuid === modification.element
                );

                return store.retrieveElement({ uuid: modification.element })
                    .then((hindrance?: SwadeHindranceInterface) => {
                        if (!hindrance) return null;

                        let appliedType: string | null = null;
                        if ((modification.value ?? 0) === 1) {
                            if (existingDrawback) {
                                appliedType = 'major';
                            } else {
                                appliedType = 'minor';
                            }
                        }
                        else if (Number(modification.value ?? 0) > 1) {
                            appliedType = 'major';
                        }

                        character.protoData.hindrances.push({
                            uuid: hindrance.uuid,
                            type: appliedType,
                            value: modification.value,
                        })
                    })
                    .catch((error) => { handleError(error) })
                ;
            }
            else if (operator === 'subtract') {
                character.protoData.hindrances = character.protoData.hindrances.filter((item: any) => {
                    return item?.uuid !== modification.element;
                })
            }

            return Promise.resolve();
        },
        async applyHindranceModification(character: CharacterSheetInterface, modification: SwadeModConfigurationInterface, operator: string) {
            if (!modification) return;

            // Supports add and subtract
            const store = useAppDataStore();
            if (operator === 'add') {
                const existingDrawback = character?.hindrances?.find((d: SwadeDrawbackInterface) =>
                    d.hindrance.uuid === modification.element
                );

                return store.retrieveElement({ uuid: modification.element })
                    .then((hindrance?: SwadeHindranceInterface) => {
                        if (hindrance) {
                            return this.addDrawbackToCharacter(character, hindrance)
                        }

                        return Promise.resolve(hindrance);
                    })
                    .then((drawback?: SwadeDrawbackInterface) => {
                        if (!drawback) return null;

                        // Find through reactive array so Vue detects the mutation
                        const reactiveDrawback = character.hindrances.find(
                            (d: SwadeDrawbackInterface) => d.uuid === drawback.uuid
                        );
                        if (!reactiveDrawback) return null;

                        if (parseInt(String(modification.value ?? 0)) === 1) {
                            if (existingDrawback) {
                                reactiveDrawback.type = 'major';
                            } else {
                                reactiveDrawback.type = 'minor';
                            }
                        }
                        else if (Number(modification.value ?? 0) > 1) {
                            reactiveDrawback.type = 'major';
                        }
                    })
                    .catch((error) => { handleError(error) })
                ;
            }
            else if (operator === 'subtract') {
                const existingDrawback = character?.hindrances?.find((item: SwadeDrawbackInterface) => {
                    // console.debug(item, modification.element)
                    return item.hindrance.uuid === modification.element;
                });

                console.debug(existingDrawback, modification);
                if (existingDrawback) {
                    // Resolved once: an override IS the severity for costing and buy-off purposes.
                    // The assignment below stays RAW — buy-off writes the player's own value.
                    const severity = resolveDrawbackType(existingDrawback);

                    // FLAG (Task 7 decision, not fixed here): if `existingDrawback` carries an
                    // override, demoting writes `existingDrawback.type = 'minor'` on the RAW field
                    // while the override still resolves to 'major' — the demotion has no visible
                    // effect. Fixing this needs override-clearing semantics, which is out of scope.
                    if (((modification.value ?? 0) === 1) && (severity === 'major')) {
                        existingDrawback.type = 'minor';
                    }
                    else if (
                        ((parseInt(String(modification.value ?? 0)) === 1) && (severity === 'minor'))
                        || ((parseInt(String(modification.value ?? 0)) > 1) && (severity === 'major'))
                    ) {
                        return this.removeDrawback(character, existingDrawback);
                    }
                }
            }

            return Promise.resolve();
        },

        async modifyProtoAttributes(character: CharacterSheetInterface, modification: SwadeModConfigurationInterface, operator: string) {
            if (!modification) return Promise.resolve();
            // The derived-stat test matched only a plain name before (an array is never found by
            // Array.includes and null fell through as ""), and still matches only a plain name.
            if (['pace','parry','toughness','size','powerPoints'].includes(elementName(modification.element) ?? "")) return Promise.resolve();

            // Supports add and subtract
            // RE-TYPED, not converted: `value` is `string | number | null` and a die-step count that
            // arrived as a string has always been passed to addSteps untouched.
            let stepDelta: number = (modification.value ?? 0) as number;
            if (operator === 'subtract') {
                stepDelta = stepDelta * -1;
            }

            // String() is what property access already does with a non-string key (obj[x] is
            // obj[String(x)]), so the key read and written is unchanged.
            const attributeKey = String(modification.element ?? "");
            console.debug(character, modification.element, character.protoData.attributes[attributeKey]?.base);
            let attribute = this.dieRank(character.protoData.attributes[attributeKey]?.base, null);
            if (attribute.isParsed()) {
                if (modification.valueType === 'step') {
                    attribute.addSteps(stepDelta);
                }

                character.protoData.attributes[attributeKey].base = attribute.getDieCode();
            }

            return Promise.resolve()
        },
        applyStepOrModify(rank: DieRank, delta: number, valueType: string | null | undefined): void {
            if (delta === 0) return;
            if (valueType === 'step') {
                if (delta < 0) rank.subtractSteps(Math.abs(delta));
                else rank.addSteps(delta);
            } else if (valueType === 'modify') {
                rank.addModifier(delta);
            }
        },
        _applyAttributeCalculateBase(
            character: CharacterSheetInterface,
            modification: SwadeModConfigurationInterface,
            stepDelta: number,
        ): void {
            // obj[x] is obj[String(x)]; spelled out so the union does not have to index directly.
            const targets = CALCULATE_BASE_TARGETS[String(modification.element ?? '')];
            if (!targets || targets.length === 0) return;
            character.additionalData ??= {};
            character.additionalData.calculateBase ??= {};
            for (const { purpose, attribute } of targets) {
                const calculateBase = character.additionalData.calculateBase as Record<string, Record<string, string>>;
                calculateBase[purpose] ??= {};
                const current = calculateBase[purpose][attribute] ?? 'd4';
                const rank = this.dieRank(current);
                this.applyStepOrModify(rank, stepDelta, modification.valueType);
                calculateBase[purpose][attribute] = rank.getDieCode() ?? current;
            }
        },
        async applyAttributeModification(
            character: CharacterSheetInterface,
            modification: SwadeModConfigurationInterface,
            operator: string,
            source: ModSource = 'permanent',
        ) {
            if (!character) return;
            if (!modification) return;

            // Handled derived attribute values slightly different
            console.debug("CharacterData | applyAttributeModification | ", modification, operator)
            // Matched only a plain name before and still does: Array.includes never found an array
            // or a partial list, and null fell through as "".
            if (['pace','parry','toughness','armor','size','powerPoints','strain'].includes(elementName(modification.element) ?? "")) {
                return this.applyDerivedAttributeModification(character, modification, operator);
            }

            // RE-TYPED, not converted — see modifyProtoAttributes above.
            let stepDelta: number = (modification.value ?? 0) as number;
            if (operator === 'subtract') {
                stepDelta = stepDelta * -1;
            }

            // 1. EXISTING behavior: mutate character.X — always
            this._applyAttributeLive(character, modification, stepDelta);

            // 2. NEW: mutate protoData.attributes[name].max — ancestry-tier step mods only
            if (source === 'ancestry' && modification.valueType === 'step') {
                this._applyAttributeMax(character, modification, stepDelta);
            }

            // 3. NEW: mutate calculateBase — non-temporary tiers, when slot is registered
            if (source !== 'temporary') {
                this._applyAttributeCalculateBase(character, modification, stepDelta);
            }

            return Promise.resolve();
        },

        _applyAttributeLive(
            character: CharacterSheetInterface,
            modification: SwadeModConfigurationInterface,
            stepDelta: number,
        ): void {
            const element = String(modification.element ?? '');
            const attribute = this.dieRank(
                (character as any)[element],
                (character as any)[element + 'Modifier'],
            );
            if (!attribute.isParsed()) return;
            this.applyStepOrModify(attribute, stepDelta, modification.valueType);
            (character as any)[element] = attribute.getRankAsString();
            (character as any)[element + 'Value'] = attribute.getValue();
            (character as any)[element + 'Modifier'] = attribute.getDieModifier();
        },

        _applyAttributeMax(
            character: CharacterSheetInterface,
            modification: SwadeModConfigurationInterface,
            stepDelta: number,
        ): void {
            const element = String(modification.element ?? '');
            const proto = (character.protoData?.attributes as any)?.[element];
            if (!proto) return;
            const maxRank = this.dieRank(proto.max ?? 'd12');
            this.applyStepOrModify(maxRank, stepDelta, modification.valueType);
            proto.max = maxRank.getDieCode() ?? proto.max;
        },
        async applyDerivedAttributeModification(character: CharacterSheetInterface, modification: SwadeModConfigurationInterface, operator: string) {
            if (!character) return;
            if (!modification) return;

            // Supports add and subtract
            // RE-TYPED, not converted — see modifyProtoAttributes above.
            let valueDelta: number = (modification.value ?? 0) as number;
            if (operator === 'subtract') {
                valueDelta = valueDelta * -1;
            }

            // obj[x] is obj[String(x)]; the key read and written is unchanged. The 'size' test is
            // separate on purpose: Array.includes compares by identity, so it matched only a plain
            // name before, and elementName keeps it that way.
            const attributeKey = String(modification.element ?? "");
            console.debug("CharacterData | applyDerivedAttributeModification | ", character, modification.element, character[attributeKey]);
            console.debug("CharacterData | applyDerivedAttributeModification | ", !character[attributeKey]);
            //if (isNaN(character[modification.element ?? ""])) return;

            let currentValue = character[attributeKey] ?? 0;

            character[attributeKey] = currentValue + valueDelta
            if (!['size'].includes(elementName(modification.element) ?? "") && character[attributeKey] <= 0) {
                character[attributeKey] = null;
            }
            console.debug("CharacterData | applyDerivedAttributeModification | ", character[attributeKey]);

            return Promise.resolve()
        },
        async applyPowerPointModification(character: CharacterSheetInterface, modification: SwadeModConfigurationInterface, operator: string) {
            if (!character) return;
            if (!modification) return;

            // Supports add and subtract
            let valueDelta: number = parseInt(String(modification.value ?? '0'));
            if (operator === 'subtract') {
                valueDelta = valueDelta * -1;
            }

            // Two different questions, kept apart on purpose. The `=== 'default'` test must see the
            // RAW value — an array has never been equal to the string and still is not — while the
            // index key is the string property access already coerced to (obj[x] is obj[String(x)]).
            const requestedType = modification.element ?? 'default';
            let powerPointType: string = String(requestedType);
            if (requestedType === 'default') {
                const availableTypes = Object.keys(character.powerPoints);
                const typeWithPoints = availableTypes.find((key: string) => {
                    return (character.powerPoints[key] ?? 0) > 0
                });

                if (typeWithPoints) {
                    powerPointType = typeWithPoints;
                }
            }

            console.debug("CharacterData | applyPowerPointModification | ", character, modification.element, character.powerPoints[powerPointType]);
            console.debug("CharacterData | applyPowerPointModification | ", character, modification.element, character.powerPoints[powerPointType]);
            console.debug("CharacterData | applyPowerPointModification | ", !character[String(modification.element ?? "")]);
            if (isNaN(character.powerPoints[powerPointType])) return Promise.resolve();

            let currentValue = parseInt(character.powerPoints[powerPointType] ?? '0');
            if (isNaN(currentValue)) {
                currentValue = 0;
            }

            character.powerPoints[powerPointType] = currentValue + valueDelta
            console.debug("CharacterData | applyPowerPointModification | ", character.powerPoints[powerPointType]);

            if (character.powerPoints[powerPointType] <= 0) {
                character.powerPoints[powerPointType] = null;
            }

            return Promise.resolve()
        },

        /**
         * `element` is read through elementUuids, like applySkillModification: a bare uuid, a uuid list
         * or a { uuid, label } chip list name one catalog skill per entry, and each is changed in turn.
         */
        async modifyProtoSkills(character: CharacterSheetInterface, modification: SwadeModConfigurationInterface, operator: string) {
            if (!modification) return Promise.resolve();

            const store = useAppDataStore();
            for (const uuid of elementUuids(modification.element)) {
                const existingProtoSkill = character?.protoData?.skills?.find((item: SwadeSkillRankInterface) => {
                    return item.uuid === uuid;
                })

                // Supports add and subtract
                if (operator === 'add') {
                    if (existingProtoSkill) {
                        let rank = this.dieRank(existingProtoSkill.base, null);
                        if (rank.isParsed()) {
                            if (modification.valueType === 'step') {
                                rank.addSteps(modification.value ?? 0);
                            }

                            this.setProtoSkillBase(existingProtoSkill, rank.getRankAsString() ?? "");
                            existingProtoSkill.rankValue = rank.getValue();
                            existingProtoSkill.modifier = rank.getDieModifier();
                        }
                    }
                    else {
                        const skill: SwadeSkillInterface = await store.retrieveElement({ uuid });
                        let rank = this.dieRank('d4', null);
                        if (rank.isParsed()) {
                            if (Number(modification.value ?? 0) > 1) {
                                rank.addSteps(Number(modification.value ?? 0) - 1)
                            }

                            if (skill) {
                                const newBase = rank.getRankAsString();
                                character?.protoData?.skills?.push({
                                    uuid: skill.uuid,
                                    base: newBase,
                                    rank: newBase,
                                    rankValue: rank.getValue(),
                                    linkedAttribute: skill.linkedAttribute,
                                    modifier: rank.getDieModifier(),
                                    max: this.computeSkillMax(newBase),
                                })
                            }
                        }
                    }
                }
                else if (operator === 'subtract') {
                    if (existingProtoSkill) {
                        let rank = this.dieRank(existingProtoSkill.base, null);
                        if (rank.isParsed()) {
                            if (((rank.getValue() ?? 4) === 4) && (modification.valueType === 'step')) {
                                this.removeProtoSkill(character, existingProtoSkill)
                            }
                            else {
                                if (modification.valueType === 'step') {
                                    rank.subtractSteps(parseInt(String(modification.value ?? 0)));
                                }

                                this.setProtoSkillBase(existingProtoSkill, rank.getRankAsString() ?? null);
                                existingProtoSkill.rank = rank.getRankAsString() ?? "";
                            }
                        }
                    }
                }
            }

            return Promise.resolve();
        },
        removeProtoSkill(character: CharacterSheetInterface, existingProtoSkill: SwadeSkillInterface, rankUuid: string | null = null) {
            if (!character?.protoData?.skills) return;

            character.protoData.skills = character.protoData.skills.filter((item: any) => {
                if (rankUuid && item.rankUuid) {
                    return item.rankUuid !== rankUuid;
                }

                return item.uuid !== existingProtoSkill.uuid;
            })
        },
        /**
         * `element` is read through elementUuids, like the ability, power and edge appliers: a bare uuid,
         * a uuid list or a { uuid, label } chip list name one catalog skill per entry, and each is changed
         * in turn. The API's import writes the chip list; the whole value used to be compared to one
         * skill uuid and handed to retrieveElement as the uuid, so a chip list — even one chip — changed
         * no skill and asked the API for `/elements/[object Object]/one`.
         */
        async applySkillModification(character: CharacterSheetInterface, modification: SwadeModConfigurationInterface, operator: string) {
            if (!modification) return;

            const store = useAppDataStore();
            for (const uuid of elementUuids(modification.element)) {
                const existingSkillRank = character?.skills?.find((item: SwadeSkillRankInterface) => {
                    return item.skill?.uuid === uuid;
                })

                // Supports add and subtract
                if (operator === 'add') {
                    console.debug("CharacterData | applySkillModification | existingSkillRank | ", existingSkillRank)
                    if (existingSkillRank) {
                        let rank = this.dieRank(existingSkillRank.rank, existingSkillRank.modifier);
                        if (rank.isParsed()) {
                            if (modification.valueType === 'step') {
                                rank.addSteps(modification.value ?? 0);
                            }
                            else if (modification.valueType === 'modify') {
                                rank.addModifier(modification.value ?? 0);
                            }
                            console.debug("CharacterData | applySkillModification | modifiedRank | ", rank)

                            existingSkillRank.rank = rank.getRankAsString() ?? "";
                            existingSkillRank.rankValue = rank.getValue();
                            existingSkillRank.modifier = rank.getDieModifier();
                        }
                    }
                    else if (modification.valueType === 'step') {
                        const skill = await store.retrieveElement({ uuid });
                        // A uuid the catalog no longer answers (retrieveElement reports the error and
                        // resolves undefined) is skipped so the rest of the list still lands.
                        if (!skill) continue;
                        await this.addSkillToCharacter(character, skill);

                        const extraSteps = Number(modification.value ?? 1) - 1;
                        if (extraSteps <= 0) continue;

                        const newSkillRank = character?.skills?.find((item: SwadeSkillRankInterface) => {
                            return item.skill?.uuid === uuid;
                        });
                        if (!newSkillRank) continue;

                        const rank = this.dieRank(newSkillRank.rank, newSkillRank.modifier);
                        if (rank.isParsed()) {
                            rank.addSteps(extraSteps);
                            newSkillRank.rank = rank.getRankAsString() ?? "";
                            newSkillRank.rankValue = rank.getValue();
                            newSkillRank.modifier = rank.getDieModifier();
                        }
                    }
                }
                else if (operator === 'subtract') {
                    console.debug("CharacterData | applySkillModification | existingSkillRank | ", existingSkillRank)
                    if (existingSkillRank) {
                        if ((existingSkillRank.rankValue <= 4) && (modification.valueType !== 'modify')) {
                            this.removeSkill(character, existingSkillRank.skill);
                            continue;
                        }

                        let rank = this.dieRank(existingSkillRank.rank, existingSkillRank.modifier);
                        console.debug("CharacterData | applySkillModification | ", rank)
                        if (rank.isParsed()) {
                            if (modification.valueType === 'step') {
                                rank.subtractSteps((modification.value ?? 0));
                            }
                            else if (modification.valueType === 'modify') {
                                rank.subtractModifier((modification.value ?? 0));
                            }
                            console.debug("CharacterData | applySkillModification | modifiedRank | ", rank)

                            if ((rank.getValue() < 4) && (modification.valueType === 'step')) {
                                this.removeSkill(character, existingSkillRank.skill);
                                continue;
                            }

                            existingSkillRank.rank = rank.getRankAsString() ?? "";
                            existingSkillRank.rankValue = rank.getValue();
                            existingSkillRank.modifier = rank.getDieModifier();
                        }

                        console.debug("CharacterData | applySkillModification | ", rank, existingSkillRank)
                    }
                }
            }

            return Promise.resolve();
        },
        /**
         * A container.skills RANK effect (owner, 2026-10-08), applied (`direction` 1) or rolled back (-1) on
         * the skill rank it names, as an edge's skill bonus is: `step` moves the die and, with `updateProto`,
         * the build's base, so a granted step costs no skill points; `modify` moves the flat modifier. The
         * target is the rank a record names, or the granting rank itself in self mode. An authored Specific
         * Choices list applies only through its record, as a trapping does, and All Specified is not
         * supported for a rank: a change made to the matches of one moment cannot be taken back from the
         * matches of another. Never registered in the effects ledger, whose rebuild reads `rank` as a POWER's.
         */
        applySkillRankEffect(
            character: CharacterSheetInterface,
            configuration: SwadeModConfigurationInterface,
            sourceContainerUuid: string | null,
            direction: 1 | -1,
            updateProto: boolean,
        ): void {
            const amount = skillRankAmount(configuration);
            const { targets, mode } = resolveContainerTargets(configuration, character, sourceContainerUuid);
            if (amount === null || (mode !== 'self' && mode !== 'byContainer')) {
                console.debug('CharacterData | applySkillRankEffect | nothing applied', mode, configuration);
                return;
            }

            const change = amount * direction;
            for (const rank of targets as SwadeSkillRankInterface[]) {
                const shifted = shiftSkillRank(rank.rank, rank.modifier, configuration.valueType, change);
                if (!shifted) continue;
                rank.rank = shifted.rank;
                rank.rankValue = shifted.rankValue;
                rank.modifier = shifted.modifier;

                if (!updateProto || configuration.valueType !== 'step') continue;
                const protoSkill = protoSkillFor(character, rank);
                const base = protoSkill ? shiftSkillRank(protoSkill.base, null, 'step', change) : null;
                if (base) this.setProtoSkillBase(protoSkill, base.rank);
            }
        },

        async rollbackModification(
            character: CharacterSheetInterface,
            modification: SwadeModConfigurationInterface,
            updateProto: boolean = true,
            source: ModSource = 'permanent',
            sourceContainerUuid: string | null = null,
        ) {
            console.debug("CharacterData | rollbackModification | ", modification);

            // BEFORE the empty-element guard below. A self-targeted container modification has no
            // authored element by design, so that guard would return early and it would never roll
            // back. This is why the authored element is left intact rather than overwritten with
            // resolved targets.
            if ((modification.path ?? '').startsWith('container.')) {
                if (isSkillRankEffect(modification)) {
                    return this.applySkillRankEffect(character, modification, sourceContainerUuid, -1, updateProto);
                }
                // Narrowed to the one modification being rolled back. registerContainerEffect keys
                // effects per-modification; an unnarrowed (source-wide) deregister here would also
                // destroy a sibling modification's still-active effect from the same source.
                return this.deregisterContainerEffects(
                    character,
                    sourceContainerUuid ?? '',
                    deriveContainerModKey(modification),
                );
            }

            // effective.* rows are rebuilt, never rolled back — see applyModification.
            if (isEffectivePath(modification.path)) return;

            if (isElementEmpty(modification.element)) return;

            if (modification.mod_type === 'buildOption') {
                return this.rollbackCharacterBuildOption(character, modification);
            }

            if (modification.path === 'character.edges') {
                let reverseAction = 'subtract';
                if (modification.operator === 'subtract') {
                    reverseAction = 'add';
                }

                if (updateProto) {
                    await this.modifyProtoEdges(character, modification, reverseAction);
                }

                return this.applyEdgeModification(character, modification, reverseAction);
            }
            else if (modification.path === 'character.skills') {
                let reverseAction = 'subtract';
                if (modification.operator === 'subtract') {
                    reverseAction = 'add';
                }

                if (updateProto) {
                    await this.modifyProtoSkills(character, modification, reverseAction);
                }

                return this.applySkillModification(character, modification, reverseAction)
            }
            else if (modification.path === 'character.attributes') {
                let reverseAction = 'subtract';
                if (modification.operator === 'subtract') {
                    reverseAction = 'add';
                }

                if (updateProto) {
                    await this.modifyProtoAttributes(character, modification, reverseAction);
                }

                return this.applyAttributeModification(character, modification, reverseAction, source)
            }
            else if (modification.path === 'character.abilities') {
                let reverseAction = 'subtract';
                if (modification.operator === 'subtract') {
                    reverseAction = 'add';
                }

                return this.applyAbilityModification(character, modification, reverseAction);
            }
            else if (modification.path === 'character.languages') {
                // After the isElementEmpty guard above: a null or empty list is inert both ways.
                return this.applyLanguageModification(character, modification, modification.operator === 'subtract' ? 'add' : 'subtract');
            }
            else if (modification.path === 'character.equipment') {
                let reverseAction = 'subtract';
                if (modification.operator === 'subtract') {
                    reverseAction = 'add';
                }

                return this.applyEquipmentModification(character, modification, reverseAction);
            }
            else if (modification.path === 'character.powers') {
                let reverseAction = 'subtract';
                if (modification.operator === 'subtract') {
                    reverseAction = 'add';
                }

                return this.applyPowersModification(character, modification, reverseAction);
            }
            else if (modification.path === 'character.hindrances') {
                let reverseAction = 'subtract';
                if (modification.operator === 'subtract') {
                    reverseAction = 'add';
                }

                console.debug("CharacterData | rollbackModification | ", reverseAction);
                if (updateProto) {
                    await this.modifyProtoHindrances(character, modification, reverseAction);
                }

                return this.applyHindranceModification(character, modification, reverseAction);
            }
            else if (modification.path === 'character.powerPoints') {
                let reverseAction = 'subtract';
                if (modification.operator === 'subtract') {
                    reverseAction = 'add';
                }

                return this.applyPowerPointModification(character, modification, reverseAction)
            }
        },

        async initializeElementChoices(alias: string, url: string|null, filters?: Array<FilterDescriptor>, displayLoadingIndicator: boolean = true, limit: number | null = null) {
            console.info("CharacterData | initializeElementChoices | ", alias);
            const store = useAppDataStore();
            if (!store.system) return;

            if (!url) {
                url = '/systems/'+ store.system?.uuid +'/'+ alias;
                if (filters && (filters?.length ?? 0) > 0) {
                    let compiled = this.filtersToQueryString(filters ?? []);
                    url = url + '?' + compiled;
                }
                // Only a url built here takes the page size. A given url is a page link from the API,
                // which already carries the limit of the request that produced it (spec 2026-10-16).
                url = withPageSize(url, limit);
            }

            // The app-wide spinner is raised only when asked for, and lowered however the request
            // ends. A request that asks for none leaves alone one that something else raised.
            if (displayLoadingIndicator) await store.setLoading(true);
            return api.get(url)
                .then((response) => {
                    console.debug("CharacterData | initializeElementChoices | ", response);
                    let results = [];
                    if (Array.isArray(response.data?.results)) {
                        results = response.data?.results.sort((element1: SettingElementInterface, element2: SettingElementInterface) => {
                            if ((element1.name ?? "") > (element2.name ?? "")) {
                                return 1;
                            }
                            else if ((element1.name ?? "") < (element2.name ?? "")) {
                                return -1;
                            }

                            return 0;
                        }) ?? []
                    }
                    else if (Array.isArray(response.data)) {
                        results = response.data.sort((element1: SettingElementInterface, element2: SettingElementInterface) => {
                            if ((element1.name ?? "") > (element2.name ?? "")) {
                                return 1;
                            }
                            else if ((element1.name ?? "") < (element2.name ?? "")) {
                                return -1;
                            }

                            return 0;
                        }) ?? []
                    }

                    return Promise.resolve({ metadata: response.data.metadata, links: response.data.links, results: results });
                })
                .then((data: any) => {
                    console.debug("CharacterData | initializeElementChoices | ", alias);
                    // Every alias maps to an identically-named state array
                    // (abilities, skills, advantages, disadvantages, weapons,
                    // armor, items, powers, frameworks, tables). Assign generically.
                    if (Array.isArray((this.$state as any)[alias])) {
                        (this as any)[alias] = data.results;
                    }

                    console.debug("CharacterData | initializeElementChoices | ", this.$state[alias]);
                    return data;
                })
                .catch((error) => { handleError(error); })
                .finally(() => {
                    if (displayLoadingIndicator) store.setLoading(false);
                });
        },
        initializeSkillChoices(filters?: Array<FilterDescriptor>) {
            return this.initializeElementChoices('skills', null, filters);
        },
        initializeHindranceChoices(filters?: Array<FilterDescriptor>) {
            return this.initializeElementChoices('disadvantages', null, filters);
        },
        initializeEdgeChoices(filters?: Array<FilterDescriptor>) {
            return this.initializeElementChoices('advantages', null, filters);
        },
        initializeWeaponChoices(filters?: Array<FilterDescriptor>) {
            return this.initializeElementChoices('weapons', null, filters);
        },
        initializeArmorChoices(filters?: Array<FilterDescriptor>) {
            return this.initializeElementChoices('armor', null, filters);
        },
        initializeEquipmentChoices(filters?: Array<FilterDescriptor>) {
            return this.initializeElementChoices('items', null, filters);
        },
        initializeFrameworkChoices(filters?: Array<FilterDescriptor>) {
            return this.initializeElementChoices('frameworks', null, filters);
        },

        cancelNewCharacterContext() {
            const store = useAppDataStore();
            console.debug("Dialog Options: ", store.props.dialogOptions);

            store.props.edit = false;

            return this.recalculateDerivedStats();
        },
        async loadCharacterContext(data: CharacterSheetInterface | any) {
            console.info("method: loadCharacterContext");
            console.debug("loadCharacterContext | ", data);
            if (!data) return;

            this.resetBuildValidation();
            const store = useAppDataStore();

            return api.get('/characters/'+ data.uuid, )
                .then((response) => {
                    //console.debug(response);
                    store.$patch({
                        character: response.data,
                        props: {
                            edit: false,
                        },
                    });

                    return store.character;
                })
                .then((character) => {
                    this.initializeSkillChoices();
                    this.initializeHindranceChoices();
                    this.initializeEdgeChoices();
                    this.initializeWeaponChoices();
                    this.initializeArmorChoices();
                    this.initializeEquipmentChoices();

                    return character;
                })
                .catch((error) => { handleError(error); })
            ;
        },

        hydrateProtoAndCalculateBase(character: CharacterSheetInterface): void {
            if (!character) return;

            // Backfill attribute max for legacy characters
            const protoAttrs = character.protoData?.attributes;
            if (protoAttrs) {
                for (const name of ['agility', 'smarts', 'spirit', 'strength', 'vigor'] as const) {
                    const attr = (protoAttrs as any)[name];
                    if (attr && !attr.max) attr.max = 'd12';
                }
            }

            // Backfill skill max from base
            if (Array.isArray(character.protoData?.skills)) {
                for (const skill of character.protoData.skills) {
                    if (!skill.max) skill.max = this.computeSkillMax(skill.base);
                }
            }

            // Seed calculateBase.strain from live vigor/spirit when missing and flag enabled.
            // Only applies to characters with populated protoData attributes (real legacy chars);
            // skips minimal fixtures or characters without protoData attribute objects.
            const appData = useAppDataStore();
            const hasProtoAttrs = !!(protoAttrs && ((protoAttrs as any).vigor || (protoAttrs as any).spirit));
            if (appData.world?.flags?.strain === true && hasProtoAttrs) {
                character.additionalData ??= {};
                (character.additionalData as any).calculateBase ??= {};
                if (!(character.additionalData as any).calculateBase.strain) {
                    (character.additionalData as any).calculateBase.strain = {
                        vigor: character.vigor ?? 'd4',
                        spirit: character.spirit ?? 'd4',
                    };
                }
            }
        },

        async recalculateDerivedStats(character: CharacterSheetInterface) {
            if (!character) return;

            this.hydrateProtoAndCalculateBase(character);
            this.recalculateEffectiveAttributes(character);

            console.info("CharacterData | recalculateDerivedStats")
            this.calculateAgilityValue(character);
            this.calculateSmartsValue(character);
            this.calculateSpiritValue(character);
            this.calculateStrengthValue(character);
            this.calculateVigorValue(character);

            this.calculateToughness(character);
            this.calculateParry(character);
            this.calculateStrain(character);

            return Promise.resolve(character);
        },
        calculateStrain(character: CharacterSheetInterface) {
            if (!character) return;
            // Only worlds that enable the Strain rule track this stat.
            // World flags live on the full world detail in the app-data store, NOT on
            // character.world (which is a slim reference without flags/defaults).
            const appData = useAppDataStore();
            if (appData.world?.flags?.strain !== true) return;
            // Max Strain Limit is half of the lower of Vigor or Spirit (SWADE SFC),
            // plus any character.attributes / strain modifications sourced from abilities,
            // powers, ancestry grants, or in-use equipment. Mods are summed from sources
            // every recalc — applyDerivedAttributeModification's imperative write to
            // character.strain would otherwise be wiped here.
            character.strain = this.defaultStrain(character) + this.strainModificationsTotal(character);
        },
        /**
         * Every modification on the character whose configuration satisfies `matches`, with how many
         * times it is in effect. ONE walk for every derived-stat rebuild (spec 2026-09-18 §5.1): the
         * strain, flat-toughness, flat-armor and derived-toughness readers all filter this, so a
         * source added here reaches all of them and none can drift on coverage again.
         *
         * Sources, in order, each read from the field it actually carries: powers
         * (configuration.modifications), abilities (modifications — an ability is pushed onto the
         * sheet as the bare element, so that is where its list lives), edges (selection.modifications),
         * hindrances (hindrance.modifications), the framework, each ancestry grant and the ancestry's
         * own list (modifications, inline with no wrapping container), in-use equipment
         * (selection.modifications, once per multiplierForContainer), the carried-payload ledger
         * (spec 2026-09-10 §3.4: once per count) and the applied choice records in
         * additionalData.choices (once per choiceSourceUnits of the record's source).
         */
        *iterateModifications(
            character: CharacterSheetInterface,
            matches: (configuration: SwadeModConfigurationInterface) => boolean,
        ): IterableIterator<{ configuration: SwadeModConfigurationInterface, multiplier: number }> {
            const yieldMods = function* (list: unknown, multiplier: number) {
                if (!Array.isArray(list)) return;
                for (const mod of list) {
                    const configuration = (mod as SwadeModificationInterface | null)?.configuration;
                    if (configuration && matches(configuration)) yield { configuration, multiplier };
                }
            };

            // A legacy power (schema 1) keeps walking its copies once; a schema-2 power walks its
            // direct grants once per level (spec 2026-09-21 §5.3 item 2), and its offered sets never;
            // a target grant is walked through its records, not here.
            if (Array.isArray(character?.powers)) {
                for (const p of character.powers) {
                    if (isLegacyPowerContainer(p as any)) yield* yieldMods((p as any)?.configuration?.modifications, 1);
                    else yield* yieldMods(fixedGrants(p as any), grantMultiplier(p as any));
                }
            }
            if (Array.isArray(character?.abilities)) for (const a of character.abilities) yield* yieldMods((a as any)?.modifications, 1);
            if (Array.isArray(character?.edges)) for (const e of character.edges) yield* yieldMods((e as any)?.selection?.modifications, 1);
            if (Array.isArray(character?.hindrances)) for (const h of character.hindrances) yield* yieldMods((h as any)?.hindrance?.modifications, 1);
            if (character?.framework) yield* yieldMods((character.framework as any)?.modifications, 1);
            if (Array.isArray(character?.ancestry?.grants)) for (const g of character.ancestry.grants) yield* yieldMods((g as any)?.modifications, 1);
            // The ancestry's OWN list (applyAncestry applies it beside the grants; the build-point
            // walkers read it). An author retargeting Toughness on the ancestry itself lands here.
            if (character?.ancestry) yield* yieldMods((character.ancestry as any)?.modifications, 1);
            if (Array.isArray(character?.equipment)) {
                for (const container of character.equipment) {
                    if (!container || !this.isItemInUse(container)) continue;
                    yield* yieldMods((container as any)?.selection?.modifications, this.multiplierForContainer(container));
                }
            }
            for (const row of carriedRows(character?.additionalData?.carriedPayloads)) {
                if (matches(row)) yield { configuration: row, multiplier: row.multiplier };
            }
            // Applied choice records (additionalData.choices): the option a player picked from a
            // choice set, in effect as many times as its source is — containerUnits for a container
            // source; once for the world, a local setting, the ancestry, the framework or an ability.
            // cleanupOrphanedChoices prunes records whose source is gone; a record whose source is
            // present but not in use (a stored implant) is kept for re-use and counts 0 here.
            if (Array.isArray(character?.additionalData?.choices)) {
                for (const record of character.additionalData.choices) {
                    const configuration = (record as AppliedConfiguration | null)?.configuration;
                    if (!configuration || !matches(configuration)) continue;
                    const multiplier = this.choiceSourceUnits(character, (record as AppliedConfiguration).uuid);
                    if (multiplier > 0) yield { configuration, multiplier };
                }
            }
        },
        /**
         * How many times a choice record's SOURCE is in effect (spec 2026-09-18 §5.1, ruling
         * 2026-09-18): containerUnits for a container source — an in-use equipment container's
         * multiplier, else 0; 1 while a skill, edge, hindrance or power is present — and 1 for the
         * non-container sources cleanupOrphanedChoices keeps live: the world, a character-local
         * setting, the ancestry, the framework, an ability. 0 for anything else, so an orphan a
         * cleanup has not reached yet is never counted.
         */
        choiceSourceUnits(character: any, sourceUuid: unknown): number {
            if (typeof sourceUuid !== 'string' || sourceUuid.length === 0) return 0;
            for (const arrayName of ['equipment', 'skills', 'edges', 'hindrances', 'powers']) {
                const list = character?.[arrayName];
                if (!Array.isArray(list)) continue;
                const container = list.find((c: any) => c?.uuid === sourceUuid);
                if (container) return this.containerUnits(character, container);
            }
            const singletons = [
                character?.world?.uuid,
                character?.additionalData?.localSetting?.uuid,
                character?.ancestry?.uuid,
                character?.framework?.uuid,
            ];
            if (singletons.includes(sourceUuid)) return 1;
            if (Array.isArray(character?.abilities) && character.abilities.some((a: any) => a?.uuid === sourceUuid)) return 1;
            return 0;
        },
        *iterateStrainModifications(
            character: CharacterSheetInterface,
        ): IterableIterator<{ configuration: SwadeModConfigurationInterface, multiplier: number }> {
            yield* this.iterateModifications(character, (configuration: SwadeModConfigurationInterface) =>
                configuration.path === 'character.attributes' && configuration.element === 'strain');
        },
        strainAlterOverride(character: CharacterSheetInterface): number | null {
            let result: number | null = null;
            for (const { configuration } of this.iterateStrainModifications(character)) {
                console.debug("strainAlterOverride", configuration.operator, configuration.value)
                if (configuration.operator !== 'alter') continue;
                if (!Number.isFinite(configuration.value as number)) continue;
                const v = configuration.value as number;
                result = (result === null) ? v : Math.min(result, v);
            }

            console.debug("strainAlterOverride", result)
            return result;
        },
        strainModificationsTotal(character: CharacterSheetInterface): number {
            let total = 0;
            for (const { configuration, multiplier } of this.iterateStrainModifications(character)) {
                if (configuration.operator === 'alter') continue;   // override layer consumes these
                if (!Number.isInteger(configuration.value)) continue;
                const value = configuration.value as number;
                const signed = configuration.operator === 'subtract' ? -value : value;
                total += signed * multiplier;
            }
            return total;
        },
        async recalculateCharacterPoints(character: CharacterSheetInterface, systemDefaults: SwadeSystemDefaultsInterface | null, world?: WorldInterface) {
            if (!character) return;

            console.info("CharacterData | recalculateCharacterPoints")
            console.debug("CharacterData | recalculateCharacterPoints | ", character, systemDefaults, world);

            const mergedDefaults = this.combineSettingRules(
                systemDefaults,
                world
            );

            console.debug("CharacterData | recalculateCharacterPoints | ", mergedDefaults);
            this.build.attributes.total = mergedDefaults?.attributes.creationPoints ?? 0;
            this.build.traits.total = mergedDefaults?.skills.creationPoints ?? 0;

            this.updateBuildValidation(character, mergedDefaults);
        },
        async recalculateTotalPerkPoints(character: CharacterSheetInterface) {
            if (!character) return;

            console.info("CharacterData | recalculateTotalPerkPoints")
            let perkPointsReceived = 0;
            if (character?.ancestry === null) {
                // Null value assumes humans
                perkPointsReceived = perkPointsReceived + 2;
            }
            
            character?.hindrances?.forEach(function (item: SwadeDrawbackInterface) {
                if (resolveDrawbackType(item) === 'major') {
                    perkPointsReceived = perkPointsReceived + 2;
                } else {
                    perkPointsReceived = perkPointsReceived + 1;
                }
            });

            // perkPointModifier is deliberately ABSENT here: perkPoints is a ceiling and
            // belongs only to build.perkPoints.max. Only grants reach the total. The world's
            // grant comes from store state because this function receives no settingRules,
            // and buildOptions is null after resetBuildValidation.
            perkPointsReceived = perkPointsReceived
                + (this.buildOptions?.hindrances?.extraPoints ?? 0)
                + this.characterBuildPointModifier(character, 'extraPerkPoints');

            let protoPerkPointTotal = 0;
            character.protoData?.hindrances?.forEach((item: any) => {
                protoPerkPointTotal = protoPerkPointTotal + parseInt(item.value);
            })

            console.debug("CharacterData | recalculateTotalPerkPoints | received / proto", perkPointsReceived, protoPerkPointTotal)
            let finalTotal = perkPointsReceived - protoPerkPointTotal;
            if (finalTotal > this.build.perkPoints.max) {
                finalTotal = this.build.perkPoints.max;
            }

            this.build.perkPoints.total = finalTotal;

            // The perk validation flags (overAllocated/fullyAllocated/unspentPoints) are
            // derived from `total`, but they're stored — set by updateAllocatedPerkPoints,
            // a separate pass. Callers that run that pass BEFORE this one (e.g. the
            // re-entry path in CharacterCreatorView.onCharacterUpdated) would otherwise be
            // left with flags computed against the stale default total. Refresh them here
            // so `total` and its derived flags can never drift apart, whatever the order.
            // Guard on protoData: updateAllocatedPerkPoints assumes it exists (updateBuildStatus
            // normally creates it via updateProtoAttributes first); this path has no such guarantee.
            if (character.protoData) {
                this.updateAllocatedPerkPoints(character);
            }

            return Promise.resolve(character);
        },

        removeDrawback(character: CharacterSheetInterface, item: SwadeDrawbackInterface) {
            if (!item?.uuid) return;

            const store = useAppDataStore();
            if (!character) return;

            return store.retrieveElement(item.hindrance)
                .then(async (hindrance: SwadeHindranceInterface) => {
                    hindrance?.modifications?.forEach((modification: SwadeModificationInterface) => {
                        if (modification.configuration.path === 'character.choiceSet') return;
                        this.rollbackModification(character, modification.configuration, true, 'permanent', item.uuid);
                    });
                    await this.rollbackAppliedChoicesFor(character, item.uuid);
                })
                .then(async () => {
                    if (!character) return;

                    character.hindrances = character?.hindrances?.filter((value: SwadeDrawbackInterface) => {
                        return value.uuid !== item.uuid;
                    });

                    await this.syncCarriedPayloads(character);
                    this.sortHindranceList(character);
                    this.recalculateDerivedStats(character);
                    this.recalculateTotalPerkPoints(character);
                })
                .catch((error) => { handleError(error) })
        },
        removeEdge(character: CharacterSheetInterface, container: SwadeEdgeContainerInterface) {
            if (!container?.selection?.uuid) return;

            const store = useAppDataStore();
            if (!character) return;

            return store.retrieveElement(container.selection)
                .then(async (edge: SwadeEdgeInterface) => {
                    edge?.modifications?.forEach(async (modification: SwadeModificationInterface) => {
                        if (modification.configuration.path === 'character.choiceSet') return;
                        await this.rollbackModification(character, modification.configuration, true, 'permanent', container.uuid ?? '');
                    });
                    await this.rollbackAppliedChoicesFor(character, container.uuid);

                    return edge;
                })
                .then(async (edge) => {
                    if (!character) return;

                    if (container.uuid) {
                        character.edges = character?.edges?.filter((element: SwadeEdgeContainerInterface) => {
                            return element.uuid !== container.uuid;
                        });
                    } else {
                        character.edges = character?.edges?.filter((element: SwadeEdgeContainerInterface) => {
                            return element !== container;
                        });
                    }

                    await this.syncCarriedPayloads(character);
                    this.removeProtoEdge(character, edge, container.uuid);

                    this.sortEdgeList(character);
                    this.recalculateDerivedStats(character);
                })
                .catch((error) => { handleError(error) })
        },
        async removePowerOrAbility(character: CharacterSheetInterface, item: SwadePowerInterface|SwadeAbilityInterface) {
            console.info("CharacterData | removePowerOrAbility");
            console.debug("CharacterData | removePowerOrAbility | ", item);
            if (!item?.uuid) return;

            console.debug("CharacterData | removePowerOrAbility | ", character);
            if (!character) return;

            if (!Array.isArray(character?.powers) && !Array.isArray(character?.abilities)) return;

            // Roll back the ability's own non-choiceSet modifications BEFORE removing the
            // entry. applyAbilityModification's add path uses withChoiceResolution which
            // applies these mods (e.g. Inhuman Appearance's Persuasion −1); removal must
            // symmetrically reverse them. choiceSet resolutions are recorded under
            // additionalData.choices and undone by rollbackAppliedChoicesFor below.
            // (applyPowersModification's add path does NOT walk power.selection.modifications,
            // so we don't need to roll those back here.)
            const grantedAbility = (character.abilities ?? []).find((a: any) => a.uuid === item.uuid);
            if (grantedAbility && Array.isArray((grantedAbility as any).modifications)) {
                for (const mod of (grantedAbility as any).modifications) {
                    if (mod?.configuration?.path === 'character.choiceSet') continue;
                    await this.rollbackModification(character, mod.configuration, true, 'permanent', item.uuid);
                }
            }

            await this.rollbackAppliedChoicesFor(character, item.uuid);

            character.powers = character.powers.filter((element: SwadePowerContainerInterface) => {
                return element.selection?.uuid !== item.uuid;
            });

            character.abilities = character.abilities.filter((element: SwadeAbilityInterface) => {
                return element.uuid !== item.uuid;
            });
            console.debug("CharacterData | removePowerOrAbility | ", character.powers.length, character.abilities.length);

            await this.syncCarriedPayloads(character);
            return this.recalculateDerivedStats(character);
        },
        // Two branches, and they compare differently on purpose. With a rankUuid the rank is
        // identified by its own uuid — unambiguous, and the branch the setting configurator uses.
        // Without one the rank is identified by OBJECT IDENTITY, which is what lets the two
        // in-app callers remove one specialization out of several sharing a skill.uuid; a uuid
        // comparison there would delete all of them.
        //
        // The hazard is the mismatch: removeProtoSkill filters by uuid either way, so handing this
        // a freshly-retrieved element (never the same object as the stored shallow copy) used to
        // leave the rank in place and delete its proto record — a phantom skill that
        // updateAllocatedSkillPoints stops costing. Only touch protoData if a rank actually went.
        removeSkill(character: CharacterSheetInterface, item: SwadeSkillInterface, rankUuid: string | null = null) {
            if (!item?.uuid) return;

            if (!Array.isArray(character?.skills)) return;

            const before = character.skills.length;

            character.skills = character.skills.filter((value: SwadeSkillRankInterface) => {
                if (!rankUuid) {
                    return value.skill !== item;
                }

                return value.uuid !== rankUuid;
            })

            if (character.skills.length !== before) {
                this.removeProtoSkill(character, item, rankUuid);
            }

            this.sortSkillList(character);
            return this.syncCarriedPayloads(character).then(() => this.recalculateDerivedStats(character));
        },
        async removeItem(character: CharacterSheetInterface, container: SwadeItemContainerInterface) {
            if (!container) return;
            if (!Array.isArray(character?.equipment)) return;

            // Only roll back modifications if currently in-use; out-of-use containers had their
            // modifications rolled back when status flipped via applyItemStatusChange.
            if (this.isItemInUse(container)) {
                const multiplier = Math.max(1, this.multiplierForContainer(container));
                await this._ticksContainerMods(character, container, multiplier, 'rollback');
            }
            // Strip recorded choices regardless of in-use status. Uninstalled cyberware can hold
            // a resolved-at-add choice that was never applied; leaving it behind would orphan it.
            if (container.uuid) this._stripRecordedChoicesFor(character, container.uuid);
            if (container.uuid) this._stripGrantedEquipmentFor(character, container.uuid);

            // Splice in place so any computed reading `character.equipment` (e.g. Strain.vue's
            // currentStrain) reactively re-evaluates. `character.equipment = newArray` would
            // produce the right data but doesn't reliably re-trigger consumer computeds when
            // the value flows through a `defineModel`-bound prop chain.
            const idx = character.equipment.indexOf(container);
            if (idx >= 0) character.equipment.splice(idx, 1);

            await this.syncCarriedPayloads(character);
            this.sortEquipmentList(character);
            return this.recalculateDerivedStats(character);
        },

        sortSkillList(character: CharacterSheetInterface) {
            console.info("CharacterData | sortSkillList");
            if (!Array.isArray(character?.skills)) return;

            // By the string the sheet renders — chosen labels included — not the bare composed name.
            character.skills = character.skills.sort(compareContainersBy((c) => containerDisplayNameFor(character, c)));
        },
        sortEquipmentList(character: CharacterSheetInterface) {
            console.info("CharacterData | sortEquipmentList");
            if (!Array.isArray(character?.equipment)) return;

            character.equipment.sort(compareContainersByName);
        },
        sortEdgeList(character: CharacterSheetInterface) {
            console.info("CharacterData | sortEdgeList");
            if (!character?.edges) return;

            character.edges.sort(compareContainersBy((c) => containerDisplayNameFor(character, c)));
        },
        sortPowersList(character: CharacterSheetInterface) {
            console.info("CharacterData | sortPowersList");
            if (!Array.isArray(character?.powers)) return;

            character.powers.sort((item1: SwadePowerContainerInterface, item2: SwadePowerContainerInterface) => {
                if (containerDisplayName(item1) === containerDisplayName(item2)) {
                    return 0;
                }

                return (containerDisplayName(item1) > containerDisplayName(item2))
                    ? 1
                    : -1;
            });
        },
        sortHindranceList(character: CharacterSheetInterface) {
            console.info("CharacterData | sortHindranceList");
            if (!character?.hindrances) return;

            character.hindrances.sort(compareContainersBy((c) => containerDisplayNameFor(character, c)));
        },

        newCharacter(config?: SwadeCharacterInterface): SwadeCharacterInterface {
            const store = useAppDataStore();

            const character = {
                uuid: null,
                protoData: this.newProtoData(),
                system: config?.system ?? store.system ?? null,
                // A synthetic world is a character-local setting's carrier, not a real world, and
                // _buildCharacterPayload would POST it as a dangling { uuid } foreign key. The slot
                // is not cleared when the editor unmounts (see the setting configurator spec §3.2),
                // so this guard is the only thing standing between the configurator and a bad POST.
                world: config?.world
                    ?? (isSyntheticWorldUuid(store.world?.uuid) ? null : store.world)
                    ?? null,
                configuration: null,
                name: config?.name ?? null,
                summary: null,
                portrait: null,
                token: null,
                description: null,
                ancestry: null,
                agility: 'd4',
                agilityValue: 4,
                agilityModifier: null,
                smarts: 'd4',
                smartsValue: 4,
                smartsModifier: null,
                spirit: 'd4',
                spiritValue: 4,
                spiritModifier: null,
                strength: 'd4',
                strengthValue: 4,
                strengthModifier: null,
                vigor: 'd4',
                vigorValue: 4,
                vigorModifier: null,
                runningDie: 'd6',
                runningDieValue: 6,
                purchasingPower: null,
                wounds: 3,
                pace: 6,
                parry: 2,
                size: 0,
                toughness: 4,
                armor: null,
                powerPoints: {
                    default: 0,
                    magic: 0,
                    miracles: 0,
                    gifted: 0,
                    psionics: 0,
                    weirdScience: 0,
                    ppe: 0,
                    isp: 0,
                },
                abilities: [],
                hindrances: [],
                edges: [],
                skills: [],
                equipment: [],
                powers: [],
            } as SwadeCharacterInterface;

            // World flags live on the app-data store's full world detail, not on
            // character.world (which is a slim reference). See [[architecture-world-slim-vs-full]].
            if (store.world?.flags?.strain === true) {
                character.strain = this.defaultStrain(character);
                character.additionalData ??= {};
                (character.additionalData as any).calculateBase ??= {};
                (character.additionalData as any).calculateBase.strain = {
                    vigor: character.vigor ?? 'd4',
                    spirit: character.spirit ?? 'd4',
                };
            }

            return character;
        },
        newModification(type: string = 'character'): SwadeModConfigurationInterface {
            return {
                label: null,
                summary: null,
                path: null,
                operator: null,
                value: null,
                valueType: null,
                element: null,
                mod_type: type,
            }
        },
        newProtoData(): SwadeCharacterPrototypeInterface {
            return {
                locked: false,
                attributes: {
                    agility:  { base: 'd4', novice: 'd4', max: 'd12' },
                    smarts:   { base: 'd4', novice: 'd4', max: 'd12' },
                    spirit:   { base: 'd4', novice: 'd4', max: 'd12' },
                    strength: { base: 'd4', novice: 'd4', max: 'd12' },
                    vigor:    { base: 'd4', novice: 'd4', max: 'd12' },
                },
                skills: [],
                languages: [],
                edges: [],
                hindrances: [],
                perkPointAllocation: [],
                powers: [],
                powerSets: [],
            }
        },
        newAbility(type: string, system: SystemInterface, position: number = 0): SwadeAbilityInterface|null {
            console.debug(this.system)
            if (!system?.uuid) return null;

            return {
                uuid: "",
                name: "",
                system: { uuid: system?.uuid, name: system?.name },
                modifications: [],
                active: true,
                position: position,
                type: type,
                summary: null,
                description: null,
                grants: [],
            }
        },
        newAncestry(system: SystemInterface): SwadeAncestryInterface|null {
            if (!system?.uuid) return null;

            return {
                uuid: "",
                name: "",
                system: { uuid: system?.uuid, name: system?.name },
                modifications: [],
                active: true,
                summary: null,
                description: null,
                grants: [],
            }
        },
        newFramework(system: SystemInterface): SwadeCharacterFrameworkInterface|null {
            console.debug(system)
            if (!system?.uuid) return null;

            return {
                uuid: "",
                name: "",
                system: { uuid: system?.uuid, name: system?.name },
                modifications: [],
                active: true,
                requirements: null,
                summary: null,
                description: null,
            }
        },
        newAdvantage(system: SystemInterface): SwadeEdgeInterface|null {
            console.debug(system)
            if (!system?.uuid) return null;

            return {
                uuid: "",
                name: "",
                system: { uuid: system?.uuid, name: system?.name },
                modifications: [],
                active: true,
                type: null,
                summary: null,
                description: null,
            }
        },
        newDisadvantage(system: SystemInterface): SwadeHindranceInterface|null {
            console.debug(system)
            if (!system?.uuid) return null;

            return {
                uuid: "",
                name: "",
                system: { uuid: system?.uuid, name: system?.name },
                modifications: [],
                active: true,
                type: null,
                summary: null,
                description: null,
            }
        },
        newSkill(system: SystemInterface): SwadeSkillInterface|null {
            console.debug(system)
            if (!system?.uuid) return null;

            return {
                uuid: "",
                name: "",
                system: { uuid: system?.uuid, name: system?.name },
                modifications: [],
                active: true,
                linkedAttribute: null,
                derivative: null,
                summary: null,
                description: null,
            }
        },
        newItem(type: string, system: SystemInterface): SwadeItemInterface|null {
            console.debug(system)
            if (!system?.uuid) return null;

            return {
                uuid: "",
                name: "",
                system: { uuid: system?.uuid, name: system?.name },
                modifications: [],
                active: true,
                type: type,
                description: null,
                grants: [],
                cost: null,
                details: {
                    armor: type === 'armor' ? null : undefined,
                    toughness:  type === 'armor' ? null : undefined,
                    range:  type === 'weapon' ? null : undefined,
                    damage: type === 'weapon' ? null : undefined,
                    rateOfFire: type === 'weapon' ? null : undefined,
                    ap: type === 'weapon' ? null : undefined,
                    minStr: ['weapon','armor'].includes(type) ? null : undefined,
                    notes: null,
                    weight: null,
                }
            }
        },
        /**
         * FLAG (Runtime finding): every sibling factory seeds `uuid: ""` and the element's own
         * collection fields; this one seeds neither `uuid` nor `modifiers`. The return type states
         * that rather than claiming a complete power — filling the keys in would change what the
         * editor POSTs. Both callers already tolerate it (PowerList casts to any; Details.vue's
         * local is unused).
         */
        newPower(system: SystemInterface): Omit<SwadePowerInterface, 'uuid' | 'modifiers'>|null {
            console.debug(system)
            if (!system?.uuid) return null;

            return {
                name: "",
                type: 'standard',
                system: { uuid: system?.uuid, name: system?.name },
                modifications: [],
                active: true,
                summary: null,
                description: null,
                grants: [],
                details: {
                    rank: null,
                    powerPoints: null,
                    range: null,
                    duration: null
                }
            }
        },
        newPowerContainer(character: CharacterSheetInterface, power: SwadePowerInterface|null): SwadePowerContainerInterface|null {
            if (!power) return null;

            // `<T>` named a type parameter that does not exist here; deepCopyObject infers it.
            // `levels` belongs to the superpower member of the detail union — a core power's detail
            // simply has no such key, so the `?.` chain already yielded undefined for one. Asserted
            // rather than narrowed with `in` so the emitted JavaScript is unchanged.
            //
            // `?.[0]` replaces `?.at(0)`: identical for index 0 (Array.prototype.at(0) returns
            // this[0], undefined on an empty array either way), and the TS lib this project targets
            // predates `at`, so the now-typed array rejects the call. Raising the project's `lib` is
            // out of scope for a per-file burn-down.
            //
            // FLAG (Runtime finding): the seed is dereferenced unguarded below (`defaultLevel.range`)
            // while `numericPowerPoints` spells `defaultLevel?.powerPoints`, so a power with null
            // `details` throws here. Typed as non-null to preserve that, not to hide it.
            const defaultLevel = deepCopyObject(
                (power.details as SwadeSuperPowerDetailInterface | null)?.levels?.[0] ?? power.details,
            ) as SwadePowerLevelInterface;
            // Power point costs can be textual ("Special", "1/target"); coercing those to
            // a number yields NaN, which survives every ?? display fallback. Both point
            // fields must stay numeric-or-null for the backend; displays fall back to
            // selection.details.powerPoints for the text.
            const numericPowerPoints = Number(defaultLevel?.powerPoints ?? 0);
            const hasNumericPowerPoints = Number.isFinite(numericPowerPoints);

            return {
                // Client-generated, exactly as newItemContainer does it. A power container is a
                // modification SOURCE (registerContainerEffect keys the ledger on the granting
                // container's uuid and refuses a blank one), so without this every super-power
                // `container.*` modification was silently dropped at Details.vue's dispatch.
                // Safe against the backend: `uuid` is in the power:container:write group and
                // PowerContainerDenormalizer returns null for an unknown uuid, so the entity is
                // created and setUuid overwrites the server-generated one — the same round trip
                // ItemContainer already relies on.
                uuid: uuid(),
                selection: power,
                name: null,
                trappings: null,
                basePowerPoints: hasNumericPowerPoints ? numericPowerPoints : null,
                powerPoints: hasNumericPowerPoints ? numericPowerPoints : null,
                configuration: {
                    // Ruling 20: the marker, not the copy list, says this container is Super Powers v2.
                    schema: POWER_CONTAINER_SCHEMA,
                    range: defaultLevel.range,
                    duration: defaultLevel.duration,
                    damage: defaultLevel.damage,
                    multiplier: hasNumericPowerPoints ? numericPowerPoints : 1,
                    modifiers: [],
                    // No `modifications` copy list (spec §5.4): direct grants apply from the catalog
                    // grantMultiplier times, and picks are records.
                },
                character: { uuid: character.uuid },
            } as SwadePowerContainerInterface
        },
        defaultStatusForType(type: string | null): string {
            return rulesFor(type).defaultStatus;
        },
        isStatusInUse(type: string | null | undefined, status: string | null | undefined): boolean {
            return rulesFor(type).inUseStatuses.includes(status ?? '');
        },
        multiplierForContainer(container: SwadeItemContainerInterface | null | undefined): number {
            if (!container) return 1;
            const rules = rulesFor(container.selection?.type);
            return rules.multiplier?.(container) ?? 1;
        },
        isItemInUse(container: SwadeItemContainerInterface): boolean {
            return this.isStatusInUse(container.selection?.type, container.status);
        },
        /**
         * How many times a modification defined on a container is in effect (spec 2026-09-10
         * carried modifications §3.1): an equipment container's multiplier while in use, else 0;
         * 1 while a skill, edge, hindrance or power container is present; 0 for anything else.
         */
        containerUnits(character: any, container: any): number {
            if (!container || typeof container.uuid !== 'string') return 0;
            const has = (arrayName: string) => Array.isArray(character?.[arrayName])
                && character[arrayName].some((c: any) => c?.uuid === container.uuid);
            if (has('equipment')) return this.isItemInUse(container) ? Math.max(1, this.multiplierForContainer(container)) : 0;
            return has('skills') || has('edges') || has('hindrances') || has('powers') ? 1 : 0;
        },
        // Callers, all at their end: applyItemStatusChange, applyCyberwareQuantityChange,
        // withChoiceResolution, addItemToCharacter, addSkillToCharacter, applyFramework,
        // applyTableRoll, removeItem, removeDrawback, removeEdge, removeSkill, removePowerOrAbility,
        // reconcileRecordSets, rollbackAppliedChoicesFor, retireResolvedChoices, cleanupOrphanedChoices,
        // rollbackTableRoll, setContainerQuantity — plus two callers outside this store, Details.vue's
        // onPowerChanged and onPowerDeleted. The next store action that pushes to or splices from
        // equipment/skills/edges/hindrances, sets a container's status or quantity, or writes
        // additionalData.choices belongs on this list too — INCLUDING anything reachable from
        // applyModification/rollbackModification. There is no exclusion rule: the sync is
        // re-entrancy-guarded per character, so a nested call from inside the apply/rollback graph
        // returns immediately and requests one sequential re-run instead of recursing. An earlier
        // version of this comment claimed the opposite, and it was wrong in its own first entry —
        // withChoiceResolution is a caller AND sits inside that graph (applyEdgeModification →
        // addEdgeToCharacter → withChoiceResolution, applyEquipmentModification →
        // withChoiceResolution), so a carried character.edges or character.equipment grant
        // re-entered the sync mid-application: the nested pass spliced the row that was still being
        // applied, re-pushed it, and applied the payload a second time or never terminated.
        /**
         * The full-state sync: re-derives container overrides and additionalData.carriedPayloads
         * from the sheet as it is now (spec 2026-09-10 carried modifications §3.2; container
         * overrides added by spec 2026-09-13 §4.2). Total, not incremental, for the reason
         * recomputeContainerOverrides gives; idempotent — a second call with no change applies
         * nothing. A carried entry is applied and rolled back HERE and nowhere else:
         * applyModification never reads `carry`. Called at the end of every store action that adds,
         * removes, re-statuses or re-quantifies a container or writes records; never on load. A
         * count moves only after its call returns, so a failed apply is retried by the next sync
         * rather than counted. A sync that applies nothing writes nothing: the ledger is attached on
         * its first row, never materialised empty.
         *
         * Re-entrancy: a carried payload may itself add or remove containers (a character.edges or
         * character.equipment grant), and those store actions sync at their end. The guard makes
         * that safe and bounded. A nested call records a request and returns; the outer call
         * consumes the request by running the reconciliation ONCE more, sequentially, after its own
         * up-pass — a re-run rather than a nested pass, because the container the payload added may
         * itself carry payloads that the first pass could not see. A request raised during the
         * re-run is dropped: the work is bounded at one re-run per outer call, and the next store
         * action's sync self-heals whatever is left, per §3.2's "a missed site is a stale ledger".
         */
        async syncCarriedPayloads(character: any): Promise<void> {
            if (!character) return;
            if (carriedSyncInFlight.has(character)) {
                carriedResyncRequested.add(character);
                return;
            }

            carriedSyncInFlight.add(character);
            try {
                await this._reconcileCarriedPayloads(character);
                if (carriedResyncRequested.has(character)) {
                    carriedResyncRequested.delete(character);
                    await this._reconcileCarriedPayloads(character);
                }
            } finally {
                carriedSyncInFlight.delete(character);
                // Drops a request raised during the re-run, and any request left by a throw.
                carriedResyncRequested.delete(character);
            }
        },
        /** syncCarriedPayloads' body. Private: everything calls the guarded wrapper. */
        async _reconcileCarriedPayloads(character: any): Promise<void> {
            const existingLedger = Array.isArray(character.additionalData?.carriedPayloads) ? character.additionalData.carriedPayloads : null;
            const ledger: CarriedPayloadApplication[] = existingLedger ?? [];
            let attached = existingLedger !== null;

            // A power's carried CHARACTER effects apply once per level (spec 2026-09-21 §5.3 item 3); a
            // carried container-detail effect alters the power itself once per pick (spec 2026-09-22
            // §3.3, ruling 26). Every other container keeps its units; containerUnits stays 1 for a power.
            // A rung record's carried effects scale with the points invested (owner's ruling 2026-09-23,
            // spec v2.2 §11, ruling 45: Armor Piercing ×3 at +2 AP per point is AP 6). The repeat count is
            // read off the record's value against the set's rung — pickedPowerOptions' one derivation, the
            // card's too — with the world for a universal set. Anything that is not a rung record, a legacy
            // container, or a set the sheet cannot see counts once, as before.
            const world = useAppDataStore().world;
            const repeats = (record: AppliedConfiguration): number => {
                if (record.configuration?.path !== 'container.powers') return 1;
                const power = (character.powers ?? []).find((p: any) => p?.uuid === record.uuid);
                if (!power) return 1;
                return pickedPowerOptions(character, power, world).find((row) => row.record === record)?.count ?? 1;
            };
            // Rows are held by KEY, never by object reference across an await: a carried payload's
            // own store action can legitimately rewrite the ledger while apply/rollback is in
            // flight, so every count move re-finds its row first and stops if it is gone.
            const rowFor = (key: string) => ledger.find((r) => r.key === key);

            // The sync is the full-state derivation step for BOTH ledgers (spec 2026-09-13 §4.2):
            // container overrides first, so an All Specified entry is re-resolved against the sheet
            // as it is now — every add, removal and status flow ends here — then the carries. It
            // SETTLES (ruling 47, 2026-09-24): a borrowed value can be produced by this very pass —
            // Armor Piercing's rows give the power its AP, and the same power's Unarmed wrapper
            // borrows it — so the derivation runs again until a pass applies and rolls back nothing.
            // Two passes settle every case on the table; the cap guards against a cycle between two
            // borrowers, it is not a budget.
            for (let pass = 0; pass < 4; pass++) {
                this.recomputeContainerOverrides(character);
                const { desired, skipped } = desiredCarriedApplications(character, (container, entry) =>
                    isPowerOf(character, container)
                        ? (supportedContainerPath(entry?.path ?? null) ? 1 : grantMultiplier(container))
                        : this.containerUnits(character, container), repeats);
                if (pass === 0) {
                    for (const entry of skipped) {
                        console.warn('CharacterData | syncCarriedPayloads | carried entry needs a fixed element, not applied', entry);
                    }
                }
                const wanted = new Map<string, { row: Omit<CarriedPayloadApplication, 'count'>, count: number }>();
                for (const d of desired) {
                    const seen = wanted.get(d.key);
                    if (seen) seen.count += d.desired;
                    else wanted.set(d.key, { row: { key: d.key, ownerUuid: d.ownerUuid, targetUuid: d.targetUuid, configuration: d.configuration }, count: d.desired });
                }
                let moved = 0;

                // Down first: rows no longer wanted, or above their count. Rollback uses the stored copy.
                for (const snapshot of [...ledger]) {
                    const key = snapshot.key;
                    const target = wanted.get(key)?.count ?? 0;
                    let row = rowFor(key);
                    while (row && row.count > target) {
                        await this.rollbackModification(character, deepCopyObject(row.configuration), true, 'temporary', row.targetUuid);
                        moved += 1;
                        row = rowFor(key);
                        if (!row) break;
                        row.count -= 1;
                    }
                    // A zero-count row that is still DESIRED survives for the up-pass to raise; only a
                    // row nothing wants leaves the ledger. Splicing a wanted row here is what let a
                    // re-entrant pass lose track of an application that was already under way.
                    if (row && row.count <= 0 && target <= 0) ledger.splice(ledger.indexOf(row), 1);
                }
                // Then up.
                for (const { row, count } of wanted.values()) {
                    if (count <= 0) continue;
                    if (!rowFor(row.key)) {
                        // Pushed BEFORE the first apply so the re-find below can succeed.
                        if (!attached) {
                            character.additionalData ??= {};
                            character.additionalData.carriedPayloads = ledger;
                            attached = true;
                        }
                        ledger.push({ ...row, configuration: deepCopyObject(row.configuration), count: 0 });
                    }
                    let existing = rowFor(row.key);
                    while (existing && existing.count < count) {
                        await this.applyModification(character, deepCopyObject(existing.configuration), true, 'temporary', existing.targetUuid);
                        moved += 1;
                        existing = rowFor(row.key);
                        if (!existing) break;
                        existing.count += 1;
                    }
                }

                if (moved === 0) break;
            }
        },
        async _ticksContainerMods(
            character: CharacterSheetInterface,
            container: SwadeItemContainerInterface,
            count: number,
            action: 'apply' | 'rollback',
            source: ModSource = 'temporary',
        ) {
            if (count <= 0) return;
            const directMods = directModificationsWithoutOverrides(character, container.uuid, container.selection?.modifications);
            const recordedChoices = (character.additionalData?.choices ?? [])
                .filter((c: AppliedConfiguration) => c.uuid === container.uuid);
            for (let i = 0; i < count; i++) {
                for (const mod of directMods) {
                    if (action === 'apply') await this.applyModification(character, mod.configuration, true, source, container.uuid ?? '');
                    else await this.rollbackModification(character, mod.configuration, true, source, container.uuid ?? '');
                }
                for (const choice of recordedChoices) {
                    if (action === 'apply') await this.applyModification(character, choice.configuration, true, source, container.uuid ?? '');
                    else await this.rollbackModification(character, choice.configuration, true, source, container.uuid ?? '');
                }
            }
        },
        _stripRecordedChoicesFor(character: CharacterSheetInterface, containerUuid: string) {
            if (!containerUuid || !character.additionalData?.choices) return;
            character.additionalData.choices = character.additionalData.choices.filter(
                (c: AppliedConfiguration) => c.uuid !== containerUuid,
            );
        },
        _stripGrantedEquipmentFor(character: CharacterSheetInterface, containerUuid: string) {
            if (!containerUuid || !character.additionalData?.grantedEquipment) return;
            character.additionalData.grantedEquipment = character.additionalData.grantedEquipment.filter(
                (g: any) => g.containerUuid !== containerUuid,
            );
        },
        async applyItemStatusChange(
            character: CharacterSheetInterface,
            container: SwadeItemContainerInterface,
            oldStatus: string | null | undefined,
            newStatus: string | null | undefined,
        ) {
            if (!character || !container) return;
            // The store owns the status (spec 2026-09-10 carried modifications §3.3): the gear row
            // set it before calling and the editor did not, so every in-use read below — and the
            // carried-payload sync at the end — sees the current status either way.
            if (typeof newStatus === 'string') container.status = newStatus;
            const type = container.selection?.type;
            const wasInUse = this.isStatusInUse(type, oldStatus);
            const isInUse = this.isStatusInUse(type, newStatus);
            if (wasInUse === isInUse) return;

            const multiplier = Math.max(1, this.multiplierForContainer(container));
            const modifications = container.selection?.modifications ?? [];

            if (isInUse) {
                // out-of-use → in-use: prefer choices already recorded at add-time over
                // re-prompting. Only resolve fresh if no recorded choices exist for this
                // container (covers legacy data or non-cyberware items entering this branch).
                const existing = (character.additionalData?.choices ?? [])
                    .filter((c: AppliedConfiguration) => c.uuid === container.uuid);

                let resolved: SwadeModConfigurationInterface[];

                if (existing.length > 0) {
                    resolved = existing.map((c: AppliedConfiguration) =>
                        deepCopyObject(c.configuration) as SwadeModConfigurationInterface
                    );
                } else {
                    let applied: AppliedConfiguration[];
                    try {
                        ({ applied, resolved } = await this.resolveChoiceSets(
                            character,
                            { uuid: container.uuid ?? '', name: container.selection?.name ?? '' },
                            modifications,
                        ));
                    } catch (err) {
                        if (err instanceof ChoiceCancelledError) return;
                        throw err;
                    }
                    if (applied.length > 0) {
                        character.additionalData ??= {};
                        character.additionalData.choices ??= [];
                        character.additionalData.choices.push(...applied);
                    }
                }

                for (let i = 0; i < multiplier; i++) {
                    for (const mod of directModificationsWithoutOverrides(character, container.uuid, modifications)) {
                        await this.applyModification(character, mod.configuration, true, 'temporary', container.uuid ?? '');
                    }
                    for (const config of resolved) {
                        await this.applyModification(character, config, true, 'temporary', container.uuid ?? '');
                    }
                }
            }
            else {
                // in-use → out-of-use: roll back direct mods AND recorded choices × multiplier.
                // Do NOT strip the recorded choices — they represent the user's chosen options
                // for this container and must persist so re-install can reuse them without
                // re-prompting. Choices are stripped only when the container is removed.
                await this._ticksContainerMods(character, container, multiplier, 'rollback');

                // Safety net, and deliberately UNNARROWED. The tick above deregisters one effect
                // per modification it replays, so it can only reach effects whose configuration is
                // still reachable from selection.modifications or from this container's recorded
                // choices. Anything else this source registered — an option edited away, a
                // modification since dropped from the element, a stray from a future writer — would
                // otherwise outlive its source going out of use, which is the one thing the effects
                // ledger exists to prevent.
                //
                // Scoped to sourceUuid, never a blanket wipe: other sources' effects on the same
                // target survive. And it touches containerEffects only — the choices ledger above
                // is untouched, so the decision still survives for the re-install.
                this.deregisterContainerEffects(character, container.uuid ?? '');
            }
            await this.syncCarriedPayloads(character);
        },
        async applyCyberwareQuantityChange(
            character: CharacterSheetInterface,
            container: SwadeItemContainerInterface,
            delta: number,
        ) {
            if (!character || !container || delta === 0) return;
            if (!this.isItemInUse(container)) return;
            await this._ticksContainerMods(
                character,
                container,
                Math.abs(delta),
                delta > 0 ? 'apply' : 'rollback',
            );
            await this.syncCarriedPayloads(character);
        },
        // One path for "this container now holds N of the item". Quantity is not a plain
        // assignment: in-use cyberware applies its modifiers once per unit, so the difference has
        // to be ticked through applyCyberwareQuantityChange — the same thing Gear.vue's item
        // editor does when its own quantity field changes.
        async setContainerQuantity(
            character: CharacterSheetInterface,
            container: SwadeItemContainerInterface,
            quantity: number,
        ): Promise<void> {
            if (!character || !container) return;

            // Whole units, at least one — a container holding zero is a removal, which is
            // removeItem's job, not this one's.
            const next = Math.max(1, Math.floor(quantity));
            const previous = container.quantity ?? 1;
            if (next === previous) return;

            container.quantity = next;

            if (container.selection?.type === 'cyberware') {
                // Self-guards on isItemInUse: uninstalled cyberware contributes nothing to tick.
                await this.applyCyberwareQuantityChange(character, container, next - previous);
            }

            // Audit addition (spec §3.2): setContainerQuantity sets a container's quantity directly
            // and is not itself in the wired-sites table. The cyberware branch above already syncs
            // via applyCyberwareQuantityChange; every other item type's multiplier is quantity-
            // independent (item-type-rules.ts), so this is a no-op for them today — kept here anyway
            // so a future quantity-sensitive rule for another type cannot silently reopen this gap.
            await this.syncCarriedPayloads(character);
            await this.recalculateDerivedStats(character);
        },
        /**
         * A pick that names a stack takes one unit of it (spec 2026-10-13 §6). For each equipment
         * container a record names while it holds more than one unit, one unit is split off and the
         * answer's records that name the stack are pointed at it — one split per stack per answer, so two
         * picks from one source that land on the same stack share the unit. A record naming its own
         * source is that source's self-reference and never splits it. Records are REPLACED, never
         * mutated: a resolver can hand back the live records of additionalData.choices, and mutating one
         * would empty reconcileRecordSets' diff. Callers run this once the whole answer is in and before
         * any of it is recorded, so a cancelled prompt never leaves a split behind.
         */
        async splitTargetedStacks(character: any, records: AppliedConfiguration[]): Promise<AppliedConfiguration[]> {
            const equipment: SwadeItemContainerInterface[] = Array.isArray(character?.equipment) ? character.equipment : [];
            const copies = new Map<string, string>();
            const result: AppliedConfiguration[] = [];
            for (const record of records) {
                const element: unknown = record?.configuration?.element;
                if (supportedContainerPath(record?.configuration?.path) !== 'equipment' || typeof element !== 'string' || element === record.uuid) {
                    result.push(record);
                    continue;
                }
                if (!copies.has(element)) {
                    const stack = equipment.find((container) => container.uuid === element);
                    if (stack && (stack.quantity ?? 1) > 1) copies.set(element, (await this.splitOneUnit(character, stack)).uuid as string);
                }
                const copyUuid = copies.get(element);
                result.push(copyUuid ? { ...record, configuration: { ...record.configuration, element: copyUuid } } : record);
            }
            return result;
        },
        /**
         * Take one unit out of a stack into a container of its own (spec 2026-10-13 §6). The copy is the
         * stack with a new uuid and quantity 1 — the same item, status, name and current charges, which
         * are not re-seeded — and it carries the stack's own records, its framework provenance and the
         * values the player entered on its fields. Unless the item is cyberware, a record of the
         * stack's that picks another container stays with the stack alone: the copy's pick waits for
         * its pencil, and a choice-set answer stays chosen with no target. The stack gives the unit up
         * through setContainerQuantity, which ticks installed cyberware back once; the copy is applied
         * once through the per-unit path when it is in use. Other sources' records naming the stack
         * stay with it, so the units left behind are not modified. The stack must hold more than one
         * unit: splitTargetedStacks checks that before it calls this.
         */
        async splitOneUnit(character: any, stack: SwadeItemContainerInterface): Promise<SwadeItemContainerInterface> {
            const stackUuid = stack.uuid ?? '';
            const copy = deepCopyObject(stack) as SwadeItemContainerInterface;
            const copyUuid: string = uuid();
            copy.uuid = copyUuid;
            copy.quantity = 1;

            await this.setContainerQuantity(character, stack, (stack.quantity ?? 1) - 1);

            // The stack's own records are its answers, and the copy answers the same. A record that names
            // the stack itself — an item's self-named specific list resolves to its own container — names
            // the copy instead, so the copy's own effect lands on the copy.
            //
            // A pick of another container is the exception, unless the item is cyberware. Cyberware
            // applies its records once per unit, so the copy takes its unit's share of every one. Any
            // other item applied them once for the whole stack, so the pick stays with the stack: two
            // copies would otherwise hold one target (spec 2026-10-13 §4a), and the target would take
            // the effect twice. The copy's pick waits for its pencil instead (owner, 2026-10-14). A
            // direct pick is left unrecorded. A choice-set answer keeps its option as chosen, with no
            // target, the way resolveOptionTarget records one when nothing is free: it applies nothing,
            // and the copy's own record keeps its pencil showing.
            character.additionalData ??= {};
            const perUnit = rulesFor(stack.selection?.type).multiplier !== undefined;
            const namesAnotherContainer = (record: AppliedConfiguration): boolean => {
                const element: unknown = record.configuration?.element;
                return supportedContainerPath(record.configuration?.path) !== undefined
                    && typeof element === 'string' && element.length > 0 && element !== stackUuid;
            };
            const sets = (stack.selection?.modifications ?? []).filter((mod) => mod?.configuration?.path === 'character.choiceSet');
            const own: AppliedConfiguration[] = [];
            for (const record of (character.additionalData.choices ?? []) as AppliedConfiguration[]) {
                if (record.uuid !== stackUuid) continue;
                if (perUnit || !namesAnotherContainer(record)) {
                    own.push(record);
                    continue;
                }
                const [asChosen] = chosenOptionRecords(sets, [record]);
                // Not an answer to one of the item's sets: a direct pick, left unrecorded.
                if (asChosen === record) continue;
                // One record per option: a pick-many answer holds one record per target.
                const known = own.some((kept) => resolvedModificationUuid(kept) === resolvedModificationUuid(asChosen)
                    && matchesConfiguration(kept.configuration, asChosen.configuration));
                if (!known) own.push(asChosen);
            }
            if (own.length > 0) {
                character.additionalData.choices = [
                    ...character.additionalData.choices,
                    ...own.map((record: AppliedConfiguration) => {
                        const configuration = deepCopyObject(record.configuration);
                        if (configuration.element === stackUuid) configuration.element = copyUuid;
                        return { ...deepCopyObject(record), uuid: copyUuid, configuration };
                    }),
                ];
            }
            // Framework-granted cyberware is strain-exempt (currentStrain reads this), and so is its copy.
            const granted = (character.additionalData.grantedEquipment ?? [])
                .find((entry: { containerUuid: string }) => entry.containerUuid === stackUuid);
            if (granted) character.additionalData.grantedEquipment.push({ ...granted, containerUuid: copyUuid });

            character.equipment.push(copy);
            if (this.isItemInUse(copy)) await this._ticksContainerMods(character, copy, 1, 'apply');

            // The values the player typed on the stack's fields, read and written the way the field itself
            // does, so the copy shows what the stack shows — on its own re-registered effects and on other
            // sources' All Specified effects, which now target the copy too. A field the copy does not have
            // (another source's pick that names only the stack) is skipped.
            const ledger: ContainerEffect[] = Array.isArray(character.additionalData.containerEffects)
                ? character.additionalData.containerEffects
                : [];
            const fields = new Map<string, { path: string, detail: string }>();
            for (const effect of ledger) {
                const path = effect?.configuration?.path;
                const detail = effect?.configuration?.detail;
                if (typeof path !== 'string' || typeof detail !== 'string' || !isEnterable(effect.configuration)) continue;
                if (!effectTargets(character, effect).some((target: { uuid?: unknown }) => target?.uuid === stackUuid)) continue;
                fields.set(`${path}#${detail}`, { path, detail });
            }
            for (const { path, detail } of fields.values()) {
                const value = enteredValueFor(enterableEffectFor(character, path, stack, detail), stackUuid);
                const field = enterableEffectFor(character, path, copy, detail);
                if (value !== null && field) this.enterContainerEffectValue(character, field.effectId, copyUuid, value);
            }

            await this.syncCarriedPayloads(character);
            this.sortEquipmentList(character);
            // Two containers now count where one did (an equipped suit stacks, the item's own modifiers
            // apply per container — spec 2026-10-13 §9), and not every caller recalculates afterwards.
            await this.recalculateDerivedStats(character);
            return copy;
        },
        newItemContainer(item: SwadeItemInterface): SwadeItemContainerInterface {
            return {
                uuid: uuid(),
                selection: item,
                name: null,
                quantity: 1,
                charges: null,
                status: this.defaultStatusForType(item.type),
            };
        },
        newSettingRule(system: SystemInterface): SettingRuleInterface|null {
            console.debug(system)
            if (!system?.uuid) return null;

            return {
                uuid: "",
                name: "",
                system: { uuid: system?.uuid, name: system?.name },
                modifications: [],
                summary: null,
                description: null,
            }
        },
        newRandomTable(system: SystemInterface): RandomTableInterface | null {
            if (!system?.uuid) return null;

            return {
                uuid: "",
                name: "",
                system: { uuid: system?.uuid, name: system?.name },
                notation: null,
                summary: null,
                description: null,
                min: 0,
                max: 0,
                relatedElements: [],
                modifications: [],
            }
        },
        newRandomTableResult(): RandomTableResultInterface {
            return {
                uuid: "",
                name: "",
                min: 0,
                max: 0,
                summary: null,
                modifications: [],
            }
        },

        attributeValue(name: string | null, character?: CharacterSheetInterface | null): number {
            const store = useAppDataStore();
            const targetCharacter = character ?? store.character;

            const valueKey = (name ?? '') + 'Value'
            if (targetCharacter && !isNaN(targetCharacter[valueKey])) {
                const actualValue = targetCharacter[valueKey] ?? 0;

                //TODO: This needs to be more complex and tax into account protoData
                // Proto Value is the base starting value after Ancestry/Ancestry-like modifications are factored in
                const protoValue = (this.dieRank(targetCharacter?.protoData?.attributes[name ?? ''], null))

                return actualValue - protoValue.getValue();
            }

            return 0;
        },
        // Full die-code rank for an attribute. character[name] is the modifier-FREE
        // die code (e.g. "d12"); the modifier lives in character[name + 'Modifier'],
        // so both are combined here to recover a rank such as d12+2 (a legendary,
        // above-cap attribute) or d6-2 (a Hindrance-reduced one).
        attributeRank(name: string | null, character?: CharacterSheetInterface | null): DieRank | null {
            const store = useAppDataStore();
            const targetCharacter = character ?? store.character;

            const code = targetCharacter?.[name ?? ''];
            if (typeof code !== 'string') return null;

            const rank = this.dieRank(code, targetCharacter?.[(name ?? '') + 'Modifier'] ?? null);
            return rank.isParsed() ? rank : null;
        },
        attributeDieSides(name: string | null, character?: CharacterSheetInterface | null): number | null {
            return this.attributeRank(name, character)?.getDieSides() ?? null;
        },
        // Die-steps a rank sits above d4, treating each point of an at-d12 modifier
        // as a real step above the cap (d12=4, d12+1=5, …). A modifier BELOW d12 is
        // a flat trait bonus (gear/Hindrance) and is ignored, so d8 and d8+2 tie.
        dieRankSteps(rank: DieRank | null): number {
            if (!rank) return 0;
            const sides = rank.getDieSides() ?? 4;
            const aboveCap = sides === 12 ? Math.max(0, rank.getDieModifier() ?? 0) : 0;
            return (sides / 2 - 2) + aboveCap;
        },
        // Die-steps of the live rank plus the effective.skillCost offset (spec 2026-09-20 §6.2).
        // NEGATIVE IS ALLOWED: a d4 attribute treated as one type lower is -1 steps, which is what
        // makes a d4 skill under it cost 2. Null when the attribute does not resolve, so callers
        // keep their existing "unresolved" branch.
        effectiveAttributeSteps(name: string | null, character?: CharacterSheetInterface | null): number | null {
            const rank = this.attributeRank(name, character);
            if (!rank) return null;
            const target = character ?? useAppDataStore().character;
            return this.dieRankSteps(rank) + effectiveOffset(target?.protoData, name ?? '', 'effective');
        },
        skillValue(rank: string | null, linkedAttribute: string, character?: CharacterSheetInterface | null) {
            if (!rank) return 0;

            const skillRank = new DieRank(rank);
            if (!skillRank.isParsed()) return 0;

            // Skill level is driven by the DIE CODE plus any genuine above-d12 steps
            // (see dieRankSteps). The linked attribute is compared the same way, so a
            // legendary (above-d12) attribute still out-ranks the skill and a flat
            // modifier on either side never inflates the level.
            const skillSteps = this.dieRankSteps(skillRank);
            // The linked attribute's steps INCLUDING its effective.skillCost offset (spec 2026-09-20
            // §6.2), so an attribute treated as one type lower surcharges one step earlier.
            // Unresolved linked attribute → treat as equal so no surcharge applies.
            const attributeSteps = this.effectiveAttributeSteps(linkedAttribute, character) ?? skillSteps;

            // 1 point per die-step from d4, plus 1 EXTRA point per step the skill
            // exceeds its linked attribute — SWADE charges double past the attribute,
            // so an above-cap step above the attribute costs 2, but only 1 while the
            // (legendary) attribute is still higher.
            const baseCost = skillSteps + 1;
            const surcharge = Math.max(0, skillSteps - attributeSteps);

            return baseCost + surcharge;
        },
        defaultStrain(character: CharacterSheetInterface): number {
            if (!character) return 0;

            // Override layer: operator='alter' strain mods override the dice-derived default.
            // Lowest wins (most restrictive cap binds). See spec § Section 4.
            const override = this.strainAlterOverride(character);
            if (override !== null) return override;

            const cb = (character.additionalData as any)?.calculateBase?.strain;
            const vigorDie  = cb?.vigor  ?? character.vigor  ?? 'd4';
            const spiritDie = cb?.spirit ?? character.spirit ?? 'd4';
            const vigorValue  = this.dieRank(vigorDie).getValue();
            const spiritValue = this.dieRank(spiritDie).getValue();
            return Math.floor(Math.min(vigorValue, spiritValue) / 2);
        },
        currentStrain(character: CharacterSheetInterface): number {
            if (!character) return 0;
            // World flags live on the app-data store's full world detail, not on
            // character.world (which is a slim reference). See [[architecture-world-slim-vs-full]].
            const appData = useAppDataStore();
            if (appData.world?.flags?.strain !== true) return 0;
            const equipment = Array.isArray(character.equipment) ? character.equipment : [];
            const exempt = new Set(
                (character.additionalData?.grantedEquipment ?? []).map((g: { containerUuid: string }) => g.containerUuid),
            );
            let total = 0;
            for (const container of equipment) {
                if (container?.selection?.type !== 'cyberware') continue;
                if (container?.status !== 'installed') continue;
                if (exempt.has(container.uuid)) continue;
                const strain = resolveContainerDetails(container)?.strain ?? 0;
                const qty = container.quantity ?? 1;
                total += strain * qty;
            }
            return total;
        },
        calculateAgilityValue(character: CharacterSheetInterface) {
            if (!character) return;
            if (!character?.agility) return;

            const agility = this.dieRank(character.agility, character.agilityModifier ?? null);
            character.agilityValue = agility.getValue();
        },
        calculateSmartsValue(character: CharacterSheetInterface) {
            if (!character) return;
            if (!character?.smarts) return;

            const smarts = this.dieRank(character.smarts, character.smartsModifier ?? null);
            character.smartsValue = smarts.getValue();
        },
        calculateSpiritValue(character: CharacterSheetInterface) {
            if (!character) return;
            if (!character?.spirit) return;

            const spirit = this.dieRank(character.spirit, character.spiritModifier ?? null);
            character.spiritValue = spirit.getValue();
        },
        calculateStrengthValue(character: CharacterSheetInterface) {
            if (!character) return;
            if (!character?.strength) return;

            const strength = this.dieRank(character.strength, character.strengthModifier ?? null);
            character.strengthValue = strength.getValue();
        },
        calculateVigorValue(character: CharacterSheetInterface) {
            if (!character) return;
            if (!character?.vigor) return;

            const vigor = this.dieRank(character.vigor, character.vigorModifier ?? null);
            character.vigorValue = vigor.getValue();
        },
        calculateToughness(character: CharacterSheetInterface) {
            if (!character) return;
            
            // spec 2026-09-18 §5.3: the base is the highest active retarget (derived.toughness, alter),
            // else the stock formula; size, every flat bonus and every calculated bonus stack on top.
            // Rows come from every source the shared iterator walks. applyModification has no branch
            // for this path on purpose: this rebuild, run after every add and remove, owns it.
            type DerivedRow = { configuration: SwadeModConfigurationInterface, multiplier: number, parts: DerivedFormulaParts };
            const derived: DerivedRow[] = [];
            for (const { configuration, multiplier } of this.iterateModifications(character, (c: SwadeModConfigurationInterface) => c.path === DERIVED_TOUGHNESS_PATH)) {
                const parts = parseDerivedFormula(configuration.value);
                if (parts === null || !isDerivedSourceAttribute(configuration.element)) continue;
                derived.push({ configuration, multiplier, parts });
            }
            // agilityValue … vigorValue: sides plus modifier, computed earlier in this same recalc.
            const evaluated = (row: DerivedRow): number =>
                evaluateDerivedFormula(row.parts, Number((character as any)[`${String(row.configuration.element)}Value`] ?? 0) || 0);

            const retargets = derived.filter((row) => row.configuration.operator === 'alter').map(evaluated);
            const base = retargets.length > 0
                ? Math.max(...retargets)
                : evaluateDerivedFormula(STOCK_TOUGHNESS_FORMULA, character?.vigorValue ?? 0);
            // A retarget ignores the multiplier (two installed copies do not retarget twice); a
            // calculated bonus is floored per term, signed by its operator, then scaled.
            const calculated = derived
                .filter((row) => row.configuration.operator === 'add' || row.configuration.operator === 'subtract')
                .reduce((sum, row) => sum + (row.configuration.operator === 'subtract' ? -evaluated(row) : evaluated(row)) * row.multiplier, 0);

            character.toughness = base
                + (character?.size ?? 0)
                + this.toughnessModificationsTotal(character)
                + calculated;

            let totalArmor: number = this.armorModificationsTotal(character);
            if (Array.isArray(character.equipment)) {
                const equippedArmor = character.equipment?.filter((container: SwadeItemContainerInterface) => {
                    if (container.selection?.type !== 'armor' || container.status !== 'equipped') return false;
                    const locations = resolveContainerDetails(container)?.locations ?? [];
                    return locations.length === 0 || locations.includes('torso');
                }) ?? [];

                // How many ADDITIONAL suits stack past the primary one, each at half value.
                // 0 = no stacking; 1 = SWADE core.
                // buildOptions is the only source that carries a WORLD's override. The
                // system.defaults rung is NOT a world-safety net — it cannot know what a world
                // forbids — it only carries a non-default SYSTEM value through paths where
                // buildOptions is still null (resetBuildValidation clears it, and some
                // recalculateDerivedStats callers do not rebuild it). Kept for consistency with
                // the same fallback in AdditionalAttributes.vue.
                const appData = useAppDataStore();
                const rawLimit = (this.buildOptions ?? appData.system?.defaults)?.armorStackingLimit;
                // Only a real number configures the rule. null, '', or a stale string degrade to
                // stock stacking rather than to 0 — treating '' as 0 would silently forbid
                // stacking. No ceiling: 99 is an authoring affordance, not a domain limit.
                const stackingLimit = normalizeArmorStackingLimit(rawLimit) ?? 1;

                equippedArmor
                    .sort((a: SwadeItemContainerInterface, b: SwadeItemContainerInterface) => {
                        return (Number(resolveContainerDetails(b)?.armor ?? 0) || 0)
                            - (Number(resolveContainerDetails(a)?.armor ?? 0) || 0);
                    })
                    .slice(0, stackingLimit + 1)
                    .forEach((container: SwadeItemContainerInterface, index: number) => {
                        // The primary suit counts in full; each stacking suit within the limit
                        // counts half. Suits past the limit were already dropped by the slice,
                        // so armor and toughness cannot diverge on where the cut-off falls.
                        const stacked = (value: number) => index > 0 ? Math.floor(value / 2) : value;

                        character.toughness = (character.toughness ?? 0)
                            + stacked(Number(resolveContainerDetails(container)?.toughness ?? 0) || 0);

                        totalArmor = totalArmor
                            + stacked(Number(resolveContainerDetails(container)?.armor ?? 0) || 0);
                    });
            }

            if ((totalArmor ?? 0) > 0) {
                character.armor = totalArmor;
            }
            else character.armor = null;
        },
        calculatePace(character: CharacterSheetInterface) {
            if (!character) return;

            character.pace = 6;
            //TODO: recalculate by taking into accounts ability modifications
        },
        calculateParry(character: CharacterSheetInterface) {
            if (!character) return;

            let parryValue = 2;

            //TODO: find skill based on world settings or system default
            //store.system.defaults.derived.parry
            const skill = character?.skills?.find(function (item: SwadeSkillRankInterface) {
                return item.skill.derivative === 'parry';
            });

            if (skill) {
                let value = this.dieRankValue(skill.rank ?? "", skill.modifier);
                if (value) {
                    parryValue = parryValue + (Math.floor((value ?? 0) / 2));
                }
            }

            //Factor in modifications
            const parryModifiers = [];
            if (Array.isArray(character.ancestry?.grants)) {
                const ancestryModifiersToParry = character.ancestry.grants.filter((item: SwadeAbilityInterface) => {
                        console.debug("CharacterData | calculateParry", item);
                        return item.type === 'ability' &&
                            (item.modifications ?? []).filter((mod: SwadeModificationInterface) => {
                                return mod.configuration.path === 'character.attributes' &&
                                    mod.configuration.element === 'parry';
                            }).length > 0
                    })
                        .map((item: SwadeAbilityInterface) => {
                            return (item.modifications ?? []).filter((mod: SwadeModificationInterface) => {
                                return mod.configuration.path === 'character.attributes' &&
                                    mod.configuration.element === 'parry';
                            }).map((mod: SwadeModificationInterface) => {
                                return mod.configuration;
                            });
                        })
                        .reduce((accumulator: SwadeModConfigurationInterface[], value: SwadeModConfigurationInterface[]) => accumulator.concat(value), [] as SwadeModConfigurationInterface[])
                ;

                parryModifiers.push(...ancestryModifiersToParry);
            }

            if (Array.isArray(character.abilities)) {
                const abilityModifiersToParry =  character.abilities.filter((item: SwadeAbilityInterface) => {
                        console.debug("CharacterData | calculateParry", item);
                        return item.type === 'ability' &&
                            (item.modifications ?? []).filter((mod: SwadeModificationInterface) => {
                                return mod.configuration.path === 'character.attributes' &&
                                    mod.configuration.element === 'parry';
                            }).length > 0
                    })
                        .map((item: SwadeAbilityInterface) => {
                            return (item.modifications ?? []).filter((mod: SwadeModificationInterface) => {
                                return mod.configuration.path === 'character.attributes' &&
                                    mod.configuration.element === 'parry';
                            }).map((mod: SwadeModificationInterface) => {
                                return mod.configuration;
                            });
                        })
                        .reduce((accumulator, value) => accumulator.concat(value), [])
                ;

                parryModifiers.push(...abilityModifiersToParry);
            }

            if (Array.isArray(character.hindrances)) {
                const hindranceModifiersToParry =  character.hindrances.filter((item: SwadeDrawbackInterface) => {
                        console.debug("CharacterData | calculateParry", item);
                        return (item.hindrance?.modifications ?? []).filter((mod: SwadeModificationInterface) => {
                            return mod.configuration?.path === 'character.attributes' &&
                                mod.configuration?.element === 'parry';
                        }).length > 0
                    })
                        .map((item: SwadeDrawbackInterface) => {
                            return (item.hindrance?.modifications ?? []).filter((mod: SwadeModificationInterface) => {
                                return mod.configuration?.path === 'character.attributes' &&
                                    mod.configuration?.element === 'parry';
                            }).map((mod: SwadeModificationInterface) => {
                                return mod.configuration;
                            });
                        })
                        .reduce((accumulator, value) => accumulator.concat(value), [])
                ;

                parryModifiers.push(...hindranceModifiersToParry);
            }

            parryModifiers.push(
                ...this.getEdgeModificationConfigs(character.edges ?? [], 'character.attributes', 'parry')
            );

            if (Array.isArray(character.powers)) {
                const powerModifiersToParry =  character.powers.filter((item: SwadePowerContainerInterface) => {
                        console.debug("CharacterData | calculateParry", item);
                        if (!Array.isArray(item.selection?.modifications)) return false;

                        return item.selection.modifications.filter((mod: SwadeModificationInterface) => {
                            return mod.configuration.path === 'character.attributes' &&
                                mod.configuration.element === 'parry';
                            }).length > 0
                        })
                        .map((item: SwadePowerContainerInterface) => {
                            // `?? []` never fires: the filter above already required an array. Written
                            // this way to match the equipment walker below rather than to add a guard.
                            return (item.selection.modifications ?? []).filter((mod: SwadeModificationInterface) => {
                                return mod.configuration.path === 'character.attributes' &&
                                    mod.configuration.element === 'parry';
                                })
                                .map((mod: SwadeModificationInterface) => {
                                    // Textual-cost powers carry null points; apply their mods once.
                                    // `multiplier` is optional on the container configuration and a
                                    // container persisted before it existed has none — dividing by
                                    // undefined yields NaN, which the isFinite test below already
                                    // handles, so it is asserted rather than defaulted.
                                    const powerPointRatio = item.powerPoints == null
                                        ? 1
                                        : (item.powerPoints / (item.configuration.multiplier as number));
                                    return {
                                        ...mod.configuration,
                                        multiplier: Number.isFinite(powerPointRatio) ? powerPointRatio : 1,
                                    };
                                });
                        })
                        .reduce((accumulator, value) => accumulator.concat(value), [])
                ;

                parryModifiers.push(...powerModifiersToParry);
            }

            //Factor in equipment bonuses
            if (Array.isArray(character.equipment)) {
                const equippedShield = character.equipment.find((container: SwadeItemContainerInterface) => {
                    return container.selection?.type === 'shield' && container.status === 'equipped';
                });

                if (equippedShield) {
                    parryValue = parryValue + (resolveContainerDetails(equippedShield)?.parry ?? 0);
                }

                //Factor in equipment modifications
                const equipmentModifiersToParry = character.equipment
                    .filter((container: SwadeItemContainerInterface) => {
                        if (!this.isItemInUse(container)) return false;
                        if (!Array.isArray(container.selection?.modifications)) return false;

                        return container.selection.modifications.filter((mod: SwadeModificationInterface) => {
                            return mod.configuration.path === 'character.attributes' &&
                                mod.configuration.element === 'parry';
                        }).length > 0;
                    })
                    .map((container: SwadeItemContainerInterface) => {
                        return (container.selection?.modifications ?? []).filter((mod: SwadeModificationInterface) => {
                            return mod.configuration.path === 'character.attributes' &&
                                mod.configuration.element === 'parry';
                        }).map((mod: SwadeModificationInterface) => {
                            return mod.configuration;
                        });
                    })
                    .reduce((accumulator, value) => accumulator.concat(value), []);

                parryModifiers.push(...equipmentModifiersToParry);
            }

            // The carried-payload ledger is a source too (spec 2026-09-10 carried modifications §3.4):
            // the sync's direct write to character.parry does not survive this rebuild.
            parryModifiers.push(
                ...carriedAppliedConfigs(character.additionalData?.carriedPayloads, 'character.attributes', 'parry')
            );

            parryModifiers.forEach((modifier: WalkerModConfiguration) => {
                console.debug("CharacterData | calculateParry", modifier.path, modifier.element);
                // `*` performs ToNumeric on a string operand, which is exactly Number(x).
                let modificationValue = Number(modifier.value) * (modifier.multiplier ?? 1);
                if (modifier.operator === 'subtract') {
                    modificationValue = modificationValue * -1;
                }

                parryValue = parryValue + modificationValue;
            })

            character.parry = parryValue;
        },
        calculateSize(character: CharacterSheetInterface) {
            if (!character) return;

            character.size = 0;
            //TODO: recalculate by taking into accounts ability modifications
        },
        dieRank(dieRank: string, dieModifier: number | null): DieRank {
            let step = dieRank;
            if ((dieModifier ?? 0) > 0) {
                step = step +'+'+ dieModifier;
            }
            else if ((dieModifier ?? 0) < 0) {
                step = step +''+ dieModifier;
            }

            return new DieRank(step)
        },
        dieRankValue(dieRank: string, dieModifier: number | null) {
            const rank = this.dieRank(dieRank, dieModifier);
            if (rank.isParsed()) {
                return rank.getValue();
            }

            return null;
        },

        /**
         * The flat `character.attributes` total for a derived stat, summed from every source the
         * shared iterator walks (spec 2026-09-18 §5.2): the integer value, negated for `subtract`,
         * once per unit in effect. `alter` rows belong to an override layer (strain's) and are
         * skipped; any other operator adds, so a row whose operator was never set keeps counting.
         * Rebuilt on every recalculation: applyDerivedAttributeModification's direct write does not
         * survive calculateToughness, so this is the only place a flat bonus can persist.
         */
        flatAttributeTotal(character: CharacterSheetInterface, element: 'toughness' | 'armor'): number {
            let total = 0;
            const rows = this.iterateModifications(character, (configuration: SwadeModConfigurationInterface) =>
                configuration.path === 'character.attributes'
                && configuration.element === element
                && configuration.operator !== 'alter');
            for (const { configuration, multiplier } of rows) {
                if (!Number.isInteger(configuration.value)) continue;
                const value = configuration.value as number;
                total += (configuration.operator === 'subtract' ? -value : value) * multiplier;
            }
            return total;
        },
        toughnessModificationsTotal(character: CharacterSheetInterface): number {
            return this.flatAttributeTotal(character, 'toughness');
        },
        armorModificationsTotal(character: CharacterSheetInterface): number {
            return this.flatAttributeTotal(character, 'armor');
        },

        /**
         * Rebuild the signed die-step offsets on protoData.attributes.<attr> from every effective.*
         * row the shared walk sees (spec 2026-09-20 §6.1). Runs first in updateBuildStatus, so the
         * skill-point allocation in the same cycle reads fresh offsets, and again in
         * recalculateDerivedStats, so a legacy character has them on first load.
         *
         * Deliberately NO `protoData.locked` / hasActiveAdvances guard: those protect creation
         * allocation, and an edge taken as an advance must still move these offsets (ruling 2026-09-20).
         */
        recalculateEffectiveAttributes(character: CharacterSheetInterface): void {
            if (!character) return;
            if (!character.protoData) {
                character.protoData = this.newProtoData();
            }
            const rows = this.iterateModifications(character, (configuration: SwadeModConfigurationInterface) => isEffectivePath(configuration.path));
            writeEffectiveOffsets(character.protoData.attributes, sumEffectiveOffsets(rows));
        },
        updateBuildStatus(character: CharacterSheetInterface) {
            console.info('CharacterData | updateBuildStatus');
            if (!character) return;

            console.debug('CharacterData | updateBuildStatus | update allocations', character);
            // FIRST: updateAllocatedSkillPoints below costs skills against the effective attribute.
            this.recalculateEffectiveAttributes(character);
            this.updateProtoAttributes(character);
            this.updateAllocatedAttributePoints(character);
            this.updateProtoSkills(character);
            this.updateAllocatedSkillPoints(character);
            this.updateAllocatedPerkPoints(character);
            this.updateAllocatedCharacterPoints(character);
            this.updateAllocatedLanguages(character);
        },
        updateProtoAttributes(character: CharacterSheetInterface) {
            if (!character.protoData) {
                character.protoData = this.newProtoData();
            }

            if (character.protoData.locked || this.hasActiveAdvances(character)) return;

            const agility = this.dieRank(character.agility, character.agilityModifier)
            character.protoData.attributes.agility.novice = agility.getDieCode();

            const smarts = this.dieRank(character.smarts, character.smartsModifier)
            character.protoData.attributes.smarts.novice = smarts.getDieCode();

            const spirit = this.dieRank(character.spirit, character.spiritModifier)
            character.protoData.attributes.spirit.novice = spirit.getDieCode();

            const strength = this.dieRank(character.strength, character.strengthModifier)
            character.protoData.attributes.strength.novice = strength.getDieCode();

            const vigor = this.dieRank(character.vigor, character.vigorModifier)
            character.protoData.attributes.vigor.novice = vigor.getDieCode();

            // During character creation, attribute pickers use `v-model="character.vigor"` (etc.) —
            // direct mutations that bypass applyModification, so the orchestrator's calculateBase
            // update never fires. Sync calculateBase.strain.{vigor,spirit} from live values here so
            // strain reflects creation-time attribute allocation. Only writes existing slots — does
            // NOT auto-create calculateBase.strain (that's newCharacter / hydration's job).
            // The live die also carries the temporary tier's steps (installed cyberware, equipped gear,
            // carried payloads), which applyAttributeModification keeps out of calculateBase; they come
            // back out here, or the next edit's recalculation carries a cyberware step into the strain base.
            const strain = (character.additionalData as any)?.calculateBase?.strain;
            if (strain) {
                const withoutTemporary = (attribute: 'vigor' | 'spirit'): string | null => {
                    const rank = this.dieRank((character as any)[attribute], (character as any)[attribute + 'Modifier']);
                    this.applyStepOrModify(rank, -this.temporaryAttributeSteps(character, attribute), 'step');
                    return rank.getDieCode();
                };
                strain.vigor = withoutTemporary('vigor') ?? strain.vigor;
                strain.spirit = withoutTemporary('spirit') ?? strain.spirit;
            }
        },
        /**
         * The die steps an attribute currently carries from the temporary tier, which
         * applyAttributeModification applies to the live die but keeps out of calculateBase: each in-use
         * equipment container's direct modifications and recorded choices once per unit (what
         * applyItemStatusChange and _ticksContainerMods apply), and each carried payload once per applied
         * count (the syncCarriedPayloads ledger).
         */
        temporaryAttributeSteps(character: CharacterSheetInterface, attribute: string): number {
            const stepsOf = (configuration: SwadeModConfigurationInterface | null | undefined): number => {
                if (configuration?.path !== 'character.attributes' || configuration.valueType !== 'step') return 0;
                if (elementName(configuration.element) !== attribute) return 0;
                const value = Number(configuration.value ?? 0);
                return configuration.operator === 'subtract' ? -value : value;
            };
            let steps = 0;
            for (const container of character.equipment ?? []) {
                if (!container || !this.isItemInUse(container)) continue;
                const direct = directModificationsWithoutOverrides(character, container.uuid, container.selection?.modifications)
                    .reduce((sum, mod) => sum + stepsOf(mod.configuration), 0);
                const recorded = (character.additionalData?.choices ?? [])
                    .filter((record: AppliedConfiguration) => record.uuid === container.uuid)
                    .reduce((sum: number, record: AppliedConfiguration) => sum + stepsOf(record.configuration), 0);
                steps += Math.max(1, this.multiplierForContainer(container)) * (direct + recorded);
            }
            for (const row of (character.additionalData?.carriedPayloads ?? []) as CarriedPayloadApplication[]) {
                steps += (row?.count ?? 0) * stepsOf(row?.configuration);
            }
            return steps;
        },
        updateAllocatedAttributePoints(character: CharacterSheetInterface) {
            if (character.protoData.locked || this.hasActiveAdvances(character)) return;

            let allocated =
                this.allocatedToAgility(character)
                + this.allocatedToSmarts(character)
                + this.allocatedToSpirit(character)
                + this.allocatedToStrength(character)
                + this.allocatedToVigor(character)
            ;

            this.build.attributes.allocated = allocated;
            this.build.attributes.fullyAllocated = allocated === (this.build.attributes.total ?? 0);
            this.build.attributes.unspentPoints = allocated < (this.build.attributes.total ?? 0);
            this.build.attributes.overAllocated = allocated > (this.build.attributes.total ?? 0);
        },

        allocatedToAgility(character: CharacterSheetInterface): number {
            const agility = this.dieRank(character?.protoData?.attributes.agility.novice ?? 'd4', 0);
            const baseAgility = this.dieRank(character?.protoData?.attributes.agility.base ?? 'd4', 0);
            return Math.ceil((agility.getValue() - baseAgility.getValue()) / 2)
        },
        allocatedToSmarts(character: CharacterSheetInterface): number {
            const smarts = this.dieRank(character?.protoData?.attributes.smarts.novice ?? 'd4', 0);
            const baseSmarts = this.dieRank(character?.protoData?.attributes.smarts.base ?? 'd4', 0);
            return Math.ceil((smarts.getValue() - baseSmarts.getValue()) / 2)
        },
        allocatedToSpirit(character: CharacterSheetInterface): number {
            const spirit = this.dieRank(character?.protoData?.attributes.spirit.novice ?? 'd4', 0);
            const baseSpirit = this.dieRank(character?.protoData?.attributes.spirit.base ?? 'd4', 0);
            return Math.ceil((spirit.getValue() - baseSpirit.getValue()) / 2)
        },
        allocatedToStrength(character: CharacterSheetInterface): number {
            const strength = this.dieRank(character?.protoData?.attributes.strength.novice ?? 'd4', 0);
            const baseStrength = this.dieRank(character?.protoData?.attributes.strength.base ?? 'd4', 0);
            return Math.ceil((strength.getValue() - baseStrength.getValue()) / 2)
        },
        allocatedToVigor(character: CharacterSheetInterface): number {
            const vigor = this.dieRank(character?.protoData?.attributes.vigor.novice ?? 'd4', 0);
            const baseVigor = this.dieRank(character?.protoData?.attributes.vigor.base ?? 'd4', 0);
            return Math.ceil((vigor.getValue() - baseVigor.getValue()) / 2)
        },

        updateProtoSkills(character: CharacterSheetInterface) {
            if (!character.protoData) {
                character.protoData = this.newProtoData();
            }

            if (character.protoData.locked || this.hasAdvances(character)) return;
            character?.skills?.forEach((item: SwadeSkillRankInterface) => {
                const protoSkill = protoSkillFor(character, item);

                if (!protoSkill) {
                    character.protoData.skills.push({
                        uuid: item.skill.uuid,
                        rankUuid: item.uuid,
                        base: null,
                        rank: item.rank,
                        // Carry the flat/above-cap modifier through; without it a
                        // d12+N skill collapses to d12 and its extra steps are not
                        // counted in the allocated total.
                        modifier: item.modifier ?? null,
                        linkedAttribute: item.skill.linkedAttribute,
                        max: this.computeSkillMax(null),
                    })
                }
                else {
                    protoSkill.rank = item.rank;
                    protoSkill.modifier = item.modifier ?? null;
                }
            })
        },
        updateAllocatedSkillPoints(character: CharacterSheetInterface) {
            if (character.protoData.locked || this.hasActiveAdvances(character)) return;

            let allocated = 0;

            console.debug("SwadeData | updateAllocatedSkillPoints | allocated | ", allocated, this.build.traits.total);
            character?.protoData?.skills.forEach((protoSkill: any) => {
                // protoSkill.rank is the modifier-FREE die code; any above-d12 steps
                // live in protoSkill.modifier. Recombine them so a legendary skill
                // (d12+1, …) is costed correctly instead of counting as a flat d12.
                const rankCode = this.dieRank(protoSkill?.rank ?? '', protoSkill?.modifier ?? null)
                    .getDieCode() ?? protoSkill?.rank;

                let currentValue =
                    this.skillValue(rankCode, protoSkill?.linkedAttribute, character)
                    - this.skillValue(protoSkill?.base, protoSkill?.linkedAttribute, character);
                if (currentValue < 0) currentValue = 0;

                console.debug("SwadeData | updateAllocatedSkillPoints | current | ", protoSkill.linkedAttribute, allocated, currentValue);
                allocated = allocated + currentValue;
            })

            console.debug("SwadeData | updateAllocatedSkillPoints | allocated | ", allocated, this.build.traits.total);
            this.build.traits.allocated = allocated;
            this.build.traits.fullyAllocated = allocated === (this.build.traits.total ?? 0);
            this.build.traits.unspentPoints = allocated < (this.build.traits.total ?? 0);
            this.build.traits.overAllocated = allocated > (this.build.traits.total ?? 0);
        },
        updateAllocatedLanguages(character: CharacterSheetInterface) {
            if (!this.build?.languages) return;
            if (character.protoData.locked || this.hasActiveAdvances(character)) return;

            if (!Array.isArray(character.protoData.languages)) {
                character.protoData.languages = [];
            }

            // Binary language variant: picks live on additionalData.knownLanguages with no skill
            // rank behind them. One reduce over both arrays needs no mode check — in each variant
            // the other array is empty. The `?? 1` also stops a malformed entry (no slotsUsed)
            // from poisoning the total with NaN, which the previous bare addition allowed.
            const allocated = [
                ...(character?.protoData?.languages ?? []),
                ...(character?.additionalData?.knownLanguages ?? []),
            ].reduce((total: number, language: any) => total + (language.slotsUsed ?? 1), 0);

            this.build.languages.allocated = allocated;
            this.build.languages.fullyAllocated = allocated === (this.build.languages.total ?? 0);
            this.build.languages.unspentPoints = allocated < (this.build.languages.total ?? 0);
            this.build.languages.overAllocated = allocated > (this.build.languages.total ?? 0);
        },
        updateAllocatedPerkPoints(character: CharacterSheetInterface) {
            if (character.protoData.locked || this.hasActiveAdvances(character)) return;

            if (!Array.isArray(character.protoData.edges)) {
                character.protoData.edges = [];
            }

            if (!Array.isArray(character.protoData.perkPointAllocation)) {
                character.protoData.perkPointAllocation = [];
            }

            //TODO: Need a way to allocate hindrance points to things besides Edges
            let allocated = 0;
            //console.debug("SwadeData | updateAllocatedPerkPoints | allocated | ", allocated);
            (character?.protoData?.edges ?? []).forEach((item: any) => {
                allocated = allocated + parseInt(item.value);
            });

            //console.debug("SwadeDate | updateAllocatedPerkPoints | allocated | ", allocated);
            (character?.protoData?.perkPointAllocation ?? []).forEach((item: any) => {
                allocated = allocated + parseInt(item.value);
            });

            //console.debug("SwadeDate | updateAllocatedPerkPoints | allocated | ", allocated);
            this.build.perkPoints.allocated = allocated;
            this.build.perkPoints.fullyAllocated = allocated === this.build.perkPoints.total;
            this.build.perkPoints.unspentPoints = allocated < this.build.perkPoints.total;
            this.build.perkPoints.overAllocated = allocated > this.build.perkPoints.total;
        },

        updateProtoPowers(character: CharacterSheetInterface) {
            console.debug("CharacterData | updateProtoPowers", character.protoData, character.powers)
            if (!character.protoData) {
                character.protoData = this.newProtoData();
            }

            if (!Array.isArray(character.protoData.powers)) {
                character.protoData.powers = [];
            }

            if (this.hasActiveAdvances(character) || !Array.isArray(character.powers)) return;

            character.protoData.powers = character.powers?.filter((item: SwadePowerContainerInterface) => {
                    return item.selection?.details?.type === 'superpower'
                })
                ?.map((item: SwadePowerContainerInterface) => {
                    return {
                        uuid: item.selection.uuid,
                        label: containerDisplayName(item),
                        value: resolvePowerDetails(item).powerPoints ?? item.powerPoints
                    }
                }) ?? []

            if (!Array.isArray(character.protoData.powers)) {
                character.protoData.powers = [];
            }
        },
        updateAllocatedCharacterPoints(character: CharacterSheetInterface) {
            if (!this.build.other) return;

            if (Array.isArray(character?.protoData?.powers) && (character?.protoData?.powers ?? []).length > 0) {
                // Normally frozen already, as syncPowerLock locked the sheet; this late copy is the fallback for a
                // sheet loaded with powers and no saved pools. A saved configuration may still carry `other`.
                this.freezeCreationPools(character);
                character.configuration.other = undefined;
            }
            else if (!this.hasActiveAdvances(character)) {
                character.configuration = null;
            }

            console.debug("SwadeData | updateAllocatedCharacterPoints | protodata | ", character.protoData);
            let allocated = 0;
            console.debug("SwadeData | updateAllocatedCharacterPoints | allocated | ", allocated);
            if (Array.isArray(character?.protoData?.powers)) {
                (character?.protoData?.powers ?? []).forEach((item: any) => {
                    const value = parseInt(item.value ?? "0");
                    allocated = allocated + (isNaN(value) ? 0 : value);
                });
            }

            console.debug("SwadeData | updateAllocatedCharacterPoints | allocated | ", allocated);
            this.build.other.allocated = allocated;
            this.build.other.fullyAllocated = allocated === this.build.other.total;
            this.build.other.unspentPoints = allocated < this.build.other.total;
            this.build.other.overAllocated = allocated > this.build.other.total;
        },
    }
});
